import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useHistory } from 'react-router-dom';
import jsPDF from 'jspdf';
import Navbar from '../components/common/Navbar';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/dist/css/intlTelInput.css';
import agreyaLogo from '../assets/images/logo.png';
import letterheadImage from '../assets/images/letterhead.png';
import { 
  validateFile, 
  validateFiles, 
  getFileConstraintMessage,
  validateRequired,
  validateName,
  validateEmail,
  validatePhone,
  validatePositiveNumber,
  validatePercentage,
  validateSelect,
  validateCheckbox,
  validateAtLeastOneCheckbox,
  validatePastDate,
} from '../utils/fileValidation';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const RELATIONSHIP_MANAGERS = [
  'Anand Kartikeyan',
  'Arjun Narsipur',
  'Krishnan MV',
  'Nilesh Shevade',
  'Shailendra Agarwal',
  'Vivek Vasudevan',
];

const DISTRIBUTOR_RM_MAP = {
  Metis: 'Vivek Vasudevan',
  'Rishabh Gautam': 'Shailendra Agarwal',
  Sanyojit: 'Shailendra Agarwal',
  'Bluelake Finserve': 'Shailendra Agarwal',
  Ontrust: 'Krishnan MV',
  Phoenix: 'Arjun Narsipur',
  SIFT: 'Arjun Narsipur',
  'Must Dessrupt': 'Arjun Narsipur',
  'R Ramachandran': 'Vivek Vasudevan',
};

const DISTRIBUTOR_FULL_NAME_MAP = {
  Metis: 'Metis Family Office Services Pvt Ltd',
  'Rishabh Gautam': 'Rishabh Gautam Finserv LLP',
  Sanyojit: 'Sanyojit Financial Services',
  'Bluelake Finserve': 'Blue Lake Finserve LLP',
  Ontrust: 'Ontrust Capital Markets Pvt Ltd',
  Phoenix: 'Phoenix Financial Services',
  SIFT: 'Sift Growth Partners LLP',
  'Must Dessrupt': 'Must Desrrupt Technologies Pvt Ltd',
  'R Ramachandran': 'R Ramachandran',
};

function getExpScore(exp) {
  const map = { no_prior_experience: 3, 'Limited Experience': 7, Moderate: 7, Extensive: 10 };
  return map[exp] || 0;
}

function computeRisk(d) {
  const expScore = getExpScore(d.investment_experience);
  const total =
    parseInt(d.risk_tolerance || 0) +
    parseInt(d.investment_objective || 0) +
    parseInt(d.investment_horizon || 0) +
    parseInt(d.expense_dependency || 0) +
    parseInt(d.substantial_decline_reaction || 0) +
    expScore;
  let cat = total >= 31 ? 'HIGH' : total >= 19 ? 'MEDIUM' : 'LOW';
  let outlook = cat === 'HIGH' ? 'AGGRESSIVE' : cat === 'MEDIUM' ? 'MODERATE' : 'CONSERVATIVE';
  return { totalScore: total, riskCategory: cat, riskOutlook: outlook };
}

function computeCombinedRisk(d) {
  // Calculate risk category for first applicant
  const exp1 = getExpScore(d.investment_experience);
  const total1 =
    parseInt(d.risk_tolerance || 0) +
    parseInt(d.investment_objective || 0) +
    parseInt(d.investment_horizon || 0) +
    parseInt(d.expense_dependency || 0) +
    parseInt(d.substantial_decline_reaction || 0) +
    exp1;
  const cat1 = total1 >= 31 ? 'HIGH' : total1 >= 19 ? 'MEDIUM' : 'LOW';
  console.log('[COMBINED] Applicant 1 - exp:', exp1, 'total:', total1);

  // Calculate risk category for second applicant
  const exp2 = getExpScore(d.second_investment_experience);
  const total2 =
    parseInt(d.second_risk_tolerance || 0) +
    parseInt(d.second_investment_objective || 0) +
    parseInt(d.second_investment_horizon || 0) +
    parseInt(d.second_expense_dependency || 0) +
    parseInt(d.second_substantial_decline_reaction || 0) +
    exp2;
  const cat2 = total2 >= 31 ? 'HIGH' : total2 >= 19 ? 'MEDIUM' : 'LOW';
  console.log('[COMBINED] Applicant 2 - exp:', exp2, 'total:', total2);

  // Calculate risk category for third applicant
  const exp3 = getExpScore(d.third_investment_experience);
  const total3 =
    parseInt(d.third_risk_tolerance || 0) +
    parseInt(d.third_investment_objective || 0) +
    parseInt(d.third_investment_horizon || 0) +
    parseInt(d.third_expense_dependency || 0) +
    parseInt(d.third_substantial_decline_reaction || 0) +
    exp3;
  const cat3 = total3 >= 31 ? 'HIGH' : total3 >= 19 ? 'MEDIUM' : 'LOW';

  // Compare and take the LOWEST SCORE (most conservative)
  let categories = [{ cat: cat1, score: total1, idx: 1 }, { cat: cat2, score: total2, idx: 2 }];
  if (d.third_investment_experience || d.third_risk_tolerance !== '0') {
    categories.push({ cat: cat3, score: total3, idx: 3 });
  }

  // Find the applicant with the LOWEST SCORE
  let mostConservative = categories[0];
  for (let i = 1; i < categories.length; i++) {
    if (categories[i].score < mostConservative.score) {
      mostConservative = categories[i];
    }
  }

  let finalCat = mostConservative.cat;
  let finalScore = mostConservative.score;
  let outlook = finalCat === 'HIGH' ? 'AGGRESSIVE' : finalCat === 'MEDIUM' ? 'MODERATE' : 'CONSERVATIVE';
  console.log('[COMBINED] FINAL - mostConservative applicant:', mostConservative.idx, 'score:', finalScore, 'cat:', finalCat);
  return { totalScore: finalScore, riskCategory: finalCat, riskOutlook: outlook };
}

function getDefaultSharePercentages(count) {
  if (count === 1) return ['100'];
  if (count === 2) return ['50', '50'];
  if (count === 3) {
    const third = (100 / 3).toFixed(2);
    return [third, third, (100 - parseFloat(third) * 2).toFixed(2)];
  }
  return [];
}

function encodeForDisplay(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function normalizeAndEncodeErrorValue(value) {
  if (Array.isArray(value)) return encodeForDisplay(value.join(' '));
  if (value && typeof value === 'object') return encodeForDisplay(JSON.stringify(value));
  return encodeForDisplay(value);
}

const defaultNominee = () => ({
  first_name: '', middle_name: '', last_name: '',
  is_minor: '', dob: '', relationship: '',
  address_same: '', full_address: '',
  id_proof_type: '', id_proof_other: '', mobile_number: '', email: '',
  sharing_percentage: '100',
});

const INITIAL = {
  joint_account: '',
  sole_holder_first_name: '',
  sole_holder_middle_name: '',
  sole_holder_last_name: '',
  gender: '',
  date_of_birth: '',
  occupation: '',
  marital_status: '',
  residential_status: '',
  country_of_birth_id: '',
  state_of_birth_id: '',
  city_of_birth_id: '',
  manual_city_of_birth: '',
  citizenship: '',
  mother_first_name: '',
  mother_middle_name: '',
  mother_last_name: '',
  applicant_address_same_address: '',
  communication_address_proof: '',
  communication_proof_other: '',
  permanent_address_proof: '',
  mobile_number: '',
  email: '',
  annual_income: '',
  net_worth: '',
  pmla: 'None of the above',
  first_applicant_stock_restrictions: 'No',
  first_applicant_stock_restrictions_list: '',
  bank_name: '',
  bankname: '',
  bank_branch: '',
  ifsc_code: '',
  bank_account_number: '',
  // Second applicant
  second_applicant_first_name: '',
  second_applicant_middle_name: '',
  second_applicant_last_name: '',
  second_applicant_gender: '',
  second_applicant_date_of_birth: '',
  second_applicant_occupation: '',
  second_applicant_marital_status: '',
  second_applicant_residential_status: '',
  second_applicant_country_of_birth_id: '',
  second_applicant_state_of_birth_id: '',
  second_applicant_city_of_birth_id: '',
  manual_second_city_of_birth: '',
  second_applicant_citizenship: '',
  second_applicant_mother_first_name: '',
  second_applicant_mother_middle_name: '',
  second_applicant_mother_last_name: '',
  second_applicant_address_same_as_first: '',
  second_applicant_address_same_address: '',
  second_applicant_communication_address_proof: '',
  second_applicant_communication_proof_other: '',
  second_applicant_permanent_address_proof: '',
  second_applicant_mobile_number: '',
  second_applicant_email: '',
  second_applicant_annual_income: '',
  second_applicant_net_worth: '',
  second_applicant_pmla: 'None of the above',
  second_applicant_stock_restrictions: 'No',
  second_applicant_stock_restrictions_list: '',
  // Third applicant
  third_applicant_first_name: '',
  third_applicant_middle_name: '',
  third_applicant_last_name: '',
  third_applicant_gender: '',
  third_applicant_date_of_birth: '',
  third_applicant_occupation: '',
  third_applicant_marital_status: '',
  third_applicant_residential_status: '',
  third_applicant_country_of_birth_id: '',
  third_applicant_state_of_birth_id: '',
  third_applicant_city_of_birth_id: '',
  manual_third_city_of_birth: '',
  third_applicant_citizenship: '',
  third_applicant_mother_first_name: '',
  third_applicant_mother_middle_name: '',
  third_applicant_mother_last_name: '',
  third_applicant_address_same_as_first: '',
  third_applicant_address_same_address: '',
  third_applicant_communication_address_proof: '',
  third_applicant_communication_proof_other: '',
  third_applicant_permanent_address_proof: '',
  third_applicant_mobile_number: '',
  third_applicant_email: '',
  third_applicant_annual_income: '',
  third_applicant_net_worth: '',
  third_applicant_pmla: 'None of the above',
  third_applicant_stock_restrictions: 'No',
  third_applicant_stock_restrictions_list: '',
  // Third applicant risk
  third_investment_experience: '',
  third_risk_tolerance: '0',
  third_investment_objective: '0',
  third_investment_horizon: '0',
  third_portfolio_manager_allocation: '0',
  third_require_systematic_investment_plan: '',
  third_expense_dependency: '0',
  third_investment_risk_awareness: '',
  third_substantial_decline_reaction: '0',
  third_cderivatives: '',
  // Nominee
  has_nominee: '',
  // Risk profiling
  investment_experience: '',
  risk_tolerance: '0',
  investment_objective: '0',
  investment_horizon: '0',
  portfolio_manager_allocation: '0',
  require_systematic_investment_plan: '',
  expense_dependency: '0',
  investment_risk_awareness: '',
  substantial_decline_reaction: '0',
  cderivatives: '',
  same_risk_profile: '',
  // Second applicant risk
  second_investment_experience: '',
  second_risk_tolerance: '0',
  second_investment_objective: '0',
  second_investment_horizon: '0',
  second_portfolio_manager_allocation: '0',
  second_require_systematic_investment_plan: '',
  second_expense_dependency: '0',
  second_investment_risk_awareness: '',
  second_substantial_decline_reaction: '0',
  second_cderivatives: '',
  same_risk_profile_third: '',
  // Investment & Fee
  investment_approach: [],
  fixed_fee_model: [],
  hybrid_fee_model: [],
  relationship_manager: '',
};

export default function OnboardingForm() {
  const { distributor } = useParams();
  const history = useHistory();

  const distributorFullName = distributor
    ? DISTRIBUTOR_FULL_NAME_MAP[distributor] || distributor
    : 'No Distributor';
  const assignedRM = distributor ? DISTRIBUTOR_RM_MAP[distributor] || '' : null;

  const [formData, setFormData] = useState({
    ...INITIAL,
    relationship_manager: assignedRM || '',
  });
  const [nominees, setNominees] = useState([defaultNominee()]);
  const [nomineeIdProofs, setNomineeIdProofs] = useState([null]);
  const [guardians, setGuardians] = useState({
    0: { first_name: '', middle_name: '', last_name: '', mobile_number: '', email: '', communication_address_proof: '', communication_proof_other: '' },
    1: { first_name: '', middle_name: '', last_name: '', mobile_number: '', email: '', communication_address_proof: '', communication_proof_other: '' },
    2: { first_name: '', middle_name: '', last_name: '', mobile_number: '', email: '', communication_address_proof: '', communication_proof_other: '' },
  });
  const [nomineeErrors, setNomineeErrors] = useState({});
  const [sharingRatioTotal, setSharingRatioTotal] = useState(100);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [secondStates, setSecondStates] = useState([]);
  const [secondCities, setSecondCities] = useState([]);
  const [thirdStates, setThirdStates] = useState([]);
  const [thirdCities, setThirdCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [cancelledChequeError, setCancelledChequeError] = useState('');
  const [panCardError, setPanCardError] = useState('');
  const [addressProofError, setAddressProofError] = useState('');
  const [pdfData, setPdfData] = useState('');
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  const [showDerivativesModal, setShowDerivativesModal] = useState(false);
  const [showSecondDerivativesModal, setShowSecondDerivativesModal] = useState(false);
  const [showThirdDerivativesModal, setShowThirdDerivativesModal] = useState(false);
  const [showThirdApplicant, setShowThirdApplicant] = useState(false);
  const [cancelledCheque, setCancelledCheque] = useState(null);
  const [panCardCopies, setPanCardCopies] = useState([]);
  const [addressProofCopies, setAddressProofCopies] = useState([]);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [hasAttemptedValidation, setHasAttemptedValidation] = useState(false);
  const [pdfError, setPdfError] = useState('');
  // Refs to store intlTelInput instances keyed by field name
  const itiRefs = useRef({});
  // Track if component is mounted to prevent state updates on unmounted component
  const isMountedRef = useRef(true);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Set page title for screen readers
  useEffect(() => {
    document.title = 'Agreya PMS: Direct-Onboarding Information for Individuals';
    return () => {
      document.title = 'Home | Agreya Capital';
    };
  }, []);

  // Load reCAPTCHA v3 script on component mount
  useEffect(() => {
    // Get reCAPTCHA site key from Django backend
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || 'YOUR_SITE_KEY';

    if (!recaptchaSiteKey || recaptchaSiteKey === 'YOUR_SITE_KEY') {
      console.warn('reCAPTCHA site key not configured');
      return;
    }

    // Load reCAPTCHA v3 script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script if component unmounts
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // Generate reCAPTCHA token before form submission
  const generateRecaptchaToken = async () => {
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || 'YOUR_SITE_KEY';

    console.log('[reCAPTCHA] Starting token generation...');
    console.log('[reCAPTCHA] Site Key:', recaptchaSiteKey ? recaptchaSiteKey.substring(0, 10) + '...' : 'NOT SET');

    if (!recaptchaSiteKey || recaptchaSiteKey === 'YOUR_SITE_KEY') {
      console.error('[reCAPTCHA] Site key not configured. Please set REACT_APP_RECAPTCHA_SITE_KEY in .env');
      return '';
    }

    try {
      // Call grecaptcha.execute to get token
      if (window.grecaptcha) {
        console.log('[reCAPTCHA] grecaptcha object found, executing...');
        const token = await window.grecaptcha.execute(recaptchaSiteKey, {
          action: 'onboarding_submission'
        });
        console.log('[reCAPTCHA] Token generated successfully:', token ? token.substring(0, 30) + '...' : 'EMPTY');
        return token;
      } else {
        console.error('[reCAPTCHA] grecaptcha not loaded. Ensure reCAPTCHA script is loaded from Google.');
        return '';
      }
    } catch (error) {
      console.error('[reCAPTCHA] Error generating token:', error);
      return '';
    }
  };

  // Derived visibility
  const isJoint = formData.joint_account === 'Yes';
  const showSecond = isJoint;
  const showSameRiskQ = isJoint;
  const showSecondRisk = isJoint && formData.same_risk_profile === 'no';
  const showSameRiskQThird = showThirdApplicant;
  const showThirdRisk = showThirdApplicant && formData.same_risk_profile_third === 'no';
  const showNominee = formData.has_nominee === 'Yes';
  const showManualCity = formData.city_of_birth_id === 'other';
  const showManualSecondCity = formData.second_applicant_city_of_birth_id === 'other';
  const riskEnabled = formData.investment_risk_awareness === 'Yes';
  const secondRiskEnabled = formData.second_investment_risk_awareness === 'Yes';
  const thirdRiskEnabled = formData.third_investment_risk_awareness === 'Yes';
  const showBank = formData.residential_status !== 'Non-Resident';

  // Risk calculation
  let riskData = null;

  // Check if we have multiple applicants with potentially different risk profiles
  const hasSecondApplicant = isJoint;
  const hasThirdApplicant = showThirdApplicant;
  const secondApplicantDifferentRisk = isJoint && (formData.same_risk_profile === 'no' || formData.same_risk_profile === 'No');
  const thirdApplicantDifferentRisk = showThirdApplicant && showThirdRisk;

  // DEBUG
  console.log('[RISK] hasSecondApplicant:', hasSecondApplicant, 'secondApplicantDifferentRisk:', secondApplicantDifferentRisk);
  console.log('[RISK] same_risk_profile value:', formData.same_risk_profile);
  console.log('[RISK] hasThirdApplicant:', hasThirdApplicant, 'thirdApplicantDifferentRisk:', thirdApplicantDifferentRisk);
  console.log('[RISK] Will use:', ((hasSecondApplicant && secondApplicantDifferentRisk) || (hasThirdApplicant && thirdApplicantDifferentRisk)) ? 'computeCombinedRisk' : 'computeRisk');

  // If multiple applicants with different risk profiles are selected, use combined risk
  if ((hasSecondApplicant && secondApplicantDifferentRisk) || (hasThirdApplicant && thirdApplicantDifferentRisk)) {
    // For multiple applicants with different risk profiles - use combined risk calculation
    // This will calculate minimum score across all applicants
    let canComputeRisk = formData.investment_experience &&
      parseInt(formData.risk_tolerance) > 0 &&
      parseInt(formData.substantial_decline_reaction) > 0;

    if (hasSecondApplicant && secondApplicantDifferentRisk) {
      canComputeRisk = canComputeRisk &&
        formData.second_investment_experience &&
        parseInt(formData.second_risk_tolerance) > 0 &&
        parseInt(formData.second_substantial_decline_reaction) > 0;
    }

    if (hasThirdApplicant && thirdApplicantDifferentRisk) {
      canComputeRisk = canComputeRisk &&
        formData.third_investment_experience &&
        parseInt(formData.third_risk_tolerance) > 0 &&
        parseInt(formData.third_substantial_decline_reaction) > 0;
    }

    if (canComputeRisk) {
      console.log('[RISK] Using computeCombinedRisk - should show MINIMUM score');
      riskData = computeCombinedRisk(formData);
    }
  } else {
    // For single applicant or same risk profile
    if (
      formData.investment_experience &&
      parseInt(formData.risk_tolerance) > 0 &&
      parseInt(formData.substantial_decline_reaction) > 0
    ) {
      console.log('[RISK] Using computeRisk - showing first applicant only');
      riskData = computeRisk(formData);
    }
  }

  // ── Investment Approach grayout logic ─────────────────────────────────────
  // Mirrors onboarding-form.js jQuery logic exactly
  const ALL_APPROACHES = [
    'Approach A: Agreya Momentum',
    'Approach B: Agreya Total Return',
    'Approach C: Agreya Multi-Asset',
    'Approach D: Agreya Multi-Asset Enhancer',
    'Approach E: Agreya Total Return Enhancer',
    'Approach F: Agreya Momentum Enhancer',
    'Approach G: Agreya Portfolio Return Optimiser',
    'Approach H: Agreya Value Momentum Strategy',
  ];

  // Which approaches are selectable (not greyed out)
  const getEnabledApproaches = () => {
    // Check all applicants have answered "Yes" to capital erosion awareness
    if (formData.investment_risk_awareness !== 'Yes') return new Set();
    if (secondApplicantDifferentRisk && formData.second_investment_risk_awareness !== 'Yes') return new Set();
    if (thirdApplicantDifferentRisk && formData.third_investment_risk_awareness !== 'Yes') return new Set();

    if (!pdfDownloaded) return new Set();
    if (!riskData) return new Set();
    const score = riskData.totalScore;
    // For joint accounts the combined (lower) derivatives consent applies
    const derivatives = isJoint
      ? (formData.cderivatives === 'No' || formData.second_cderivatives === 'No' ? 'No' : 'Yes')
      : formData.cderivatives;
    if (!derivatives) return new Set();
    if (score <= 30) {
      // LOW (6-18) or MEDIUM (19-30)
      if (derivatives === 'No') return new Set(['Approach C: Agreya Multi-Asset']);
      return new Set(['Approach C: Agreya Multi-Asset', 'Approach D: Agreya Multi-Asset Enhancer']);
    }
    // HIGH (31-60)
    if (derivatives === 'No')
      return new Set([
        'Approach A: Agreya Momentum',
        'Approach B: Agreya Total Return',
        'Approach C: Agreya Multi-Asset',
      ]);
    return new Set(ALL_APPROACHES);
  };
  const enabledApproaches = getEnabledApproaches();

  // ── Fee schedule logic ─────────────────────────────────────────────────────
  // Approach value → { fixed?: feeValue, hybrid?: feeValue }
  const APPROACH_FEE_MAP = {
    'Approach A: Agreya Momentum': {
      fixed: 'Approach A - 2.0% p.a.',
      hybrid: 'Approach A - 1.2% p.a. + Variable fee of 15% p.a. on profits over hurdle rate of 10% p.a.',
    },
    'Approach B: Agreya Total Return': {
      fixed: 'Approach B - 2.0% p.a.',
      hybrid: 'Approach B - 1.2% p.a. + Variable fee of 15% p.a. on profits over hurdle rate of 10% p.a.',
    },
    'Approach C: Agreya Multi-Asset': { fixed: 'Approach C - 0.5% p.a.' },
    'Approach D: Agreya Multi-Asset Enhancer': { fixed: 'Approach D - 1.5% p.a.' },
    'Approach E: Agreya Total Return Enhancer': {
      fixed: 'Approach E - 2.0% p.a.',
      hybrid: 'Approach E - 1.2% p.a. + Variable fee of 20% p.a. on profits over hurdle rate of 12% p.a.',
    },
    'Approach F: Agreya Momentum Enhancer': {
      fixed: 'Approach F - 2.0% p.a.',
      hybrid: 'Approach F - 1.2% p.a. + Variable fee of 20% p.a. on profits over hurdle rate of 12% p.a.',
    },
    'Approach G: Agreya Portfolio Return Optimiser': {
      hybrid: 'Approach G - 0.2% p.a. + Variable fee of 20% p.a. on profits.',
    },
    'Approach H: Agreya Value Momentum Strategy': {
      fixed: 'Approach H - 2.0% p.a.',
      hybrid: 'Approach H - 1.2% p.a. + Variable fee of 15% p.a. on profits over hurdle rate of 10% p.a.',
    },
  };

  // Build mutual-exclusion map: feeValue → feeValue (fixed<->hybrid for same letter)
  const FEE_MUTEX = {};
  Object.values(APPROACH_FEE_MAP).forEach(({ fixed, hybrid }) => {
    if (fixed && hybrid) {
      FEE_MUTEX[fixed] = hybrid;
      FEE_MUTEX[hybrid] = fixed;
    }
  });

  // Set of fee values that are disabled because the opposite type is checked
  const disabledFees = new Set();
  formData.fixed_fee_model.forEach((v) => { if (FEE_MUTEX[v]) disabledFees.add(FEE_MUTEX[v]); });
  formData.hybrid_fee_model.forEach((v) => { if (FEE_MUTEX[v]) disabledFees.add(FEE_MUTEX[v]); });

  // Clean up invalid selections when enabled approaches change
  // (useEffect so we don't mutate state during render)
  useEffect(() => {
    setFormData((prev) => {
      const validApproaches = prev.investment_approach.filter((a) => enabledApproaches.has(a));
      // Collect all fee values that correspond to still-selected approaches
      const allowedFixedFees = new Set();
      const allowedHybridFees = new Set();
      validApproaches.forEach((a) => {
        const m = APPROACH_FEE_MAP[a];
        if (m?.fixed) allowedFixedFees.add(m.fixed);
        if (m?.hybrid) allowedHybridFees.add(m.hybrid);
      });
      const fixed = prev.fixed_fee_model.filter((v) => allowedFixedFees.has(v));
      const hybrid = prev.hybrid_fee_model.filter((v) => allowedHybridFees.has(v));
      if (
        validApproaches.length === prev.investment_approach.length &&
        fixed.length === prev.fixed_fee_model.length &&
        hybrid.length === prev.hybrid_fee_model.length
      )
        return prev;
      return { ...prev, investment_approach: validApproaches, fixed_fee_model: fixed, hybrid_fee_model: hybrid };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDownloaded, formData.investment_risk_awareness, formData.cderivatives, formData.second_cderivatives, JSON.stringify(riskData)]);

  // Load countries on mount
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/onboarding/countries/`)
      .then((res) => {
        setCountries(res.data.countries);
        const india = res.data.countries.find((c) => c.name === 'India');
        if (india) {
          setFormData((prev) => ({
            ...prev,
            country_of_birth_id: String(india.id),
            citizenship: india.nationality || 'Indian',
            second_applicant_country_of_birth_id: String(india.id),
            second_applicant_citizenship: india.nationality || 'Indian',
          }));
          axios.get(`${API_BASE_URL}/get-states/${india.id}/`).then((r) => {
            setStates(r.data.states);
            setSecondStates(r.data.states);
          });
        }
      })
      .catch(() => {});
  }, []);

  // Scroll to error banner when error occurs
  useEffect(() => {
    if (submitError) {
      setTimeout(() => {
        const errorBanner = document.querySelector('[role="alert"]');
        if (errorBanner) {
          console.log('[ERROR_UI] 📍 Scrolling to error banner');
          errorBanner.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [submitError]);

  const loadStates = (countryId) => {
    if (!countryId) return;
    axios.get(`${API_BASE_URL}/get-states/${countryId}/`).then((r) => setStates(r.data.states));
  };
  const loadCities = (stateId) => {
    if (!stateId) return;
    axios.get(`${API_BASE_URL}/get-cities/${stateId}/`).then((r) => setCities(r.data.cities));
  };
  const loadSecondStates = (countryId, applicant = 'second') => {
    if (!countryId) return;
    axios
      .get(`${API_BASE_URL}/get-states/${countryId}/`)
      .then((r) => {
        if (applicant === 'third') {
          setThirdStates(r.data.states);
        } else {
          setSecondStates(r.data.states);
        }
      });
  };
  const loadSecondCities = (stateId, applicant = 'second') => {
    if (!stateId) return;
    axios
      .get(`${API_BASE_URL}/get-cities/${stateId}/`)
      .then((r) => {
        if (applicant === 'third') {
          setThirdCities(r.data.cities);
        } else {
          setSecondCities(r.data.cities);
        }
      });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Phone inputs: just update with the current input value
    // intl-tel-input will format it, and we extract the full number on submit via getNumber()
    if (type === 'tel') {
      setFormData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    if (type === 'checkbox') {
      setFormData((prev) => {
        const list = Array.isArray(prev[name]) ? prev[name] : [];
        return {
          ...prev,
          [name]: checked ? [...list, value] : list.filter((v) => v !== value),
        };
      });
      return;
    }

    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // Auto-fill bank_name from applicant name
      if (['sole_holder_first_name', 'sole_holder_middle_name', 'sole_holder_last_name'].includes(name)) {
        const fn = name === 'sole_holder_first_name' ? value : prev.sole_holder_first_name;
        const mn = name === 'sole_holder_middle_name' ? value : prev.sole_holder_middle_name;
        const ln = name === 'sole_holder_last_name' ? value : prev.sole_holder_last_name;
        next.bank_name = `${fn} ${mn} ${ln}`.replace(/\s+/g, ' ').trim();
      }
      // Auto-fill citizenship from country
      if (name === 'country_of_birth_id') {
        const c = countries.find((c) => String(c.id) === value);
        if (c) next.citizenship = c.nationality || '';
      }
      if (name === 'second_applicant_country_of_birth_id') {
        const c = countries.find((c) => String(c.id) === value);
        if (c) next.second_applicant_citizenship = c.nationality || '';
      }
      if (name === 'third_applicant_country_of_birth_id') {
        const c = countries.find((c) => String(c.id) === value);
        if (c) next.third_applicant_citizenship = c.nationality || '';
      }
      return next;
    });

    // Side effects
    if (name === 'country_of_birth_id') {
      setStates([]);
      setCities([]);
      if (value) loadStates(value);
    }
    if (name === 'state_of_birth_id') {
      setCities([]);
      if (value) loadCities(value);
    }
    if (name === 'second_applicant_country_of_birth_id') {
      setSecondStates([]);
      setSecondCities([]);
      if (value) loadSecondStates(value);
    }
    if (name === 'second_applicant_state_of_birth_id') {
      setSecondCities([]);
      if (value) loadSecondCities(value);
    }
    if (name === 'third_applicant_country_of_birth_id') {
      setThirdStates([]);
      setThirdCities([]);
      if (value) loadSecondStates(value, 'third');
    }
    if (name === 'third_applicant_state_of_birth_id') {
      setThirdCities([]);
      if (value) loadSecondCities(value, 'third');
    }
    if (name === 'cderivatives' && value === 'Yes') setShowDerivativesModal(true);
    if (name === 'second_cderivatives' && value === 'Yes') setShowSecondDerivativesModal(true);
    if (name === 'third_cderivatives' && value === 'Yes') setShowThirdDerivativesModal(true);
  };

  const handleNomineeChange = (index, field, value) => {
    setNominees((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleGuardianChange = (nomineeIndex, field, value) => {
    setGuardians((prev) => ({
      ...prev,
      [nomineeIndex]: {
        ...prev[nomineeIndex],
        [field]: value,
      },
    }));
  };

  const addNominee = () => {
    if (nominees.length < 3) {
      const newCount = nominees.length + 1;
      const newPercentages = getDefaultSharePercentages(newCount);
      setNominees((prev) => {
        const updated = [...prev];
        updated.forEach((nom, i) => { nom.sharing_percentage = newPercentages[i]; });
        updated.push({ ...defaultNominee(), sharing_percentage: newPercentages[newCount - 1] });
        return updated;
      });
      setNomineeIdProofs((prev) => [...prev, null]);
    }
  };

  const removeNominee = (index) => {
    if (nominees.length > 1) {
      const newCount = nominees.length - 1;
      const newPercentages = getDefaultSharePercentages(newCount);
      setNominees((prev) => {
        const updated = prev.filter((_, i) => i !== index);
        updated.forEach((nom, i) => { nom.sharing_percentage = newPercentages[i]; });
        return updated;
      });
      setNomineeIdProofs((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const updateSharingPercentage = (index, value) => {
    handleNomineeChange(index, 'sharing_percentage', value);
    const total = nominees.reduce((sum, nom, i) => {
      if (i === index) return sum + parseFloat(value || 0);
      return sum + parseFloat(nom.sharing_percentage || 0);
    }, 0);
    setSharingRatioTotal(total);
  };

  // Real-time field validation for individual fields
  const validateField = (fieldName, value) => {
    let error = '';

    // Name fields - First names are required, last names and middle names are optional
    if (['sole_holder_first_name', 'sole_holder_middle_name',
         'mother_first_name', 'mother_middle_name',
         'second_applicant_first_name', 'second_applicant_middle_name'].includes(fieldName)) {
      error = validateName(value) || validateRequired(value);
    }
    // Last names are optional but validate format if provided
    if (['sole_holder_last_name', 'mother_last_name', 'second_applicant_last_name'].includes(fieldName)) {
      error = validateName(value);
    }
    
    // Email fields
    if (['email', 'second_applicant_email', 'first_nominee_email'].includes(fieldName)) {
      error = validateEmail(value);
    }
    
    // Phone fields
    if (['mobile_number', 'second_applicant_mobile_number', 'third_applicant_mobile_number', 'first_nominee_mobile_number', 'guardian_mobile_number'].includes(fieldName)) {
      error = validatePhone(value);
    }
    
    // Bank details
    if (fieldName === 'bank_account_number' && value) {
      error = !/^[0-9]{5,20}$/.test(value) ? 'Account number should be 5-20 digits' : '';
    }
    
    // Numeric fields  
    if (['net_worth', 'second_applicant_net_worth', 'portfolio_manager_allocation'].includes(fieldName)) {
      error = validatePositiveNumber(value);
    }
    
    // Percentage fields
    if (['portfolio_manager_allocation', 'risk_tolerance', 'investment_objective', 
         'investment_horizon', 'expense_dependency'].includes(fieldName)) {
      error = validatePercentage(value);
    }
    
    // Select/Radio fields
    if (['joint_account', 'gender', 'occupation', 'marital_status', 'residential_status',
         'country_of_birth_id', 'state_of_birth_id', 'city_of_birth_id', 'has_nominee',
         'investment_experience', 'investment_risk_awareness', 'applicant_address_same_address',
         'communication_address_proof', 'annual_income', 'pmla', 'first_applicant_stock_restrictions'].includes(fieldName)) {
      if (fieldName.includes('id')) {
        error = value ? '' : 'Please select an option.';
      } else {
        error = validateSelect(value);
      }
    }

    return error;
  };

  // Handle field blur for real-time validation
  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    
    // For phone inputs, extract the properly formatted number using intl-tel-input API
    let valueToValidate = value;
    const phoneFields = ['mobile_number', 'second_applicant_mobile_number', 'third_applicant_mobile_number', 'first_nominee_mobile_number', 'guardian_mobile_number'];
    if (phoneFields.includes(name)) {
      const iti = itiRefs.current[name];
      if (iti) {
        valueToValidate = iti.getNumber();
        // Update formData with the properly formatted number
        setFormData(prev => ({ ...prev, [name]: valueToValidate }));
      }
    }
    
    const error = validateField(name, valueToValidate);
    
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Validate required fields, returns true if valid
  const validate = () => {
    setHasAttemptedValidation(true);
    const errs = {};

    // Relationship Manager validation (only if not distributor-assigned)
    if (!distributor && !formData.relationship_manager) errs.relationship_manager = 'Please select a relationship manager.';

    // Personal Details
    if (!formData.joint_account) errs.joint_account = 'Please select whether you want a joint account.';
    if (!(formData.sole_holder_first_name || '').trim()) errs.sole_holder_first_name = 'First name is required.';
    if (!formData.gender) errs.gender = 'Please select a gender.';
    if (!formData.date_of_birth) errs.date_of_birth = 'Date of birth is required.';
    if (!formData.occupation) errs.occupation = 'Please select an occupation.';
    if (!formData.marital_status) errs.marital_status = 'Please select a marital status.';
    if (!formData.residential_status) errs.residential_status = 'Please select a residential status.';
    if (!formData.country_of_birth_id) errs.country_of_birth_id = 'Please select a country.';
    if (!formData.state_of_birth_id) errs.state_of_birth_id = 'Please select a state.';
    if (!formData.city_of_birth_id) errs.city_of_birth_id = 'Please select a city.';
    if (!(formData.citizenship || '').trim()) errs.citizenship = 'Citizenship is required.';
    if (!(formData.mother_first_name || '').trim()) errs.mother_first_name = "Mother's first name is required.";
    // Address proof - Q1: Is communication address same as permanent address? (MANDATORY)
    if (!formData.applicant_address_same_address) {
      errs.applicant_address_same_address = 'Please answer whether communication address is same as permanent address.';
    }
    // Q2: Based on answer, require appropriate proof
    if (formData.applicant_address_same_address === 'Yes') {
      if (!formData.communication_address_proof) {
        errs.communication_address_proof = 'Please select a document for communication address proof.';
      }
    } else if (formData.applicant_address_same_address === 'No') {
      if (!formData.permanent_address_proof) {
        errs.permanent_address_proof = 'Please select a document for permanent address proof.';
      }
    }
    if (!formData.annual_income) errs.annual_income = 'Please select an annual income range.';
    if (!formData.net_worth) errs.net_worth = 'Net-worth is required.';
    if (!formData.pmla) errs.pmla = 'Please select a PMLA option.';
    if (!formData.first_applicant_stock_restrictions) errs.first_applicant_stock_restrictions = 'Please indicate whether you have stock restrictions.';

    // Stock restriction validation - if "Yes" selected, must have at least one stock
    if (formData.first_applicant_stock_restrictions === 'Yes') {
      const stocks = (formData.first_applicant_stock_restrictions_list || '').split(',').map(s => s.trim()).filter(Boolean);
      if (stocks.length === 0) errs.first_applicant_stock_restrictions_list = 'Please enter at least one stock name.';
    }

    // Mobile number and email mandatory for first applicant
    if (!(formData.mobile_number || '').trim()) errs.mobile_number = 'Mobile number is required.';
    if (!(formData.email || '').trim()) errs.email = 'Email is required.';

    // Bank Details (only if Resident)
    if (showBank) {
      if (!(formData.bank_name || '').trim()) errs.bank_name = 'Account holder name is required.';
      if (!(formData.bankname || '').trim()) errs.bankname = 'Bank name is required.';
      if (!(formData.bank_branch || '').trim()) errs.bank_branch = 'Bank branch is required.';
    }

    // Nominee
    if (!formData.has_nominee) errs.has_nominee = 'Please indicate whether you want to appoint a nominee.';

    // Validate nominees if has_nominee === 'Yes'
    if (formData.has_nominee === 'Yes') {
      const nomineeErrs = {};
      nominees.forEach((nom, i) => {
        if (!nom.first_name?.trim()) nomineeErrs[`nominee_${i}_first_name`] = 'First name is required.';
        if (!nom.dob) nomineeErrs[`nominee_${i}_dob`] = 'Date of birth is required.';
        if (!nom.relationship?.trim()) nomineeErrs[`nominee_${i}_relationship`] = 'Relationship is required.';
        if (!nom.address_same) nomineeErrs[`nominee_${i}_address_same`] = 'Please specify if address is same as applicant.';
        if (nom.address_same === 'No' && !nom.full_address?.trim()) nomineeErrs[`nominee_${i}_full_address`] = 'Full address is required.';
        if (!nom.is_minor) nomineeErrs[`nominee_${i}_is_minor`] = 'Please specify if nominee is minor.';

        // Mobile and email mandatory for nominees
        if (!(nom.mobile_number || '').trim()) nomineeErrs[`nominee_${i}_mobile_number`] = 'Mobile number is required.';
        if (!(nom.email || '').trim()) nomineeErrs[`nominee_${i}_email`] = 'Email is required.';

        // Age validation for minors (should be between 5 and 18)
        if (nom.is_minor === 'Yes' && nom.dob) {
          const dob = new Date(nom.dob);
          const today = new Date();
          const age = today.getFullYear() - dob.getFullYear();
          const monthDiff = today.getMonth() - dob.getMonth();
          const actualAge = monthDiff < 0 ? age - 1 : age;

          if (actualAge < 5 || actualAge > 18) {
            nomineeErrs[`nominee_${i}_dob`] = 'Minor nominee should be between 5 and 18 years old.';
          }
        }

        // Duplicate name and DOB check
        const nomName = `${nom.first_name}${nom.middle_name}${nom.last_name}`.toLowerCase().replace(/\s+/g, '');

        const firstApplName = `${formData.sole_holder_first_name}${formData.sole_holder_middle_name}${formData.sole_holder_last_name}`.toLowerCase().replace(/\s+/g, '');
        if (nomName === firstApplName && nom.dob === formData.date_of_birth) {
          nomineeErrs[`nominee_${i}_first_name`] = 'Nominee cannot have the same name and date of birth as first applicant.';
        }

        const secondApplName = `${formData.second_applicant_first_name}${formData.second_applicant_middle_name}${formData.second_applicant_last_name}`.toLowerCase().replace(/\s+/g, '');
        if (isJoint && nomName === secondApplName && nom.dob === formData.second_applicant_date_of_birth) {
          nomineeErrs[`nominee_${i}_first_name`] = 'Nominee cannot have the same name and date of birth as second applicant.';
        }

        const thirdApplName = `${formData.third_applicant_first_name}${formData.third_applicant_middle_name}${formData.third_applicant_last_name}`.toLowerCase().replace(/\s+/g, '');
        if (showThirdApplicant && nomName === thirdApplName && nom.dob === formData.third_applicant_date_of_birth) {
          nomineeErrs[`nominee_${i}_first_name`] = 'Nominee cannot have the same name and date of birth as third applicant.';
        }

        // Guardian details validation when nominee is minor
        if (nom.is_minor === 'Yes') {
          const guardian = guardians[i];
          if (!guardian?.first_name?.trim()) nomineeErrs[`guardian_${i}_first_name`] = 'Guardian first name is required.';
          if (!guardian?.mobile_number?.trim()) nomineeErrs[`guardian_${i}_mobile_number`] = 'Guardian mobile number is required.';
          if (!guardian?.email?.trim()) nomineeErrs[`guardian_${i}_email`] = 'Guardian email is required.';
          if (!guardian?.communication_address_proof) nomineeErrs[`guardian_${i}_communication_address_proof`] = 'Guardian address proof is required.';
        }
      });
      if (nominees.length > 1) {
        const total = nominees.reduce((sum, nom) => sum + parseFloat(nom.sharing_percentage || 0), 0);
        if (Math.abs(total - 100) > 0.01) {
          nomineeErrs.sharing_percentage_total = `Sharing percentages must sum to 100% (currently ${total.toFixed(2)}%).`;
        }
      }
      if (Object.keys(nomineeErrs).length > 0) {
        setNomineeErrors(nomineeErrs);
        Object.assign(errs, nomineeErrs);
      }
    }

    // Risk Profiling
    if (!formData.investment_experience) errs.investment_experience = 'Please describe your investment experience.';
    if (!parseInt(formData.risk_tolerance)) errs.risk_tolerance = 'Please set a risk tolerance value greater than 0.';
    if (!parseInt(formData.investment_objective)) errs.investment_objective = 'Please set an investment objective value greater than 0.';
    if (!parseInt(formData.investment_horizon)) errs.investment_horizon = 'Please set an investment horizon value greater than 0.';
    if (!parseInt(formData.portfolio_manager_allocation) || parseInt(formData.portfolio_manager_allocation) < 10) errs.portfolio_manager_allocation = 'Please set an allocation of at least 10%.';
    if (!formData.require_systematic_investment_plan) errs.require_systematic_investment_plan = 'Please answer the systematic investment plan question.';
    if (!parseInt(formData.expense_dependency)) errs.expense_dependency = 'Please set an expense dependency value greater than 0.';
    if (!formData.investment_risk_awareness) errs.investment_risk_awareness = 'Please indicate your risk awareness.';
    if (formData.investment_risk_awareness === 'Yes') {
      if (!parseInt(formData.substantial_decline_reaction)) errs.substantial_decline_reaction = 'Please set a reaction value greater than 0.';
      if (!formData.cderivatives) errs.cderivatives = 'Please indicate your consent for derivatives.';
    }

    // Email format validation
    if ((formData.email || '').trim() && validateEmail(formData.email)) {
      errs.email = validateEmail(formData.email);
    }
    if ((formData.second_applicant_email || '').trim() && validateEmail(formData.second_applicant_email)) {
      errs.second_applicant_email = validateEmail(formData.second_applicant_email);
    }
    if ((formData.first_nominee_email || '').trim() && validateEmail(formData.first_nominee_email)) {
      errs.first_nominee_email = validateEmail(formData.first_nominee_email);
    }

    // Phone format validation
    if (formData.mobile_number && validatePhone(formData.mobile_number)) {
      errs.mobile_number = validatePhone(formData.mobile_number);
    }
    if (formData.second_applicant_mobile_number && validatePhone(formData.second_applicant_mobile_number)) {
      errs.second_applicant_mobile_number = validatePhone(formData.second_applicant_mobile_number);
    }
    if (formData.first_nominee_mobile_number && validatePhone(formData.first_nominee_mobile_number)) {
      errs.first_nominee_mobile_number = validatePhone(formData.first_nominee_mobile_number);
    }
    if (formData.guardian_mobile_number && validatePhone(formData.guardian_mobile_number)) {
      errs.guardian_mobile_number = validatePhone(formData.guardian_mobile_number);
    }

    // Second Applicant Validation (when joint account is selected)
    if (showSecond) {
      if (!(formData.second_applicant_first_name || '').trim()) errs.second_applicant_first_name = 'First name is required.';
      if (!formData.second_applicant_gender) errs.second_applicant_gender = 'Please select a gender.';
      if (!formData.second_applicant_date_of_birth) errs.second_applicant_date_of_birth = 'Date of birth is required.';
      if (!formData.second_applicant_occupation) errs.second_applicant_occupation = 'Please select an occupation.';
      if (!formData.second_applicant_marital_status) errs.second_applicant_marital_status = 'Please select a marital status.';
      if (!formData.second_applicant_residential_status) errs.second_applicant_residential_status = 'Please select a residential status.';

      // Mobile and email mandatory
      if (!(formData.second_applicant_mobile_number || '').trim()) errs.second_applicant_mobile_number = 'Mobile number is required.';
      if (!(formData.second_applicant_email || '').trim()) errs.second_applicant_email = 'Email is required.';

      // Location details mandatory for residents
      if (formData.second_applicant_residential_status === 'Resident') {
        if (!formData.second_applicant_country_of_birth_id) errs.second_applicant_country_of_birth_id = 'Please select a country.';
        if (!formData.second_applicant_state_of_birth_id) errs.second_applicant_state_of_birth_id = 'Please select a state.';
        if (!formData.second_applicant_city_of_birth_id) errs.second_applicant_city_of_birth_id = 'Please select a city.';
        if (!formData.second_applicant_annual_income) errs.second_applicant_annual_income = 'Please select an annual income range.';
      }

      // Address validation: Q1 - Is address same as first applicant?
      if (!formData.second_applicant_address_same_as_first) {
        errs.second_applicant_address_same_as_first = 'Please answer whether address is same as first applicant.';
      }

      // If address is DIFFERENT from first applicant (No), then Q2 becomes mandatory
      if (formData.second_applicant_address_same_as_first === 'No') {
        // Q2: Is communication address same as permanent address? - MANDATORY when address is different
        if (!formData.second_applicant_address_same_address) {
          errs.second_applicant_address_same_address = 'Please answer whether communication address is same as permanent address.';
        }

        // Based on Q2 answer, require appropriate proof
        if (formData.second_applicant_address_same_address === 'Yes') {
          // Communication address is SAME as permanent: need communication_address_proof
          if (!formData.second_applicant_communication_address_proof) {
            errs.second_applicant_communication_address_proof = 'Please select a document for communication address proof.';
          }
        } else if (formData.second_applicant_address_same_address === 'No') {
          // Communication address is DIFFERENT from permanent: need permanent_address_proof
          if (!formData.second_applicant_permanent_address_proof) {
            errs.second_applicant_permanent_address_proof = 'Please select a document for permanent address proof.';
          }
        }
      }

      // Stock restriction validation
      if (formData.second_applicant_stock_restrictions === 'Yes') {
        const stocks = (formData.second_applicant_stock_restrictions_list || '').split(',').map(s => s.trim()).filter(Boolean);
        if (stocks.length === 0) errs.second_applicant_stock_restrictions_list = 'Please enter at least one stock name.';
      }

      // Check if second applicant has same name+DOB as first applicant
      const secondName = `${formData.second_applicant_first_name}${formData.second_applicant_middle_name}${formData.second_applicant_last_name}`.toLowerCase().replace(/\s+/g, '');
      const firstName = `${formData.sole_holder_first_name}${formData.sole_holder_middle_name}${formData.sole_holder_last_name}`.toLowerCase().replace(/\s+/g, '');
      if (secondName === firstName && formData.second_applicant_date_of_birth === formData.date_of_birth) {
        errs.second_applicant_first_name = 'Second applicant cannot have the same name and date of birth as first applicant.';
      }
    }

    // Second Applicant Same Risk Profile Question
    if (showSameRiskQ) {
      if (!formData.same_risk_profile) errs.same_risk_profile = 'Please indicate whether second applicant has same risk profile.';
    }

    // Third Applicant Same Risk Profile Question
    if (showSameRiskQThird) {
      if (!formData.same_risk_profile_third) errs.same_risk_profile_third = 'Please indicate whether third applicant has same risk profile.';
    }

    // Third Applicant Validation (when third applicant is added)
    if (showThirdApplicant) {
      if (!(formData.third_applicant_first_name || '').trim()) errs.third_applicant_first_name = 'First name is required.';
      if (!formData.third_applicant_gender) errs.third_applicant_gender = 'Please select a gender.';
      if (!formData.third_applicant_date_of_birth) errs.third_applicant_date_of_birth = 'Date of birth is required.';
      if (!formData.third_applicant_occupation) errs.third_applicant_occupation = 'Please select an occupation.';
      if (!formData.third_applicant_marital_status) errs.third_applicant_marital_status = 'Please select a marital status.';
      if (!formData.third_applicant_residential_status) errs.third_applicant_residential_status = 'Please select a residential status.';

      // Mobile and email mandatory
      if (!(formData.third_applicant_mobile_number || '').trim()) errs.third_applicant_mobile_number = 'Mobile number is required.';
      if (!(formData.third_applicant_email || '').trim()) errs.third_applicant_email = 'Email is required.';

      // Location details mandatory for residents
      if (formData.third_applicant_residential_status === 'Resident') {
        if (!formData.third_applicant_country_of_birth_id) errs.third_applicant_country_of_birth_id = 'Please select a country.';
        if (!formData.third_applicant_state_of_birth_id) errs.third_applicant_state_of_birth_id = 'Please select a state.';
        if (!formData.third_applicant_city_of_birth_id) errs.third_applicant_city_of_birth_id = 'Please select a city.';
        if (!formData.third_applicant_annual_income) errs.third_applicant_annual_income = 'Please select an annual income range.';
      }

      // Address validation: Q1 - Is address same as first applicant?
      if (!formData.third_applicant_address_same_as_first) {
        errs.third_applicant_address_same_as_first = 'Please answer whether address is same as first applicant.';
      }

      // If address is DIFFERENT from first applicant (No), then Q2 becomes mandatory
      if (formData.third_applicant_address_same_as_first === 'No') {
        // Q2: Is communication address same as permanent address? - MANDATORY when address is different
        if (!formData.third_applicant_address_same_address) {
          errs.third_applicant_address_same_address = 'Please answer whether communication address is same as permanent address.';
        }

        // Based on Q2 answer, require appropriate proof
        if (formData.third_applicant_address_same_address === 'Yes') {
          // Communication address is SAME as permanent: need communication_address_proof
          if (!formData.third_applicant_communication_address_proof) {
            errs.third_applicant_communication_address_proof = 'Please select a document for communication address proof.';
          }
        } else if (formData.third_applicant_address_same_address === 'No') {
          // Communication address is DIFFERENT from permanent: need permanent_address_proof
          if (!formData.third_applicant_permanent_address_proof) {
            errs.third_applicant_permanent_address_proof = 'Please select a document for permanent address proof.';
          }
        }
      }

      // Stock restriction validation
      if (formData.third_applicant_stock_restrictions === 'Yes') {
        const stocks = (formData.third_applicant_stock_restrictions_list || '').split(',').map(s => s.trim()).filter(Boolean);
        if (stocks.length === 0) errs.third_applicant_stock_restrictions_list = 'Please enter at least one stock name.';
      }

      // Check if third applicant has same name+DOB as first applicant
      const thirdName = `${formData.third_applicant_first_name}${formData.third_applicant_middle_name}${formData.third_applicant_last_name}`.toLowerCase().replace(/\s+/g, '');
      const firstNameThird = `${formData.sole_holder_first_name}${formData.sole_holder_middle_name}${formData.sole_holder_last_name}`.toLowerCase().replace(/\s+/g, '');
      if (thirdName === firstNameThird && formData.third_applicant_date_of_birth === formData.date_of_birth) {
        errs.third_applicant_first_name = 'Third applicant cannot have the same name and date of birth as first applicant.';
      }

      // Check if third applicant has same name+DOB as second applicant (if joint)
      if (isJoint) {
        const secondNameThird = `${formData.second_applicant_first_name}${formData.second_applicant_middle_name}${formData.second_applicant_last_name}`.toLowerCase().replace(/\s+/g, '');
        if (thirdName === secondNameThird && formData.third_applicant_date_of_birth === formData.second_applicant_date_of_birth) {
          errs.third_applicant_first_name = 'Third applicant cannot have the same name and date of birth as second applicant.';
        }
      }
    }

    // Second Applicant Risk Profiling Validation
    if (showSecondRisk) {
      if (!formData.second_investment_experience) errs.second_investment_experience = 'Please describe your investment experience.';
      if (!parseInt(formData.second_risk_tolerance)) errs.second_risk_tolerance = 'Please set a risk tolerance value greater than 0.';
      if (!parseInt(formData.second_investment_objective)) errs.second_investment_objective = 'Please set an investment objective value greater than 0.';
      if (!parseInt(formData.second_investment_horizon)) errs.second_investment_horizon = 'Please set an investment horizon value greater than 0.';
      if (!parseInt(formData.second_portfolio_manager_allocation) || parseInt(formData.second_portfolio_manager_allocation) < 10) errs.second_portfolio_manager_allocation = 'Please set an allocation of at least 10%.';
      if (!formData.second_require_systematic_investment_plan) errs.second_require_systematic_investment_plan = 'Please answer the systematic investment plan question.';
      if (!parseInt(formData.second_expense_dependency)) errs.second_expense_dependency = 'Please set an expense dependency value greater than 0.';
      if (!formData.second_investment_risk_awareness) errs.second_investment_risk_awareness = 'Please indicate your risk awareness.';
      if (formData.second_investment_risk_awareness === 'Yes') {
        if (!parseInt(formData.second_substantial_decline_reaction)) errs.second_substantial_decline_reaction = 'Please set a reaction value greater than 0.';
        if (!formData.second_cderivatives) errs.second_cderivatives = 'Please indicate your consent for derivatives.';
      }
    }

    // Third Applicant Risk Profiling Validation
    if (showThirdRisk) {
      if (!formData.third_investment_experience) errs.third_investment_experience = 'Please describe your investment experience.';
      if (!parseInt(formData.third_risk_tolerance)) errs.third_risk_tolerance = 'Please set a risk tolerance value greater than 0.';
      if (!parseInt(formData.third_investment_objective)) errs.third_investment_objective = 'Please set an investment objective value greater than 0.';
      if (!parseInt(formData.third_investment_horizon)) errs.third_investment_horizon = 'Please set an investment horizon value greater than 0.';
      if (!parseInt(formData.third_portfolio_manager_allocation) || parseInt(formData.third_portfolio_manager_allocation) < 10) errs.third_portfolio_manager_allocation = 'Please set an allocation of at least 10%.';
      if (!formData.third_require_systematic_investment_plan) errs.third_require_systematic_investment_plan = 'Please answer the systematic investment plan question.';
      if (!parseInt(formData.third_expense_dependency)) errs.third_expense_dependency = 'Please set an expense dependency value greater than 0.';
      if (!formData.third_investment_risk_awareness) errs.third_investment_risk_awareness = 'Please indicate your risk awareness.';
      if (formData.third_investment_risk_awareness === 'Yes') {
        if (!parseInt(formData.third_substantial_decline_reaction)) errs.third_substantial_decline_reaction = 'Please set a reaction value greater than 0.';
        if (!formData.third_cderivatives) errs.third_cderivatives = 'Please indicate your consent for derivatives.';
      }
    }

    // Email validation for third applicant
    if ((formData.third_applicant_email || '').trim() && validateEmail(formData.third_applicant_email)) {
      errs.third_applicant_email = validateEmail(formData.third_applicant_email);
    }

    // Phone validation for third applicant
    if (formData.third_applicant_mobile_number && validatePhone(formData.third_applicant_mobile_number)) {
      errs.third_applicant_mobile_number = validatePhone(formData.third_applicant_mobile_number);
    }

    setFieldErrors(errs);

    const errorCount = Object.keys(errs).length;
    if (errorCount > 0) {
      console.log(`[VALIDATION] ❌ Validation failed with ${errorCount} error(s):`, errs);
      Object.entries(errs).forEach(([field, message]) => {
        console.log(`[VALIDATION]   - ${field}: ${message}`);
      });
    } else {
      console.log('[VALIDATION] ✓ All validations passed');
    }

    return errorCount === 0;
  };

  const generatePdf = async () => {
    try {
      setPdfError('');
      console.log('[PDF] Starting PDF generation...');

      if (!validate()) {
        console.log('[PDF] ❌ Validation failed with errors:', fieldErrors);
        console.log('[PDF] Nominee errors:', nomineeErrors);
        console.log('[PDF] Fields with errors:', Object.keys(fieldErrors).join(', '));
        console.log('[PDF] Nominee fields with errors:', Object.keys(nomineeErrors).join(', '));
        setPdfError('Please fix all validation errors above before generating PDF.');
        setTimeout(() => {
          // Try to scroll to first field error
          let el = document.querySelector('[data-field-error]');

          // If no field error found, try to scroll to first nominee/guardian error
          if (!el) {
            const nomineeErrorKeys = Object.keys(nomineeErrors);
            if (nomineeErrorKeys.length > 0) {
              const firstErrorKey = nomineeErrorKeys[0];
              el = document.querySelector(`[data-error-key="${firstErrorKey}"]`);
              console.log('[PDF] Scrolling to nominee error:', firstErrorKey);
            }
          }

          if (el) {
            console.log('[PDF] Scrolling to first error field:', el.parentElement?.querySelector('label')?.textContent);
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            console.log('[PDF] No error element found - checking if nominees section is collapsed');
            // Fallback: scroll to nominees section
            const nomineesSection = document.querySelector('[role="heading"]');
            if (nomineesSection) {
              nomineesSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }, 300);
        return;
      }
      console.log('[PDF] ✓ Validation passed');

      if (!riskData) {
        console.log('[PDF] Missing risk data:', riskData);
        setPdfError('⚠️ Please complete the risk profiling section first:\n\n1. Answer "Yes" to "Are you aware of capital erosion risks?"\n2. Set all slider values (must be > 0)\n3. Answer the derivatives question\n4. Then click this button again');
        return;
      }
      console.log('[PDF] ✓ Risk data available:', riskData);
      console.log('[PDF] Risk Category:', riskData.riskCategory, '| Total Score:', riskData.totalScore);

      const firstDerivatives = formData.cderivatives;
      const secondDerivatives = formData.second_cderivatives;
      const thirdDerivatives = formData.third_cderivatives;
      console.log('[PDF] First Derivatives (cderivatives):', firstDerivatives);
      console.log('[PDF] Second Derivatives (second_cderivatives):', secondDerivatives);
      console.log('[PDF] Third Derivatives (third_cderivatives):', thirdDerivatives);

      let overallDerivatives = 'Yes';
      if (firstDerivatives === 'No' || secondDerivatives === 'No') {
        overallDerivatives = 'No';
      }
      // Only check third applicant derivatives if they answered risk profiling (not same as first)
      if (showThirdRisk && thirdDerivatives === 'No') {
        overallDerivatives = 'No';
      }
      console.log('[PDF] Overall Derivatives (used for PDF):', overallDerivatives);

      let suitableIA = [];
      if (riskData.riskCategory === 'LOW' || riskData.riskCategory === 'MEDIUM') {
        suitableIA = overallDerivatives === 'No' ? ['C'] : ['C', 'D'];
        console.log('[PDF] LOW/MEDIUM risk - Selected approaches:', suitableIA);
      } else {
        suitableIA =
          overallDerivatives === 'No'
            ? ['A', 'B', 'C']
            : ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
        console.log('[PDF] HIGH risk - Selected approaches:', suitableIA);
      }

      const iaRef = [
        { code: 'A', name: 'Agreya Momentum', portfolio: 'Large Cap Direct Equity' },
        { code: 'B', name: 'Agreya Total Return', portfolio: 'Multi Cap Direct Equity' },
        { code: 'C', name: 'Agreya Multi Asset', portfolio: 'Mutual Funds, NCDs, Gsecs, PTCs, INVITs, REITs, Others' },
        { code: 'D', name: 'Agreya Multi Asset Enhancer', portfolio: 'Agreya Multi Asset portfolio + Derivatives' },
        { code: 'E', name: 'Agreya Total Return Enhancer', portfolio: 'Agreya Total Returns portfolio + Derivatives' },
        { code: 'F', name: 'Agreya Momentum Enhancer', portfolio: 'Agreya Momentum portfolio + Derivatives' },
        { code: 'G', name: 'Agreya Portfolio Return Optimizer', portfolio: 'Any permissible underlying + Derivatives' },
        { code: 'H', name: 'Agreya Value Momentum Strategy', portfolio: 'Micro and small Cap Direct Equity with Value and Momentum characteristics' },
      ];

      let firstApplicantName = `${formData.sole_holder_first_name} ${formData.sole_holder_middle_name} ${formData.sole_holder_last_name}`.replace(/\s+/g, ' ').trim();

      let secondApplicantName = '';
      if (isJoint) {
        secondApplicantName = `${formData.second_applicant_first_name} ${formData.second_applicant_middle_name} ${formData.second_applicant_last_name}`.replace(/\s+/g, ' ').trim();
      }

      let thirdApplicantName = '';
      if (showThirdApplicant && formData.third_applicant_first_name) {
        thirdApplicantName = `${formData.third_applicant_first_name} ${formData.third_applicant_middle_name} ${formData.third_applicant_last_name}`.replace(/\s+/g, ' ').trim();
      }

      // Format applicant names with proper spacing
      let applicantName = firstApplicantName;
      if (secondApplicantName && thirdApplicantName) {
        // Three applicants: First, Second and Third
        applicantName = `${firstApplicantName}, ${secondApplicantName} and ${thirdApplicantName}`;
      } else if (secondApplicantName) {
        // Two applicants: First and Second
        applicantName = `${firstApplicantName} and ${secondApplicantName}`;
      }

      // Define isCombinedRisk early (before using it)
      const isCombinedRisk = isJoint && formData.same_risk_profile === 'no';

      const expLabels = {
        no_prior_experience: 'No Prior Experience (3)',
        'Limited Experience': 'Limited Experience (7)',
        Moderate: 'Moderate Experience (7)',
        Extensive: 'Extensive Experience (10)',
      };

      // For combined risk, get minimum investment experience score and use that applicant's value
      let selectedExpLabel = formData.investment_experience;
      if (isCombinedRisk) {
        const experiences = [
          { score: getExpScore(formData.investment_experience), value: formData.investment_experience }
        ];

        if (formData.second_investment_experience) {
          experiences.push({
            score: getExpScore(formData.second_investment_experience),
            value: formData.second_investment_experience
          });
        }

        if (showThirdRisk && formData.third_investment_experience) {
          experiences.push({
            score: getExpScore(formData.third_investment_experience),
            value: formData.third_investment_experience
          });
        }

        // Find the applicant with minimum score and use their value
        // When scores are equal, prefer "Limited Experience" over "Moderate"
        const minExperience = experiences.reduce((min, curr) => {
          if (curr.score < min.score) return curr;
          if (curr.score === min.score && curr.value === 'Limited Experience' && min.value === 'Moderate') return curr;
          return min;
        });

        selectedExpLabel = minExperience.value;
      }

      const expLabel = expLabels[selectedExpLabel] || selectedExpLabel;

      const doc = new jsPDF({ format: 'a4', unit: 'mm' });
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();
      const margin = 12;
      let y = margin;

      const addWrappedText = (text, x, maxWidth, fontSize, bold = false) => {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        const lines = doc.splitTextToSize(text, maxWidth);
        doc.text(lines, x, y);
        y += lines.length * (fontSize * 0.42) + 2;
      };

      const checkPage = (needed = 20) => {
        if (y + needed > ph - margin) {
          doc.addPage();
          y = margin;
        }
      };

      // Add letterhead image from assets
      try {
        const response = await fetch(letterheadImage);
        const blob = await response.blob();
        const img = new Image();
        const dataUri = await new Promise((resolve, reject) => {
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const compressed = canvas.toDataURL('image/jpeg', 0.8);
            resolve(compressed);
          };
          img.onerror = reject;
          img.src = URL.createObjectURL(blob);
        });
        doc.addImage(dataUri, 'JPEG', 0, 0, pw, 32);
        y = 36;
        console.log('[PDF] ✓ Letterhead image added successfully');
      } catch (e) {
        // Fallback text header if image fails
        console.warn('[PDF] Letterhead image failed, using text header:', e.message);
        doc.setFillColor(3, 49, 50);
        doc.rect(0, 0, pw, 22, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('AGREYA CAPITAL ADVISORS LLP', margin, 8);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text('SEBI Registration: INP000006077 | Portfolio Manager', margin, 14);
        y = 26;
      }

      // Title
      doc.setTextColor(44, 62, 80);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`Risk Profiling Report`, pw / 2, y, { align: 'center' });
      y += 6;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`of ${applicantName}`, pw / 2, y, { align: 'center' });
      y += 8;
      doc.setLineWidth(0.5);
      doc.setDrawColor(44, 62, 80);
      doc.line(margin, y, pw - margin, y);
      y += 5;

      // Disclaimer
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Disclaimer:', margin + 2, y);
      y += 5.5;
      const disclaimer =
        'This document has been prepared by Agreya Capital Advisors LLP ("Agreya"), a SEBI registered Portfolio Manager bearing SEBI Registration Number INP000006077, based on data provided by you at the time of filling up the onboarding application form. No liability is accepted by Agreya or its representative whatsoever for any direct, indirect or consequential loss arising from the use of this document. This document is not intended as an offer or publication or solicitation for distribution or for subscription of or purchase or sale of any securities or financial instruments(s) to anyone. The information contained in this document shall not be published, reproduced or disseminated without specific confirmation from Agreya. Any person/s accessing data contained in this presentation, hereby confirms that they are not a U.S. person, within the definition of the term "US Person" under the US Securities laws.';
      doc.setFontSize(6.2);
      // doc.setFont('helvetica', 'normal');
      doc.setFont('helvetica', 'italic');
      const disclaimerWidth = pw - 2 * margin - 10;
      const disclaimerLines = doc.splitTextToSize(disclaimer, disclaimerWidth);
      const disclaimerIndent = margin + 5;
      disclaimerLines.forEach((line, index) => {
        doc.text(line, disclaimerIndent, y, { align: 'justify', maxWidth: disclaimerWidth });
        y += 3.5;
      });
      y += 2;

      // Summary Table Header
      checkPage(20);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(3, 49, 50);
      const summaryText = 'Summary of Risk Questionnaire';
      const summaryLines = doc.splitTextToSize(summaryText, pw - 2 * margin);
      summaryLines.forEach((line) => {
        doc.text(line, margin + 2, y);
        y += 2.8;
      });
      y += 1.5;
      doc.setTextColor(0, 0, 0);

      // For joint accounts with different risk profiles, use the LOWER values (more conservative)
      // isCombinedRisk already defined earlier

      const getMinValue = (val1, val2, val3 = null) => {
        const values = [parseInt(val1 || 0), parseInt(val2 || 0)];
        if (val3 !== null && showThirdRisk) {
          values.push(parseInt(val3 || 0));
        }
        return Math.min(...values);
      };

      const riskTolerance = isCombinedRisk
        ? getMinValue(formData.risk_tolerance, formData.second_risk_tolerance, formData.third_risk_tolerance)
        : parseInt(formData.risk_tolerance || 0);
      const investmentObjective = isCombinedRisk
        ? getMinValue(formData.investment_objective, formData.second_investment_objective, formData.third_investment_objective)
        : parseInt(formData.investment_objective || 0);
      const investmentHorizon = isCombinedRisk
        ? getMinValue(formData.investment_horizon, formData.second_investment_horizon, formData.third_investment_horizon)
        : parseInt(formData.investment_horizon || 0);
      const expenseDependency = isCombinedRisk
        ? getMinValue(formData.expense_dependency, formData.second_expense_dependency, formData.third_expense_dependency)
        : parseInt(formData.expense_dependency || 0);
      const substantialDecline = isCombinedRisk
        ? getMinValue(formData.substantial_decline_reaction, formData.second_substantial_decline_reaction, formData.third_substantial_decline_reaction)
        : parseInt(formData.substantial_decline_reaction || 0);

      // Portfolio allocation: use minimum if combined risk
      const portfolioAllocation = isCombinedRisk
        ? getMinValue(formData.portfolio_manager_allocation, formData.second_portfolio_manager_allocation, formData.third_portfolio_manager_allocation)
        : parseInt(formData.portfolio_manager_allocation || 0);

      const colW = [(pw - 2 * margin) * 0.55, (pw - 2 * margin) * 0.15, (pw - 2 * margin) * 0.30];
      const headers = ['Question', 'Scale', 'Answer'];
      const rows = [
        ['Describe your Investment Experience?', '1-10', expLabel],
        ['How would you personally describe your risk tolerance?', '1-10', `${riskTolerance}`],
        ['What is your Investment Objective?', '1-10', `${investmentObjective}`],
        ['What is your Investment horizon?', '1-10', `${investmentHorizon}`],
        ['Portfolio allocation to Portfolio Manager?', '1%-100%', `${portfolioAllocation}%`],
        ['How dependent are you on this investment to meet monthly expenses?', '1-10', `${expenseDependency}`],
        ['How do you react to a substantial decline (>25%) in your equity portfolio?', '1-10', `${substantialDecline}`],
        ['Are you aware of capital erosion risks?', 'Yes/No', `${formData.investment_risk_awareness}`],
        ['Are you consenting for use of derivatives in underlying investment approaches?', 'Yes/No', `${overallDerivatives}`],
      ];

      // Draw table header (matching old form - gold/brown color)
      doc.setFillColor(184, 134, 11);  // Dark goldenrod color to match old form
      doc.rect(margin, y, pw - 2 * margin, 5.5, 'F');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(7.2);
      doc.setFont('helvetica', 'bold');
      let x = margin;
      headers.forEach((h, i) => {
        doc.text(h, x + 1.2, y + 3.8);
        x += colW[i];
      });
      y += 5.5;

      // Draw table rows
      doc.setFont('helvetica', 'normal');
      rows.forEach((row, ri) => {
        checkPage(8);
        const rowH = 5.8;
        doc.setFillColor(ri % 2 === 0 ? 244 : 255, ri % 2 === 0 ? 246 : 255, ri % 2 === 0 ? 246 : 255);
        doc.rect(margin, y, pw - 2 * margin, rowH, 'F');
        doc.setDrawColor(200, 200, 200);
        doc.rect(margin, y, pw - 2 * margin, rowH);
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(6.5);
        x = margin;
        row.forEach((cell, ci) => {
          const cellLines = doc.splitTextToSize(String(cell), colW[ci] - 2.5);
          doc.text(cellLines, x + 1.2, y + 3.5);
          x += colW[ci];
        });
        y += rowH;
      });

      y += 4.5;
      checkPage(12);

      // Risk Category Section Title
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(3, 49, 50);
      doc.text('Risk Category and Risk Outlook', margin + 3, y);
      y += 2;

      // Risk Category Box
      const riskBoxHeight = 6;
      // doc.setFillColor(240, 240, 240);
      // doc.rect(margin + 2, y, pw - 2 * margin - 4, riskBoxHeight, 'F');
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);

      // Build text with different formatting for risk category and outlook
      let xPos = margin + 4;
      const textY = y + 3.5;

      // Part 1: "Agreya determines your Risk Category as "
      doc.setFont('helvetica', 'normal');
      doc.text('Agreya determines your Risk Category as ', xPos, textY);
      const part1Width = doc.getTextWidth('Agreya determines your Risk Category as ');
      xPos += part1Width;

      // Part 2: Risk Category (bold + underline)
      doc.setFont('helvetica', 'bold');
      doc.text(riskData.riskCategory, xPos, textY);
      const part2Width = doc.getTextWidth(riskData.riskCategory);
      doc.setLineWidth(0.3);
      doc.setDrawColor(0, 0, 0);
      doc.line(xPos, textY + 0.8, xPos + part2Width, textY + 0.8);
      xPos += part2Width;

      // Part 3: " and Risk Outlook as "
      doc.setFont('helvetica', 'normal');
      doc.text(' and Risk Outlook as ', xPos, textY);
      const part3Width = doc.getTextWidth(' and Risk Outlook as ');
      xPos += part3Width;

      // Part 4: Risk Outlook (bold + underline)
      doc.setFont('helvetica', 'bold');
      doc.text(riskData.riskOutlook, xPos, textY);
      const part4Width = doc.getTextWidth(riskData.riskOutlook);
      doc.setLineWidth(0.3);
      doc.setDrawColor(0, 0, 0);
      doc.line(xPos, textY + 0.8, xPos + part4Width, textY + 0.8);
      xPos += part4Width;

      // Part 5: "."
      doc.setFont('helvetica', 'normal');
      doc.text('.', xPos, textY);

      y += riskBoxHeight + 3;

      // Suitable Investment Approaches
      checkPage(15);
      doc.setFontSize(8);
      doc.setTextColor(3, 49, 50);
      doc.setFont('helvetica', 'bold');
      doc.text('Based on your Risk Category & Risk Outlook, Agreya can offer the following Investment Approaches to you:', margin + 3, y);
      y += 5.5;

      // Investment Approaches - List
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      const itemHeight = 4.5;

      iaRef
        .filter((ia) => suitableIA.includes(ia.code))
        .forEach((ia) => {
          checkPage(6);
          doc.text(`• ${ia.code} - ${ia.name}`, margin + 3, y);
          y += itemHeight;
        });
      y += 0.5;

      y += 1;
      checkPage(20);
      doc.setFontSize(7.2);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const refText = 'Please refer to the detailed references below:';
      const refLines = doc.splitTextToSize(refText, pw - 2 * margin);
      refLines.forEach((line) => {
        doc.text(line, margin + 2, y);
        y += 2.8;
      });
      y += 3;

      // IA Reference Table
      checkPage(18);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(3, 49, 50);
      const iaRefText = 'Agreya Investment Approaches (IA) and Risk - IA Suitability Matrix';
      const iaRefLines = doc.splitTextToSize(iaRefText, pw - 2 * margin);
      iaRefLines.forEach((line) => {
        doc.text(line, margin + 2, y);
        y += 2.8;
      });
      y += 1.2;

      // IA Reference Table - Centered
      const iaTableWidth = pw - 2 * margin;
      const iaTableStartX = margin;
      const iaColW = [iaTableWidth * 0.15, iaTableWidth * 0.35, iaTableWidth * 0.50];
      const iaHeaders = ['Code of IA', 'Name of IA', 'Underlying Portfolio'];

      // Draw IA table header
      doc.setFillColor(184, 134, 11);
      doc.rect(iaTableStartX, y, iaTableWidth, 4.5, 'F');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      let iaX = iaTableStartX;
      iaHeaders.forEach((h, i) => {
        doc.text(h, iaX + 1.2, y + 3);
        iaX += iaColW[i];
      });
      y += 4.5;

      // Draw IA rows
      doc.setFont('helvetica', 'normal');
      iaRef.forEach((ia, ri) => {
        checkPage(7);
        const rowH = 4.8;
        doc.setFillColor(ri % 2 === 0 ? 244 : 255, ri % 2 === 0 ? 246 : 255, ri % 2 === 0 ? 246 : 255);
        doc.rect(iaTableStartX, y, iaTableWidth, rowH, 'F');
        doc.setDrawColor(200, 200, 200);
        doc.rect(iaTableStartX, y, iaTableWidth, rowH);
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(6.2);
        iaX = iaTableStartX;
        const iaRow = [ia.code, ia.name, ia.portfolio];
        iaRow.forEach((cell, ci) => {
          const cellLines = doc.splitTextToSize(String(cell), iaColW[ci] - 2.5);
          doc.text(cellLines, iaX + 1.2, y + 2.8);
          iaX += iaColW[ci];
        });
        y += rowH;
      });

      y += 2;
      checkPage(22);

      // Suitability Matrix Table - Centered within IA table
      const matrixTableWidth = iaTableWidth * 0.85;
      const matrixTableStartX = iaTableStartX + (iaTableWidth - matrixTableWidth) / 2;
      const matrixColW = [matrixTableWidth * 0.25, matrixTableWidth * 0.25, matrixTableWidth * 0.25, matrixTableWidth * 0.25];
      const matrixHeaders = ['Risk Category', 'Risk Outlook', 'Consent for Derivatives', 'Suitable IA\'s'];

      // Draw matrix table header
      doc.setFillColor(184, 134, 11);
      doc.rect(matrixTableStartX, y, matrixTableWidth, 4.5, 'F');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'bold');
      let matrixX = matrixTableStartX;
      matrixHeaders.forEach((h, i) => {
        doc.text(h, matrixX + 1.2, y + 3);
        matrixX += matrixColW[i];
      });
      y += 4.5;

      // Matrix data
      const matrixData = [
        ['Low/Medium', 'Conservative/Moderate', 'No', 'C'],
        ['Low/Medium', 'Conservative/Moderate', 'Yes', 'C,D'],
        ['High', 'Aggressive', 'No', 'A,B,C'],
        ['High', 'Aggressive', 'Yes', 'All'],
      ];

      doc.setFont('helvetica', 'normal');
      matrixData.forEach((row, ri) => {
        checkPage(7);
        const rowH = 5.2;
        doc.setFillColor(ri % 2 === 0 ? 244 : 255, ri % 2 === 0 ? 246 : 255, ri % 2 === 0 ? 246 : 255);
        doc.rect(matrixTableStartX, y, matrixTableWidth, rowH, 'F');
        doc.setDrawColor(200, 200, 200);
        doc.rect(matrixTableStartX, y, matrixTableWidth, rowH);
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(6.2);
        matrixX = matrixTableStartX;
        row.forEach((cell, ci) => {
          const cellLines = doc.splitTextToSize(String(cell), matrixColW[ci] - 2.5);
          doc.text(cellLines, matrixX + 1.2, y + 2.8);
          matrixX += matrixColW[ci];
        });
        y += rowH;
      });

      const dataUri = doc.output('datauristring');
      setPdfData(dataUri);
      setPdfDownloaded(true);
      console.log('[PDF] ✓ PDF generated and downloaded successfully');
      doc.save('Risk-Profiling-Detailed.pdf');
    } catch (error) {
      console.error('[PDF ERROR] Failed to generate PDF:', error);
      setPdfError('❌ Error generating PDF: ' + (error.message || 'Unknown error occurred. Please check the browser console for details.'));
    }
  };

  // Convert HTML date (YYYY-MM-DD) to backend format (DD-MM-YYYY)
  const formatDateForBackend = (dateValue) => {
    if (!dateValue) return '';
    const [y, m, d] = dateValue.split('-');
    return `${d}-${m}-${y}`;
  };

  const handleSubmit = async (e) => {
    console.log('[SUBMISSION] 🎯 handleSubmit CALLED - form submission initiated');
    e.preventDefault();
    console.log('[SUBMISSION] ✓ preventDefault() called');

    // CHECK 1: PDF Downloaded
    console.log('[SUBMISSION] ⏸️  CHECK 1 - PDF Downloaded?', pdfDownloaded);
    if (!pdfDownloaded) {
      console.error('[SUBMISSION] ❌ BLOCKED: PDF not downloaded');
      setSubmitError('Please click "Yes, I accept" to generate and download the Risk Profiling PDF before submitting.');
      setTimeout(() => {
        const errorBanner = document.querySelector('[role="alert"][aria-live="assertive"]');
        if (errorBanner) errorBanner.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }
    console.log('[SUBMISSION] ✓ PDF downloaded - proceeding');

    // ===== RECAPTCHA V3 TOKEN GENERATION =====
    console.log('[SUBMISSION] 🤖 Generating reCAPTCHA token...');
    const token = await generateRecaptchaToken();
    console.log('[SUBMISSION] ⏸️  reCAPTCHA token received?', !!token);
    if (!token) {
      console.error('[SUBMISSION] ❌ BLOCKED: reCAPTCHA token generation failed');
      setSubmitError('Failed to verify reCAPTCHA. Please refresh the page and try again.');
      setTimeout(() => {
        const errorBanner = document.querySelector('[role="alert"][aria-live="assertive"]');
        if (errorBanner) errorBanner.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }
    console.log('[SUBMISSION] ✓ reCAPTCHA token generated successfully:', token.substring(0, 30) + '...');
    setRecaptchaToken(token);

    // CHECK 2: Validate Function
    console.log('[SUBMISSION] ⏸️  CHECK 2 - Running validate() function...');
    const isValid = validate();
    console.log('[SUBMISSION] ⏸️  validate() returned:', isValid);
    if (!isValid) {
      console.error('[SUBMISSION] ❌ BLOCKED: validate() returned false - form has validation errors');
      setTimeout(() => {
        // Try to scroll to first field error
        let el = document.querySelector('[data-field-error]');

        // If no field error found, try to scroll to first nominee/guardian error
        if (!el) {
          const nomineeErrorKeys = Object.keys(nomineeErrors);
          if (nomineeErrorKeys.length > 0) {
            const firstErrorKey = nomineeErrorKeys[0];
            el = document.querySelector(`[data-error-key="${firstErrorKey}"]`);
            console.log('[VALIDATION] Scrolling to nominee error:', firstErrorKey);
          }
        }

        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      return;
    }
    console.log('[SUBMISSION] ✓ Validation passed');

    // CHECK 3: Investment Approach & Fee Model (submit only)
    console.log('[SUBMISSION] ⏸️  CHECK 3 - Investment approach validation...');
    const approachErrs = {};
    if (formData.investment_approach.length === 0) {
      approachErrs.investment_approach = 'Please select at least one investment approach.';
    } else {
      const missingFee = formData.investment_approach.filter(
        (a) => !formData.fixed_fee_model.includes(APPROACH_FEE_MAP[a]?.fixed) &&
               !formData.hybrid_fee_model.includes(APPROACH_FEE_MAP[a]?.hybrid)
      );
      if (missingFee.length > 0) {
        approachErrs.investment_approach = `Please select either a Fixed or Hybrid Fee Model for: ${missingFee.join(', ')}.`;
      }
    }
    if (Object.keys(approachErrs).length > 0) {
      console.error('[SUBMISSION] ❌ BLOCKED: Investment approach validation failed:', approachErrs);
      setFieldErrors((prev) => ({ ...prev, ...approachErrs }));
      setTimeout(() => {
        const el = document.querySelector('[data-field-error]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }
    console.log('[SUBMISSION] ✓ Investment approach validation passed');

    // CHECK 4: File Validation Errors
    console.log('[SUBMISSION] ⏸️  CHECK 4 - File validation...');
    console.log('[SUBMISSION] File errors - cancelled:', !!cancelledChequeError, 'pan:', !!panCardError, 'address:', !!addressProofError);
    if (cancelledChequeError || panCardError || addressProofError) {
      console.error('[SUBMISSION] ❌ BLOCKED: File validation errors exist');
      if (isMountedRef.current) setSubmitError('Please fix file upload errors before submitting.');
      return;
    }
    console.log('[SUBMISSION] ✓ File validation passed');

    console.log('[SUBMISSION] ✓ All pre-submission checks passed - proceeding to API call');
    if (isMountedRef.current) {
      setLoading(true);
      setSubmitError('');
    }

    const fd = new FormData();

    // Add reCAPTCHA token for bot verification
    if (token) {
      fd.append('recaptcha_token', token);
      console.log('[SUBMISSION] reCAPTCHA token appended to FormData');
    } else {
      console.warn('[SUBMISSION] WARNING: reCAPTCHA token is empty!');
    }

    // Text fields (exclude arrays and stock lists — handled separately)
    const textFields = { ...formData };
    delete textFields.investment_approach;
    delete textFields.fixed_fee_model;
    delete textFields.hybrid_fee_model;
    delete textFields.first_applicant_stock_restrictions_list;
    delete textFields.second_applicant_stock_restrictions_list;
    delete textFields.third_applicant_stock_restrictions_list;

    Object.entries(textFields).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') fd.append(k, v);
    });

    // Array fields
    formData.investment_approach.forEach((v) => fd.append('investment_approach', v));
    formData.fixed_fee_model.forEach((v) => fd.append('fixed_fee_model', v));
    formData.hybrid_fee_model.forEach((v) => fd.append('hybrid_fee_model', v));

    // Stock restrictions: send each selected stock as a separate form value
    if (formData.first_applicant_stock_restrictions_list) {
      formData.first_applicant_stock_restrictions_list
        .split(',').map((s) => s.trim()).filter(Boolean)
        .forEach((s) => fd.append('first_applicant_stock_restrictions_list', s));
    }
    if (formData.second_applicant_stock_restrictions_list) {
      formData.second_applicant_stock_restrictions_list
        .split(',').map((s) => s.trim()).filter(Boolean)
        .forEach((s) => fd.append('second_applicant_stock_restrictions_list', s));
    }
    if (formData.third_applicant_stock_restrictions_list) {
      formData.third_applicant_stock_restrictions_list
        .split(',').map((s) => s.trim()).filter(Boolean)
        .forEach((s) => fd.append('third_applicant_stock_restrictions_list', s));
    }

    // Convert dates from YYYY-MM-DD to DD-MM-YYYY for backend
    ['date_of_birth', 'second_applicant_date_of_birth', 'third_applicant_date_of_birth', 'first_nominee_dob'].forEach((key) => {
      const val = fd.get(key);
      if (val) fd.set(key, formatDateForBackend(val));
    });

    // Combine phone country code with number using intlTelInput instances
    ['mobile_number', 'second_applicant_mobile_number', 'third_applicant_mobile_number', 'first_nominee_mobile_number', 'guardian_mobile_number'].forEach((key) => {
      const iti = itiRefs.current[key];
      if (iti) {
        fd.set(key, iti.getNumber());
      }
    });

    // Resolve country/state/city names (backend needs names, not IDs)
    const cob = countries.find((c) => String(c.id) === formData.country_of_birth_id);
    const sob = states.find((s) => String(s.id) === formData.state_of_birth_id);
    const cityObj = cities.find((c) => String(c.id) === formData.city_of_birth_id);
    fd.set('country_of_birth', cob ? cob.name : '');
    fd.set('state_of_birth', sob ? sob.name : '');
    fd.set(
      'city_of_birth',
      formData.city_of_birth_id === 'other' ? formData.manual_city_of_birth : cityObj ? cityObj.name : ''
    );

    const scob = countries.find((c) => String(c.id) === formData.second_applicant_country_of_birth_id);
    const ssob = secondStates.find((s) => String(s.id) === formData.second_applicant_state_of_birth_id);
    const scityObj = secondCities.find((c) => String(c.id) === formData.second_applicant_city_of_birth_id);
    fd.set('second_applicant_country_of_birth', scob ? scob.name : '');
    fd.set('second_applicant_state_of_birth', ssob ? ssob.name : '');
    fd.set(
      'second_applicant_city_of_birth',
      formData.second_applicant_city_of_birth_id === 'other'
        ? formData.manual_second_city_of_birth
        : scityObj
        ? scityObj.name
        : ''
    );

    // Third applicant location data
    if (showThirdApplicant) {
      const tcob = countries.find((c) => String(c.id) === formData.third_applicant_country_of_birth_id);
      const tsob = thirdStates.find((s) => String(s.id) === formData.third_applicant_state_of_birth_id);
      const tcityObj = thirdCities.find((c) => String(c.id) === formData.third_applicant_city_of_birth_id);
      fd.set('third_applicant_country_of_birth', tcob ? tcob.name : '');
      fd.set('third_applicant_state_of_birth', tsob ? tsob.name : '');
      fd.set(
        'third_applicant_city_of_birth',
        formData.third_applicant_city_of_birth_id === 'other'
          ? formData.manual_third_city_of_birth
          : tcityObj
          ? tcityObj.name
          : ''
      );
    }

    // Risk score
    if (riskData) {
      fd.set('total_score', String(riskData.totalScore));
      fd.set('risk_category', riskData.riskCategory);
    }

    // PDF
    if (pdfData) fd.set('pdf_data', pdfData);

    // Nominees (as indexed fields)
    if (formData.has_nominee === 'Yes') {
      nominees.forEach((nom, i) => {
        fd.append(`nominee_${i}_first_name`, nom.first_name || '');
        fd.append(`nominee_${i}_middle_name`, nom.middle_name || '');
        fd.append(`nominee_${i}_last_name`, nom.last_name || '');
        fd.append(`nominee_${i}_is_minor`, nom.is_minor || '');
        fd.append(`nominee_${i}_dob`, nom.dob ? formatDateForBackend(nom.dob) : '');
        fd.append(`nominee_${i}_relationship`, nom.relationship || '');
        fd.append(`nominee_${i}_address_same`, nom.address_same || '');
        fd.append(`nominee_${i}_full_address`, nom.full_address || '');
        fd.append(`nominee_${i}_id_proof_type`, nom.id_proof_type || '');
        fd.append(`nominee_${i}_id_proof_other`, nom.id_proof_other || '');
        fd.append(`nominee_${i}_mobile_number`, nom.mobile_number || '');
        fd.append(`nominee_${i}_email`, nom.email || '');
        fd.append(`nominee_${i}_sharing_percentage`, nom.sharing_percentage || '');
        if (nomineeIdProofs[i]) fd.append(`nominee_${i}_id_proof`, nomineeIdProofs[i]);

        // Guardian data (if nominee is minor)
        if (nom.is_minor === 'Yes') {
          const guardian = guardians[i];
          fd.append(`guardian_${i}_first_name`, guardian.first_name || '');
          fd.append(`guardian_${i}_middle_name`, guardian.middle_name || '');
          fd.append(`guardian_${i}_last_name`, guardian.last_name || '');
          fd.append(`guardian_${i}_mobile_number`, guardian.mobile_number || '');
          fd.append(`guardian_${i}_email`, guardian.email || '');
          fd.append(`guardian_${i}_communication_address_proof`, guardian.communication_address_proof || '');
          fd.append(`guardian_${i}_communication_proof_other`, guardian.communication_proof_other || '');
        }
      });
    }

    // Files
    if (cancelledCheque) fd.append('cancelled_cheque', cancelledCheque);
    panCardCopies.forEach((f) => fd.append('pan_card_copies', f));
    addressProofCopies.forEach((f) => fd.append('address_proof_copies', f));

    console.log('[SUBMISSION] Files being sent - cancelled_cheque:', !!cancelledCheque, 'pan_cards:', panCardCopies.length, 'address_proofs:', addressProofCopies.length);

    // Distributor
    fd.set('distributor_name', distributorFullName);
    if (assignedRM) fd.set('relationship_manager', assignedRM);

    const url = distributor
      ? `${API_BASE_URL}/api/onboarding/new-client/${distributor}/`
      : `${API_BASE_URL}/api/onboarding/new-client/`;

    console.log('[SUBMISSION] 📤 SENDING API REQUEST');
    console.log('[SUBMISSION] URL:', url);
    console.log('[SUBMISSION] FormData size:', new Blob([...fd]).size, 'bytes');
    console.log('[SUBMISSION] FormData keys count:', Array.from(fd.keys()).length);
    console.log('[SUBMISSION] FormData has recaptcha_token?', fd.has('recaptcha_token'));
    console.log('[SUBMISSION] FormData first 20 keys:', Array.from(fd.keys()).slice(0, 20));

    try {
      console.log('[SUBMISSION] 🌐 Making axios POST request...');
      const res = await axios.post(url, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      console.log('[SUBMISSION] ✅ RESPONSE RECEIVED (200):', res.data);
      if (isMountedRef.current) {
        if (res.data.success) {
          console.log('[SUBMISSION] ✅ SUCCESS! Redirecting to success page.');
          history.push('/onboarding-success');
        } else {
          console.log('[SUBMISSION] ⚠️  Response success=false. Error:', res.data.error);
          setSubmitError(encodeForDisplay(res.data.error || 'Submission failed. Please try again.'));
        }
      }
    } catch (err) {
      const timestamp = new Date().toISOString();
      const errorLog = {
        timestamp,
        type: 'SUBMISSION_ERROR',
        message: err.message,
        code: err.code,
        status: err.response?.status,
        url: url,
        formDataKeys: Array.from(fd.keys()),
      };

      // Log detailed network error information
      if (!err.response) {
        errorLog.errorType = 'NETWORK_ERROR';

        if (err.code === 'ECONNABORTED') {
          errorLog.details = 'Request timeout - server took too long to respond';
          console.error('[SUBMISSION] ⚠️ NETWORK TIMEOUT:', errorLog);
        } else if (err.message === 'Network Error') {
          errorLog.details = 'Network error - check internet connection or CORS settings';
          console.error('[SUBMISSION] ⚠️ NETWORK ERROR:', errorLog);
        } else {
          errorLog.details = err.message;
          console.error('[SUBMISSION] ⚠️ ERROR (no response):', errorLog);
        }

        const networkErrorMsg = 'Network error - please check your internet connection and try again.';
        if (isMountedRef.current) setSubmitError(encodeForDisplay(networkErrorMsg));
      } else {
        // Log server errors
        const errorData = err.response?.data;
        errorLog.errorType = 'SERVER_ERROR';
        errorLog.responseData = errorData;

        console.error('[SUBMISSION] ❌ Server Error (HTTP ' + err.response.status + '):', errorLog);

        // Handle server-side validation errors
        if (errorData?.errors && typeof errorData.errors === 'object') {
          console.log('[SUBMISSION] 📋 Server validation errors received:', errorData.errors);

          // Log each validation error
          Object.entries(errorData.errors).forEach(([field, message]) => {
            console.log(`[SUBMISSION]   - ${field}: ${message}`);
          });

          // Separate nominee/guardian errors from field errors
          const fieldErrs = {};
          const nomErrs = {};

          Object.entries(errorData.errors).forEach(([key, value]) => {
            const safeValue = normalizeAndEncodeErrorValue(value);
            if (key.startsWith('nominee_') || key.startsWith('guardian_')) {
              nomErrs[key] = safeValue;
              console.log(`[SUBMISSION] 👤 Adding nominee error: ${key} = ${safeValue}`);
            } else {
              fieldErrs[key] = safeValue;
              console.log(`[SUBMISSION] 📋 Adding field error: ${key} = ${safeValue}`);
            }
          });

          console.log('[SUBMISSION] 📊 Error summary - fieldErrs:', Object.keys(fieldErrs).length, 'nomErrs:', Object.keys(nomErrs).length);
          console.log('[SUBMISSION] 📋 fieldErrs object:', fieldErrs);
          console.log('[SUBMISSION] 👤 nomErrs object:', nomErrs);

          // Set error state (don't check mounted - error display is critical)
          console.log('[SUBMISSION] ✓ Setting error state for display');
          setFieldErrors(fieldErrs);
          if (Object.keys(nomErrs).length > 0) {
            setNomineeErrors(nomErrs);
            console.log('[SUBMISSION] 👤 Nominee/Guardian errors set:', Object.keys(nomErrs).length, 'keys:', Object.keys(nomErrs));
          }

          // Build comprehensive error summary for display
          const fieldErrorCount = Object.keys(fieldErrs).length;
          const nomErrorCount = Object.keys(nomErrs).length;
          const totalErrorCount = fieldErrorCount + nomErrorCount;

          let errorSummary = errorData.message || `Please fix the following ${totalErrorCount} validation error(s):`;

          if (fieldErrorCount > 0) {
            const fieldList = Object.keys(fieldErrs)
              .slice(0, 5)
              .map(f => `• ${f.replace(/_/g, ' ')}`)
              .join('\n');
            errorSummary += `\n\nForm Fields (${fieldErrorCount}):\n${fieldList}`;
            if (fieldErrorCount > 5) {
              errorSummary += `\n... and ${fieldErrorCount - 5} more`;
            }
          }

          if (nomErrorCount > 0) {
            errorSummary += `\n\nNominee/Guardian Fields (${nomErrorCount}): Check nominee details below`;
          }

          console.log('[SUBMISSION] 📢 Setting submit error banner');
          setSubmitError(encodeForDisplay(errorSummary));

          // Scroll to first error field
          setTimeout(() => {
            const errorKeys = Object.keys(errorData.errors);
            console.log('[SUBMISSION] 🔍 Attempting to scroll. Error keys:', errorKeys);
            if (errorKeys.length > 0) {
              const firstErrorKey = errorKeys[0];
              console.log(`[SUBMISSION] 🔍 First error key: ${firstErrorKey}`);

              let firstErrorField = document.querySelector(`[name="${firstErrorKey}"]`);
              console.log(`[SUBMISSION] 🔍 Looking for [name="${firstErrorKey}"]... found?`, !!firstErrorField);

              if (firstErrorField) {
                console.log(`[SUBMISSION] 🔍 Scrolling to first error field: ${firstErrorKey}`);
                firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
              } else {
                // Try to find error message div (for nominee/guardian errors)
                console.log(`[SUBMISSION] 🔍 Looking for [data-error-key="${firstErrorKey}"]...`);
                let errorDiv = document.querySelector(`[data-error-key="${firstErrorKey}"]`);
                console.log(`[SUBMISSION] 🔍 Found error div?`, !!errorDiv);

                if (errorDiv) {
                  console.log(`[SUBMISSION] 🔍 Scrolling to error element: ${firstErrorKey}`);
                  errorDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else {
                  // For guardian/nominee errors, try to scroll to the nominee section
                  if (firstErrorKey.includes('guardian_') || firstErrorKey.includes('nominee_')) {
                    console.log(`[SUBMISSION] 👤 Guardian/Nominee error detected: ${firstErrorKey}`);
                    // Try to find the nominee section header (H3 with nominee info)
                    const nomineeIdx = firstErrorKey.match(/\d+/)?.[0];
                    if (nomineeIdx !== undefined) {
                      const nomineeSection = document.querySelector(`[data-nominee-section="${nomineeIdx}"]`);
                      if (nomineeSection) {
                        console.log(`[SUBMISSION] 👤 Scrolling to nominee section: ${nomineeIdx}`);
                        nomineeSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      } else {
                        // Fallback: scroll to any H3 containing nominee/guardian text
                        const h3s = Array.from(document.querySelectorAll('h3'));
                        const nomineeH3 = h3s.find(h => h.textContent.includes('Nominee') || h.textContent.includes('Guardian'));
                        if (nomineeH3) {
                          console.log(`[SUBMISSION] 👤 Scrolling to nominee/guardian section header`);
                          nomineeH3.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }
                    }
                  } else {
                    // Debug: check what error divs exist
                    const allErrorDivs = document.querySelectorAll('[data-error-key]');
                    console.log(`[SUBMISSION] 🔍 DEBUG: Total [data-error-key] divs in DOM:`, allErrorDivs.length);
                    if (allErrorDivs.length > 0) {
                      console.log('[SUBMISSION] 🔍 DEBUG: Existing data-error-key values:', Array.from(allErrorDivs).map(el => el.getAttribute('data-error-key')));
                    }
                  }
                }
              }
            }
          }, 500);
        } else if (err.response.status === 413) {
          const fileSizeMsg = 'File size too large. Please reduce the size of your uploaded files.';
          console.error('[SUBMISSION] 📦 FILE SIZE ERROR:', fileSizeMsg);
          if (isMountedRef.current) setSubmitError(encodeForDisplay(fileSizeMsg));
        } else if (err.response.status === 400) {
          const badRequestMsg = errorData?.error || 'Invalid form data. Please check your entries.';
          console.error('[SUBMISSION] ❌ BAD REQUEST (400):', badRequestMsg);
          if (isMountedRef.current) setSubmitError(encodeForDisplay(badRequestMsg));
        } else if (err.response.status === 403 || err.response.status === 401) {
          const authMsg = 'Authentication failed. Please refresh the page and try again.';
          console.error('[SUBMISSION] 🔐 AUTH ERROR:', authMsg);
          if (isMountedRef.current) setSubmitError(encodeForDisplay(authMsg));
        } else if (err.response.status >= 500) {
          const serverMsg = 'Server error. Our team has been notified. Please try again later.';
          console.error('[SUBMISSION] 🚨 SERVER ERROR (5xx):', errorData?.error || err.message);
          if (isMountedRef.current) setSubmitError(encodeForDisplay(serverMsg));
        } else {
          const genericMsg = errorData?.error || err.message || 'An error occurred. Please check the form and try again.';
          console.error('[SUBMISSION] ⚠️ ERROR:', genericMsg);
          if (isMountedRef.current) setSubmitError(encodeForDisplay(genericMsg));
        }
      }

      // Log full error object for debugging
      console.log('[SUBMISSION] Full error object:', {
        name: err.name,
        message: err.message,
        stack: err.stack?.split('\n').slice(0, 5),
        config: err.config ? {
          url: err.config.url,
          method: err.config.method,
          headers: Object.keys(err.config.headers || {}),
        } : null,
      });
    }
    if (isMountedRef.current) setLoading(false);
  };

  // Helper: radio group
  const RadioGroup = ({ name, options, required = false }) => (
    <fieldset style={{ border: 'none', padding: 0, margin: 0, marginBottom: '8px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
        {options.map((opt) => (
          <label key={opt.value} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'normal' }}>
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={formData[name] === opt.value}
              onChange={handleChange}
              required={required}
              style={{ marginRight: '8px' }}
              aria-label={opt.label}
            />
            {opt.label}
          </label>
        ))}
      </div>
    </fieldset>
  );

  // Helper: slider with keyboard accessibility
  const sliderRefs = useRef({});

  const handleSliderKeyDown = (e, name, min, max, step) => {
    const currentValue = parseInt(formData[name] || 0);
    let newValue = currentValue;
    let shouldUpdate = false;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        newValue = Math.min(currentValue + step, max);
        shouldUpdate = true;
        e.preventDefault();
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        newValue = Math.max(currentValue - step, min);
        shouldUpdate = true;
        e.preventDefault();
        break;
      case 'Home':
        newValue = min;
        shouldUpdate = true;
        e.preventDefault();
        break;
      case 'End':
        newValue = max;
        shouldUpdate = true;
        e.preventDefault();
        break;
      case 'PageUp':
        newValue = Math.min(currentValue + step * 5, max);
        shouldUpdate = true;
        e.preventDefault();
        break;
      case 'PageDown':
        newValue = Math.max(currentValue - step * 5, min);
        shouldUpdate = true;
        e.preventDefault();
        break;
      default:
        return;
    }

    if (shouldUpdate) {
      setFormData((prev) => ({ ...prev, [name]: newValue }));
      // Maintain focus on slider after state update
      setTimeout(() => {
        if (sliderRefs.current[name]) {
          sliderRefs.current[name].focus();
        }
      }, 0);
    }
  };

  const Slider = ({ name, min = 0, max = 10, step = 1, suffix = '' }) => (
    <div style={{ marginBottom: '8px' }}>
      <input
        ref={(el) => {
          if (el) sliderRefs.current[name] = el;
        }}
        type="range"
        name={name}
        min={min}
        max={max}
        step={step}
        value={formData[name]}
        onChange={handleChange}
        onKeyDown={(e) => handleSliderKeyDown(e, name, min, max, step)}
        style={{ width: '60%' }}
        aria-label={`${name.replace(/_/g, ' ')} slider, current value ${formData[name]}${suffix}`}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={formData[name]}
        aria-valuetext={`${formData[name]}${suffix}`}
        role="slider"
        tabIndex="0"
      />
      <span style={{ marginLeft: '10px', fontWeight: 'bold' }} aria-live="polite" aria-atomic="true">
        {formData[name]}
        {suffix}
      </span>
    </div>
  );

  // Helper: phone input using intl-tel-input (mirrors original onboarding-form.js intlTelInput usage)
  const PhoneInput = ({ name, required = false, ariaLabel = '' }) => {
    const inputRef = useRef(null);
    useEffect(() => {
      if (!inputRef.current) return;

      // Initialize input value from formData on mount
      if (formData[name]) {
        inputRef.current.value = formData[name];
      }

      const iti = intlTelInput(inputRef.current, {
        initialCountry: 'in',
        autoPlaceholder: 'polite',
        separateDialCode: true,
        loadUtils: () => import('intl-tel-input/dist/js/utils.js'),
      });
      itiRefs.current[name] = iti;
      return () => {
        iti.destroy();
        delete itiRefs.current[name];
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <input
        ref={inputRef}
        type="tel"
        name={name}
        required={required}
        onChange={handleChange}
        onBlur={handleFieldBlur}
        style={{ ...inputStyle, maxWidth: '280px' }}
        aria-label={ariaLabel || name.replace(/_/g, ' ')}
        aria-required={required}
        aria-invalid={!!fieldErrors[name]}
        aria-describedby={fieldErrors[name] ? `${name}-error` : null}
      />
    );
  };

  // Helper: stock search autocomplete - searches StockRestriction model (NSE/BSE data)
  const StockSearch = ({ name }) => {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const searchTimerRef = useRef(null);
    const selected = formData[name]
      ? formData[name].split(',').map((s) => s.trim()).filter(Boolean)
      : [];

    const handleQuery = (e) => {
      const val = e.target.value;
      setQuery(val);
      clearTimeout(searchTimerRef.current);
      if (val.length < 2) { setSuggestions([]); return; }
      searchTimerRef.current = setTimeout(async () => {
        try {
          const res = await axios.get(`${API_BASE_URL}/api/stock-restrictions/search/`, {
            params: { query: val },
          });
          setSuggestions(res.data.restrictions || []);
        } catch (err) {
          console.error('[STOCK_SEARCH] Error fetching restrictions:', err.message);
          setSuggestions([]);
        }
      }, 300);
    };

    const addStock = (stock) => {
      // Stock object has { isin, instrument_name } from StockRestriction model
      const stockKey = `${stock.isin} - ${stock.instrument_name}`;
      if (!selected.includes(stockKey)) {
        const newSelected = [...selected, stockKey];
        setFormData((prev) => ({ ...prev, [name]: newSelected.join(', ') }));
      }
      setQuery('');
      setSuggestions([]);
    };

    const removeStock = (stockName) => {
      const newSelected = selected.filter((s) => s !== stockName);
      setFormData((prev) => ({ ...prev, [name]: newSelected.join(', ') }));
    };

    return (
      <div style={{ maxWidth: '450px' }}>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={query}
            onChange={handleQuery}
            placeholder="Search restricted stocks (ISIN or name)..."
            style={inputStyle}
            autoComplete="off"
            aria-label={`Search stock restrictions for ${name}`}
            aria-autocomplete="list"
            aria-controls={`${name}-suggestions`}
            aria-expanded={suggestions.length > 0}
          />
          {suggestions.length > 0 && (
            <div
              id={`${name}-suggestions`}
              role="listbox"
              style={{ position: 'absolute', zIndex: 200, background: '#fff', border: '1px solid #ccc', borderRadius: '4px', width: '100%', maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 8px rgba(0,0,0,0.12)' }}
            >
              {suggestions.map((s, i) => (
                <div
                  key={i}
                  role="option"
                  aria-selected="false"
                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontSize: '13px' }}
                  onMouseDown={() => addStock(s)}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f5f5'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                >
                  <div style={{ fontWeight: '600' }}>{s.isin} - {s.instrument_name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        {selected.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
            {selected.map((s) => (
              <span key={s} style={{ background: '#e8f5e9', border: '1px solid #66bb6a', borderRadius: '12px', padding: '3px 10px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {s}
                <button type="button" onClick={() => removeStock(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0, color: '#555' }} aria-label={`Remove ${s}`}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  const sectionHeader = (text) => (
    <h2
      style={{
        background: '#033132',
        color: '#fff',
        padding: '10px 16px',
        margin: '24px 0 12px',
        fontWeight: 'bold',
        fontSize: '16px',
        borderRadius: '4px',
      }}
    >
      {text}
    </h2>
  );

  const field = (label, input, required = false, description = null, errorKey = null) => (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>
        {label}
        {required && <span style={{ color: 'red' }} aria-label="required"> *</span>}
      </label>
      {input}
      {errorKey && fieldErrors[errorKey] && (
        <div
          id={`${errorKey}-error`}
          data-field-error
          role="alert"
          style={{ color: '#c62828', fontSize: '13px', marginTop: '4px' }}
          aria-live="polite"
          aria-atomic="true"
        >
          {fieldErrors[errorKey]}
        </div>
      )}
      {description && (
        <small
          id={`${errorKey}-desc`}
          style={{ color: '#666', display: 'block', marginTop: '3px' }}
          role="doc-tip"
        >
          {description}
        </small>
      )}
    </div>
  );

  const OCCUPATIONS = [
    'Government/Public Sector',
    'Private Sector',
    'Business',
    'Professional',
    'Agriculturist',
    'Retired',
    'Home maker',
    'Student',
    'Money Services/Foreign Exchange',
    'Others',
  ];

  const ADDRESS_PROOFS = [
    'Valid Passport',
    'Aadhar Card',
    "Voters's ID Card",
    'Valid Driving License',
    'Other',
  ];

  const PMLA_OPTIONS = [
    { value: 'Non-resident Indian', label: 'Non-resident Indian' },
    { value: 'High Networth Individual', label: 'High Networth Individual' },
    {
      value: 'Politically Exposed Person (PEP) - Self/Relative/Family Member',
      label: 'Politically Exposed Person (PEP) - Self/Relative/Family Member',
    },
    {
      value: 'Civil Servant/Bureaucrat - Self/Relative/Family Member',
      label: 'Civil Servant/Bureaucrat - Self/Relative/Family Member',
    },
    {
      value: 'Current/Former Head of State - Self/Relative/Family Member',
      label: 'Current/Former Head of State - Self/Relative/Family Member',
    },
    { value: 'None of the above', label: 'None of the above' },
  ];

  const inputStyle = {
    width: '100%',
    padding: '6px 10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px',
    boxSizing: 'border-box',
    textTransform: 'uppercase',
    color: '#222222',
  };
  const selectStyle = { ...inputStyle, maxWidth: '300px' };

  return (
    <>
      <style>{`
        input::placeholder,
        textarea::placeholder {
          color: #333333;
          opacity: 1;
        }

        input:-ms-input-placeholder {
          color: #333333;
        }

        input::-ms-input-placeholder {
          color: #333333;
        }

        input[type="text"],
        input[type="email"],
        input[type="tel"],
        input[type="date"],
        input[type="number"],
        textarea,
        select {
          color: #222222;
          border-color: #555555;
        }

        input:focus,
        textarea:focus,
        select:focus {
          outline: 2px solid #033132 !important;
          outline-offset: 2px !important;
          border-color: #033132 !important;
          box-shadow: 0 0 0 3px rgba(3, 49, 50, 0.1) !important;
        }

        input[type="text"]:focus,
        input[type="email"]:focus,
        input[type="tel"]:focus,
        input[type="date"]:focus,
        input[type="number"]:focus,
        textarea:focus,
        select:focus {
          outline: 2px solid #033132 !important;
          outline-offset: 2px !important;
          border-color: #033132 !important;
          box-shadow: 0 0 0 3px rgba(3, 49, 50, 0.1) !important;
        }

        input[type="radio"],
        input[type="checkbox"] {
          accent-color: #033132;
          width: 16px;
          height: 16px;
          cursor: pointer;
        }

        input[type="radio"]:focus,
        input[type="checkbox"]:focus {
          outline: 2px solid #033132 !important;
          outline-offset: 2px !important;
        }

        input[type="radio"]:hover,
        input[type="checkbox"]:hover {
          opacity: 0.8;
        }
      `}</style>

      {/* <header className="header-area">
        <Navbar />
      </header> */}

      <div style={{ maxWidth: '860px', margin: '100px auto', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)', overflow: 'hidden' }} role="main">
        {/* Header */}
        <div style={{ background: '#C39639', color: '#fff', padding: '30px 25px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ color: '#2c2c2c', marginBottom: '4px', margin: 0, fontSize: '20px', fontWeight: 700 }}>
              Agreya PMS:{' '}
              {distributorFullName !== 'No Distributor'
                ? `${distributorFullName}: Onboarding Information for Individuals`
                : 'Direct-Onboarding Information for Individuals'}
            </h1>
            <p style={{ color: '#2a2a2a', margin: '6px 0 0', fontSize: '14px' }}>
              We will use the information from this form to prefill your physical documents and send them to you for signature
            </p>
          </div>
          <div style={{ background: '#fff', borderRadius: '6px', padding: '6px 12px', marginLeft: '24px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            <img
              src={agreyaLogo}
              alt="Agreya Capital Advisors"
              style={{ height: '52px', width: 'auto', display: 'block' }}
            />
          </div>
        </div>

        <div style={{ padding: '24px' }}>
        <div
          style={{
            background: '#fff8e1',
            border: '1px solid #ffe082',
            borderRadius: '4px',
            padding: '10px 14px',
            marginBottom: '20px',
            fontSize: '13px',
            color: '#555',
          }}
          role="region"
          aria-label="Applicant eligibility information"
        >
          If the applicant is a Minor / Non-Individual, please do not proceed further and kindly contact us at{' '}
          <a
            href="mailto:ops@agreyacapital.com"
            style={{
              color: '#c62828',
              textDecoration: 'underline',
              fontWeight: '600',
            }}
            aria-label="Email link to operations team: ops@agreyacapital.com"
          >
            ops@agreyacapital.com
          </a>
          . Additional information to be separately obtained from NRI applicants.
        </div>

        <div
          style={{
            background: '#fff8e1',
            border: '1px solid #ffe082',
            borderRadius: '4px',
            padding: '12px 14px',
            marginBottom: '20px',
            fontSize: '13px',
            color: '#555',
          }}
          role="region"
          aria-label="Accessibility support information"
          aria-describedby="accessibility-support-description"
        >
          <div id="accessibility-support-description" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            {/* <span style={{ fontSize: '16px', lineHeight: '1.3', marginTop: '2px' }} aria-hidden="true">
              ♿
            </span> */}
            <div style={{ flex: 1 }}>
              {/* <div style={{ fontWeight: '600', marginBottom: '6px' }}>Accessibility Support</div> */}
              <p style={{ margin: 0, lineHeight: '1.5' }}>
                Investors with disabilities or accessibility requirements who need assistance in completing this online KYC form may contact our designated Accessibility Support Team at{' '}
                <a
                  href="tel:+919611157455"
                  style={{
                    color: '#c62828',
                    textDecoration: 'underline',
                    fontWeight: '600',
                  }}
                  aria-label="Phone: +91-9611157455 - Call accessibility support team"
                >
                  +91-9611157455
                </a>
                {' '}or{' '}
                <a
                  href="mailto:ops@agreyacapital.com"
                  style={{
                    color: '#c62828',
                    textDecoration: 'underline',
                    fontWeight: '600',
                  }}
                  aria-label="Email: ops@agreyacapital.com - Email accessibility support team"
                >
                  ops@agreyacapital.com
                </a>
                {' '}for appropriate support.
              </p>
            </div>
          </div>
        </div>

        {/* Error Summary Alert */}
        {hasAttemptedValidation && Object.keys(fieldErrors).length > 0 && (
          <div
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            style={{
              background: '#ffebee',
              border: '2px solid #c62828',
              padding: '12px 16px',
              borderRadius: '4px',
              marginBottom: '16px',
              color: '#c62828',
              fontSize: '14px',
            }}
          >
            <strong>Please fix the following errors before submitting:</strong>
            <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0 }}>
              {Object.entries(fieldErrors).slice(0, 5).map(([key, error]) => (
                <li key={key}>{error}</li>
              ))}
              {Object.keys(fieldErrors).length > 5 && (
                <li>... and {Object.keys(fieldErrors).length - 5} more errors</li>
              )}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} encType="multipart/form-data" noValidate>
          {/* RM / Distributor */}
          <div style={{ display: 'flex', gap: '24px', marginBottom: '16px', flexWrap: 'wrap' }}>
            {distributorFullName !== 'No Distributor' && (
              <div style={{ flex: 1 }}>
                <label style={{ fontWeight: '600' }}>Distributor Name</label>
                <input
                  type="text"
                  value={distributorFullName}
                  readOnly
                  style={{ ...inputStyle, background: '#f5f5f5' }}
                />
              </div>
            )}
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>
                Relationship Manager<span style={{ color: 'red' }}> *</span>
              </label>
              {distributor ? (
                <input
                  type="text"
                  value={assignedRM || ''}
                  readOnly
                  style={{ ...inputStyle, background: '#f5f5f5' }}
                  aria-label="Relationship manager (assigned)"
                />
              ) : (
                <>
                  <select
                    name="relationship_manager"
                    value={formData.relationship_manager}
                    onChange={handleChange}
                    required
                    style={selectStyle}
                    aria-label="Select relationship manager"
                    aria-required="true"
                    aria-invalid={!!fieldErrors.relationship_manager}
                    aria-describedby={fieldErrors.relationship_manager ? 'relationship_manager-error' : null}
                  >
                    <option value="">Select...</option>
                    {RELATIONSHIP_MANAGERS.map((rm) => (
                      <option key={rm} value={rm}>{rm}</option>
                    ))}
                  </select>
                  {fieldErrors.relationship_manager && (
                    <div id="relationship_manager-error" data-field-error role="alert" style={{ color: '#c62828', fontSize: '13px', marginTop: '4px' }}>
                      {fieldErrors.relationship_manager}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* ===== PERSONAL DETAILS ===== */}
          {sectionHeader('Personal Details')}

          {field(
            'Do you wish to open a joint account?',
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input
                      type="radio"
                      name="joint_account"
                      value={opt.value}
                      checked={formData.joint_account === opt.value}
                      onChange={handleChange}
                      required
                      aria-label={`Joint account ${opt.label}`}
                      style={{
                        margin: '0 6px 0 0',
                        padding: 0,
                        width: 'auto'
                      }}
                    />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>,
            true, null, 'joint_account'
          )}

          {field(
            'Name of the Applicant as per PAN (Sole/First Holder)',
            <div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '120px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>First Name <span style={{ color: 'red' }}>*</span></label>
                  <input
                    name="sole_holder_first_name"
                    value={formData.sole_holder_first_name}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                    required
                    style={{ ...inputStyle, borderColor: fieldErrors.sole_holder_first_name ? 'red' : '' }}
                    placeholder="First Name *"
                    aria-label="First name as per PAN"
                    aria-required="true"
                    aria-invalid={!!fieldErrors.sole_holder_first_name}
                    aria-describedby={fieldErrors.sole_holder_first_name ? 'sole_holder_first_name-error' : 'sole_holder_first_name-desc'}
                  />
                  {fieldErrors.sole_holder_first_name && (
                    <div id="sole_holder_first_name-error" role="alert" style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>
                      {fieldErrors.sole_holder_first_name}
                    </div>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: '120px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Middle Name</label>
                  <input
                    name="sole_holder_middle_name"
                    value={formData.sole_holder_middle_name}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                    style={inputStyle}
                    placeholder="Middle Name"
                    aria-label="Middle name as per PAN (optional)"
                  />
                </div>
                <div style={{ flex: 1, minWidth: '120px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Last Name</label>
                  <input
                    name="sole_holder_last_name"
                    value={formData.sole_holder_last_name}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                    style={{ ...inputStyle, borderColor: fieldErrors.sole_holder_last_name ? 'red' : '' }}
                    placeholder="Last Name"
                    aria-label="Last name as per PAN (optional)"
                    aria-invalid={!!fieldErrors.sole_holder_last_name}
                    aria-describedby={fieldErrors.sole_holder_last_name ? 'sole_holder_last_name-error' : null}
                  />
                  {fieldErrors.sole_holder_last_name && (
                    <div id="sole_holder_last_name-error" role="alert" style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>
                      {fieldErrors.sole_holder_last_name}
                    </div>
                  )}
                </div>
              </div>
            </div>,
            true,
            'PAN is a mandatory document and would be used as Id proof',
            'sole_holder_first_name'
          )}

          {field(
            'Gender',
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'others', label: 'Others' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input
                      type="radio"
                      name="gender"
                      value={opt.value}
                      checked={formData.gender === opt.value}
                      onChange={handleChange}
                      required
                      aria-label={`Gender ${opt.label}`}
                      style={{
                        margin: '0 6px 0 0',
                        padding: 0,
                        width: 'auto'
                      }}
                    />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>,
            true, null, 'gender'
          )}

          {field(
            'Date of Birth',
            <input
              type="date"
              name="date_of_birth"
              value={formData.date_of_birth}
              onChange={handleChange}
              required
              style={{ ...inputStyle, maxWidth: '220px' }}
              aria-label="Date of birth, calendar picker"
              aria-required="true"
              aria-invalid={!!fieldErrors.date_of_birth}
              aria-describedby="date_of_birth-desc"
            />,
            true,
            'Please enter date of birth in the format DD-MM-YYYY',
            'date_of_birth'
          )}

        {field(
  'Occupation',
  <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
      {OCCUPATIONS.map((occ) => (
        <label key={occ} style={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          margin: 0,
          padding: 0,
        }}>
          <input
            type="radio"
            name="occupation"
            value={occ}
            checked={formData.occupation === occ}
            onChange={handleChange}
            required
            aria-label={`Occupation ${occ}`}
            style={{
              flexShrink: 0,
              margin: '4px 8px 0 0',
              padding: 0,
              width: 'auto'
            }}
          />
          <span style={{
            margin: 0,
            padding: 0,
            textAlign: 'left',
            lineHeight: '1.4'
          }}>
            {occ}
          </span>
        </label>
      ))}
    </div>
  </fieldset>,
  true, null, 'occupation'
)}
          {field(
            'Marital Status',
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'Single', label: 'Single' }, { value: 'married', label: 'Married' }, { value: 'others', label: 'Others' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input
                      type="radio"
                      name="marital_status"
                      value={opt.value}
                      checked={formData.marital_status === opt.value}
                      onChange={handleChange}
                      required
                      aria-label={`Marital status ${opt.label}`}
                      style={{
                        margin: '0 6px 0 0',
                        padding: 0,
                        width: 'auto'
                      }}
                    />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>,
            true, null, 'marital_status'
          )}

          {field(
            'Residential Status',
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'Resident', label: 'Resident' }, { value: 'Non-Resident', label: 'Non-Resident' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input
                      type="radio"
                      name="residential_status"
                      value={opt.value}
                      checked={formData.residential_status === opt.value}
                      onChange={handleChange}
                      required
                      aria-label={`Residential status ${opt.label}`}
                      style={{
                        margin: '0 6px 0 0',
                        padding: 0,
                        width: 'auto'
                      }}
                    />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>,
            true, null, 'residential_status'
          )}

          {field(
            'Country of Birth',
            <select
              name="country_of_birth_id"
              value={formData.country_of_birth_id}
              onChange={handleChange}
              required
              style={selectStyle}
              aria-label="Country of birth"
              aria-required="true"
              aria-invalid={!!fieldErrors.country_of_birth_id}
              aria-describedby={fieldErrors.country_of_birth_id ? 'country_of_birth_id-error' : null}
            >
              <option value="">Select Country</option>
              {countries.map((c) => (
                <option key={c.id} value={String(c.id)}>{c.name}</option>
              ))}
            </select>,
            true, null, 'country_of_birth_id'
          )}

          {field(
            'State of Birth',
            <select
              name="state_of_birth_id"
              value={formData.state_of_birth_id}
              onChange={handleChange}
              required
              style={selectStyle}
              aria-label="State of birth"
              aria-required="true"
              aria-invalid={!!fieldErrors.state_of_birth_id}
              aria-describedby={fieldErrors.state_of_birth_id ? 'state_of_birth_id-error' : null}
            >
              <option value="">Select State</option>
              {states.map((s) => (
                <option key={s.id} value={String(s.id)}>{s.name}</option>
              ))}
            </select>,
            true, null, 'state_of_birth_id'
          )}

          {field(
            'City of Birth',
            <>
              <select
                name="city_of_birth_id"
                value={formData.city_of_birth_id}
                onChange={handleChange}
                required
                style={selectStyle}
                aria-label="City of birth"
                aria-required="true"
                aria-invalid={!!fieldErrors.city_of_birth_id}
                aria-describedby={fieldErrors.city_of_birth_id ? 'city_of_birth_id-error' : null}
              >
                <option value="">Select City</option>
                {cities.map((c) => (
                  <option key={c.id} value={String(c.id)}>{c.name}</option>
                ))}
                <option value="other">Other (Please specify)</option>
              </select>
              {showManualCity && (
                <input
                  name="manual_city_of_birth"
                  value={formData.manual_city_of_birth}
                  onChange={handleChange}
                  required
                  style={{ ...inputStyle, maxWidth: '220px', marginTop: '8px' }}
                  placeholder="Specify city"
                  aria-label="Specify city of birth"
                  aria-required="true"
                />
              )}
            </>,
            true, null, 'city_of_birth_id'
          )}

          {field(
            'Citizenship',
            <input
              name="citizenship"
              value={formData.citizenship}
              onChange={handleChange}
              required
              style={{ ...inputStyle, maxWidth: '220px' }}
              aria-label="Citizenship"
              aria-required="true"
              aria-invalid={!!fieldErrors.citizenship}
              aria-describedby={fieldErrors.citizenship ? 'citizenship-error' : null}
            />,
            true, null, 'citizenship'
          )}

          {field(
            "Mother's Name",
            <div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>First Name <span style={{ color: 'red' }}>*</span></label>
                  <input
                    name="mother_first_name"
                    value={formData.mother_first_name}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                    required
                    style={{ ...inputStyle, borderColor: fieldErrors.mother_first_name ? 'red' : '' }}
                    placeholder="First Name *"
                  />
                  {fieldErrors.mother_first_name && (
                    <div style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>
                      {fieldErrors.mother_first_name}
                    </div>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Middle Name</label>
                  <input
                    name="mother_middle_name"
                    value={formData.mother_middle_name}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                    style={inputStyle}
                    placeholder="Middle Name"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Last Name</label>
                  <input
                    name="mother_last_name"
                    value={formData.mother_last_name}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                    style={{ ...inputStyle, borderColor: fieldErrors.mother_last_name ? 'red' : '' }}
                    placeholder="Last Name"
                  />
                  {fieldErrors.mother_last_name && (
                    <div style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>
                      {fieldErrors.mother_last_name}
                    </div>
                  )}
                </div>
              </div>
            </div>,
            true, null, 'mother_first_name'
          )}

          {field(
            'Is the communication address of Sole/First Applicant same as the permanent address?',
            <div style={{ display: 'flex', gap: '24px' }}>
              {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                <label key={opt.value} style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  margin: 0,
                  padding: 0,
                }}>
                  <input type="radio" name="applicant_address_same_address" value={opt.value} checked={formData.applicant_address_same_address === opt.value} onChange={handleChange} required style={{
                    margin: '0 6px 0 0',
                    padding: 0,
                    width: 'auto'
                  }} />
                  <span style={{
                    margin: 0,
                    padding: 0,
                  }}>
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>,
            true, null, 'applicant_address_same_address'
          )}

          {field(
            'Document provided as proof of communication address of First/Sole Applicant',
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
              {ADDRESS_PROOFS.map((p) => (
                <label key={p} style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  margin: 0,
                  padding: 0,
                }}>
                  <input type="radio" name="communication_address_proof" value={p} checked={formData.communication_address_proof === p} onChange={handleChange} required style={{
                    flexShrink: 0,
                    margin: '4px 8px 0 0',
                    padding: 0,
                    width: 'auto'
                  }} />
                  <span style={{
                    margin: 0,
                    padding: 0,
                    textAlign: 'left',
                    lineHeight: '1.4'
                  }}>
                    {p}
                  </span>
                </label>
              ))}
              {formData.communication_address_proof === 'Other' && (
                <input name="communication_proof_other" value={formData.communication_proof_other} onChange={handleChange} style={{ ...inputStyle, maxWidth: '220px' }} placeholder="Please specify" />
              )}
            </div>,
            true, null, 'communication_address_proof'
          )}

          {field(
            'Document provided as proof of permanent address (in case different from communication address)',
            <input name="permanent_address_proof" value={formData.permanent_address_proof} onChange={handleChange} style={{ ...inputStyle, maxWidth: '340px' }} placeholder="Mention document type" />,
            false,
            null,
            'permanent_address_proof'
          )}

          {field(
            'Contact Details: Mobile Number',
            <PhoneInput name="mobile_number" required ariaLabel="Mobile number" />,
            true,
            null,
            'mobile_number'
          )}

          {field(
            'Preferred Email ID',
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleFieldBlur}
              required
              style={{ ...inputStyle, maxWidth: '340px' }}
              aria-label="Preferred email address"
              aria-required="true"
              aria-invalid={!!fieldErrors.email}
              aria-describedby={fieldErrors.email ? 'email-error' : null}
            />,
            true,
            null,
            'email'
          )}

          {field(
            'Annual Income',
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                {[
                  { value: 'Upto 25 Lakhs', label: 'Upto 25 Lakhs' },
                  { value: '25 Lakhs to 1 Crore', label: '25 Lakhs to 1 Crore' },
                  { value: '1 to 5 Crore', label: '1 crore to 5 crores' },
                  { value: 'Above 5 crores', label: 'Above 5 crores' },
                ].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input
                      type="radio"
                      name="annual_income"
                      value={opt.value}
                      checked={formData.annual_income === opt.value}
                      onChange={handleChange}
                      required
                      aria-label={`Annual income ${opt.label}`}
                      style={{
                        flexShrink: 0,
                        margin: '4px 8px 0 0',
                        padding: 0,
                        width: 'auto'
                      }}
                    />
                    <span style={{
                      margin: 0,
                      padding: 0,
                      textAlign: 'left',
                      lineHeight: '1.4'
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>,
            true, null, 'annual_income'
          )}

          {field(
            'Indicative Net-worth (INR Crs)',
            <input
              type="number"
              name="net_worth"
              value={formData.net_worth}
              onChange={handleChange}
              required
              style={{ ...inputStyle, maxWidth: '180px' }}
              aria-label="Indicative net worth in Indian rupees crores"
              aria-required="true"
              aria-invalid={!!fieldErrors.net_worth}
              aria-describedby="net_worth-desc"
            />,
            true,
            'Net-worth is the value of all the assets owned by you minus all the liabilities. Net-worth details shall not be older than the last date of the previous Financial Year.',
            'net_worth'
          )}

          {field(
            'PMLA Details (Please tick any one as applicable)',
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
              {PMLA_OPTIONS.map((opt) => (
                <label key={opt.value} style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  margin: 0,
                  padding: 0,
                }}>
                  <input type="radio" name="pmla" value={opt.value} checked={formData.pmla === opt.value} onChange={handleChange} required style={{
                    flexShrink: 0,
                    margin: '4px 8px 0 0',
                    padding: 0,
                    width: 'auto'
                  }} />
                  <span style={{
                    margin: 0,
                    padding: 0,
                    textAlign: 'left',
                    lineHeight: '1.4'
                  }}>
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>,
            true,
            'PMLA - Prevention of Money Laundering Act',
            'pmla'
          )}

          {field(
            'Do you have any stock restrictions?',
            <div style={{ display: 'flex', gap: '24px' }}>
              {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                <label key={opt.value} style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  margin: 0,
                  padding: 0,
                }}>
                  <input type="radio" name="first_applicant_stock_restrictions" value={opt.value} checked={formData.first_applicant_stock_restrictions === opt.value} onChange={handleChange} required style={{
                    margin: '0 6px 0 0',
                    padding: 0,
                    width: 'auto'
                  }} />
                  <span style={{
                    margin: 0,
                    padding: 0,
                  }}>
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>,
            true, null, 'first_applicant_stock_restrictions'
          )}

          {formData.first_applicant_stock_restrictions === 'Yes' && (
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>Stock Restrictions List <span style={{ color: 'red' }}>*</span></label>
              <StockSearch name="first_applicant_stock_restrictions_list" />
              {fieldErrors.first_applicant_stock_restrictions_list && (
                <div data-field-error role="alert" style={{ color: '#c62828', fontSize: '13px', marginTop: '6px' }}>
                  {fieldErrors.first_applicant_stock_restrictions_list}
                </div>
              )}
            </div>
          )}

          {/* Bank Details — hidden for Non-Residents */}
          {showBank && (
            <>
              {sectionHeader('Bank Account Details of the First/Sole Applicant')}

              {field(
                'Name of the Account Holder',
                <input
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleChange}
                  style={inputStyle}
                  aria-label="Account holder name"
                  aria-required="true"
                  aria-invalid={!!fieldErrors.bank_name}
                  aria-describedby={fieldErrors.bank_name ? 'bank_name-error' : null}
                />,
                true, null, 'bank_name'
              )}

              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '14px' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>Bank Name<span style={{ color: 'red' }}> *</span></label>
                  <input
                    name="bankname"
                    value={formData.bankname}
                    onChange={handleChange}
                    style={inputStyle}
                    aria-label="Bank name"
                    aria-required="true"
                    aria-invalid={!!fieldErrors.bankname}
                    aria-describedby={fieldErrors.bankname ? 'bankname-error' : null}
                  />
                  {fieldErrors.bankname && <div id="bankname-error" data-field-error role="alert" style={{ color: '#c62828', fontSize: '13px', marginTop: '4px' }}>{fieldErrors.bankname}</div>}
                </div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>Bank Branch<span style={{ color: 'red' }}> *</span></label>
                  <input
                    name="bank_branch"
                    value={formData.bank_branch}
                    onChange={handleChange}
                    style={inputStyle}
                    aria-label="Bank branch"
                    aria-required="true"
                    aria-invalid={!!fieldErrors.bank_branch}
                    aria-describedby={fieldErrors.bank_branch ? 'bank_branch-error' : null}
                  />
                  {fieldErrors.bank_branch && <div id="bank_branch-error" data-field-error role="alert" style={{ color: '#c62828', fontSize: '13px', marginTop: '4px' }}>{fieldErrors.bank_branch}</div>}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '14px' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>IFSC Code</label>
                  <input
                    name="ifsc_code"
                    value={formData.ifsc_code}
                    onChange={handleChange}
                    style={inputStyle}
                    aria-label="IFSC code"
                    aria-required="false"
                    aria-invalid={!!fieldErrors.ifsc_code}
                    aria-describedby={fieldErrors.ifsc_code ? 'ifsc_code-error' : null}
                  />
                  {fieldErrors.ifsc_code && <div id="ifsc_code-error" data-field-error role="alert" style={{ color: '#c62828', fontSize: '13px', marginTop: '4px' }}>{fieldErrors.ifsc_code}</div>}
                </div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>Account Number</label>
                  <input
                    name="bank_account_number"
                    value={formData.bank_account_number}
                    onChange={handleChange}
                    style={inputStyle}
                    aria-label="Bank account number"
                    aria-invalid={!!fieldErrors.bank_account_number}
                    aria-describedby={fieldErrors.bank_account_number ? 'bank_account_number-error' : null}
                  />
                  {fieldErrors.bank_account_number && <div id="bank_account_number-error" data-field-error role="alert" style={{ color: '#c62828', fontSize: '13px', marginTop: '4px' }}>{fieldErrors.bank_account_number}</div>}
                </div>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label
                  htmlFor="cancelled-cheque-upload"
                  style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}
                >
                  Upload Cancelled Cheque Slip
                </label>
                <input
                  id="cancelled-cheque-upload"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files[0] || null;
                    if (file) {
                      console.log('[FILE_UPLOAD] Cancelled cheque selected:', { name: file.name, size: file.size, type: file.type });
                      const validation = validateFile(file);
                      if (!validation.isValid) {
                        console.error('[FILE_UPLOAD] ❌ Cancelled cheque validation failed:', validation.error);
                        setCancelledChequeError(validation.error);
                        setCancelledCheque(null);
                      } else {
                        console.log('[FILE_UPLOAD] ✓ Cancelled cheque validated successfully');
                        setCancelledChequeError('');
                        setCancelledCheque(file);
                      }
                    } else {
                      setCancelledChequeError('');
                      setCancelledCheque(null);
                    }
                  }}
                  style={{ fontSize: '14px' }}
                  aria-label="Upload cancelled cheque slip"
                  aria-describedby="cancelled-cheque-constraints"
                  aria-invalid={!!cancelledChequeError}
                />
                <small id="cancelled-cheque-constraints" style={{ display: 'block', marginTop: '4px', color: '#666' }}>{getFileConstraintMessage()}</small>
                {cancelledChequeError && (
                  <div id="cancelled-cheque-error" role="alert" style={{ color: '#c62828', fontSize: '13px', marginTop: '6px' }}>{cancelledChequeError}</div>
                )}
              </div>
            </>
          )}

          {/* ===== SECOND APPLICANT ===== */}
          {showSecond && (
            <>
              {sectionHeader('Personal Details (Second Applicant)')}
              <p style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>
                *To be filled for joint account applicants (Second Applicant)
              </p>

              {field(
                'Name of the Second Applicant as per PAN',
                <div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>First Name <span style={{ color: 'red' }}>*</span></label>
                      <input name="second_applicant_first_name" value={formData.second_applicant_first_name} onChange={handleChange} required style={{ ...inputStyle, borderColor: fieldErrors.second_applicant_first_name ? 'red' : '' }} placeholder="First Name *" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Middle Name</label>
                      <input name="second_applicant_middle_name" value={formData.second_applicant_middle_name} onChange={handleChange} style={inputStyle} placeholder="Middle Name" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Last Name</label>
                      <input name="second_applicant_last_name" value={formData.second_applicant_last_name} onChange={handleChange} style={inputStyle} placeholder="Last Name" />
                    </div>
                  </div>
                </div>,
                true,
                'PAN is a mandatory document and would be used as Id proof',
                'second_applicant_first_name'
              )}

              {field('Gender of Second Applicant', <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'others', label: 'Others' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input type="radio" name="second_applicant_gender" value={opt.value} checked={formData.second_applicant_gender === opt.value} onChange={handleChange} style={{
                      margin: '0 6px 0 0',
                      padding: 0,
                      width: 'auto'
                    }} />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>, false, null, 'second_applicant_gender')}

              {field('Date of Birth of Second Applicant', <input type="date" name="second_applicant_date_of_birth" value={formData.second_applicant_date_of_birth} onChange={handleChange} style={{ ...inputStyle, maxWidth: '220px' }} aria-label="Date of birth of second applicant, calendar picker" />, false, null, 'second_applicant_date_of_birth')}

              {field(
                'Occupation of Second Applicant',
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                  {OCCUPATIONS.map((occ) => (
                    <label key={occ} style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      margin: 0,
                      padding: 0,
                    }}>
                      <input type="radio" name="second_applicant_occupation" value={occ} checked={formData.second_applicant_occupation === occ} onChange={handleChange} style={{
                        flexShrink: 0,
                        margin: '4px 8px 0 0',
                        padding: 0,
                        width: 'auto'
                      }} />
                      <span style={{
                        margin: 0,
                        padding: 0,
                        textAlign: 'left',
                        lineHeight: '1.4'
                      }}>
                        {occ}
                      </span>
                    </label>
                  ))}
                </div>,
                false, null, 'second_applicant_occupation'
              )}

              {field('Marital Status of Second Applicant', <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'Single', label: 'Single' }, { value: 'married', label: 'Married' }, { value: 'others', label: 'Others' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input type="radio" name="second_applicant_marital_status" value={opt.value} checked={formData.second_applicant_marital_status === opt.value} onChange={handleChange} style={{
                      margin: '0 6px 0 0',
                      padding: 0,
                      width: 'auto'
                    }} />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>, false, null, 'second_applicant_marital_status')}

              {field('Residential Status of Second Applicant', <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'Resident', label: 'Resident' }, { value: 'Non-Resident', label: 'Non-Resident' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input type="radio" name="second_applicant_residential_status" value={opt.value} checked={formData.second_applicant_residential_status === opt.value} onChange={handleChange} style={{
                      margin: '0 6px 0 0',
                      padding: 0,
                      width: 'auto'
                    }} />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>, false, null, 'second_applicant_residential_status')}

              {field(
                'Country of Birth (Second Applicant)',
                <select name="second_applicant_country_of_birth_id" value={formData.second_applicant_country_of_birth_id} onChange={handleChange} style={selectStyle}>
                  <option value="">Select Country</option>
                  {countries.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                </select>,
                false, null, 'second_applicant_country_of_birth_id'
              )}

              {field(
                'State of Birth (Second Applicant)',
                <select name="second_applicant_state_of_birth_id" value={formData.second_applicant_state_of_birth_id} onChange={handleChange} style={selectStyle}>
                  <option value="">Select State</option>
                  {secondStates.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                </select>,
                false, null, 'second_applicant_state_of_birth_id'
              )}

              {field(
                'City of Birth (Second Applicant)',
                <>
                  <select name="second_applicant_city_of_birth_id" value={formData.second_applicant_city_of_birth_id} onChange={handleChange} style={selectStyle}>
                    <option value="">Select City</option>
                    {secondCities.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                    <option value="other">Other (Please specify)</option>
                  </select>
                  {showManualSecondCity && (
                    <input name="manual_second_city_of_birth" value={formData.manual_second_city_of_birth} onChange={handleChange} required style={{ ...inputStyle, maxWidth: '220px', marginTop: '8px' }} placeholder="Specify city" />
                  )}
                </>,
                false, null, 'second_applicant_city_of_birth_id'
              )}

              {field('Citizenship of Second Applicant', <input name="second_applicant_citizenship" value={formData.second_applicant_citizenship} onChange={handleChange} style={{ ...inputStyle, maxWidth: '220px' }} />)}

              {field(
                "Mother's Name of Second Applicant",
                <div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>First Name</label>
                      <input name="second_applicant_mother_first_name" value={formData.second_applicant_mother_first_name} onChange={handleChange} style={inputStyle} placeholder="First Name" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Middle Name</label>
                      <input name="second_applicant_mother_middle_name" value={formData.second_applicant_mother_middle_name} onChange={handleChange} style={inputStyle} placeholder="Middle Name" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Last Name</label>
                      <input name="second_applicant_mother_last_name" value={formData.second_applicant_mother_last_name} onChange={handleChange} style={inputStyle} placeholder="Last Name" />
                    </div>
                  </div>
                </div>
              )}

              {field('Is the address of Second Applicant same as First/Sole Applicant?', <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input type="radio" name="second_applicant_address_same_as_first" value={opt.value} checked={formData.second_applicant_address_same_as_first === opt.value} onChange={handleChange} style={{
                      margin: '0 6px 0 0',
                      padding: 0,
                      width: 'auto'
                    }} />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>, false, null, 'second_applicant_address_same_as_first')}

              {formData.second_applicant_address_same_as_first !== 'Yes' && (
                <>
                  {field('Is the communication address of Second Applicant same as permanent address?', <div style={{ display: 'flex', gap: '24px' }}>
                    {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                      <label key={opt.value} style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        margin: 0,
                        padding: 0,
                      }}>
                        <input type="radio" name="second_applicant_address_same_address" value={opt.value} checked={formData.second_applicant_address_same_address === opt.value} onChange={handleChange} style={{
                          margin: '0 6px 0 0',
                          padding: 0,
                          width: 'auto'
                        }} />
                        <span style={{
                          margin: 0,
                          padding: 0,
                        }}>
                          {opt.label}
                        </span>
                      </label>
                    ))}
                  </div>, false, null, 'second_applicant_address_same_address')}

              {field(
                'Document provided as proof of communication address of Second Applicant',
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                  {ADDRESS_PROOFS.map((p) => (
                    <label key={p} style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      margin: 0,
                      padding: 0,
                    }}>
                      <input type="radio" name="second_applicant_communication_address_proof" value={p} checked={formData.second_applicant_communication_address_proof === p} onChange={handleChange} style={{
                        flexShrink: 0,
                        margin: '4px 8px 0 0',
                        padding: 0,
                        width: 'auto'
                      }} />
                      <span style={{
                        margin: 0,
                        padding: 0,
                        textAlign: 'left',
                        lineHeight: '1.4'
                      }}>
                        {p}
                      </span>
                    </label>
                  ))}
                  {formData.second_applicant_communication_address_proof === 'Other' && (
                    <input name="second_applicant_communication_proof_other" value={formData.second_applicant_communication_proof_other} onChange={handleChange} style={{ ...inputStyle, maxWidth: '220px' }} placeholder="Please specify" />
                  )}
                </div>,
                false,
                null,
                'second_applicant_communication_address_proof'
              )}

              {field('Document provided as proof of permanent address of Second Applicant (in case different from communication address)', <input name="second_applicant_permanent_address_proof" value={formData.second_applicant_permanent_address_proof} onChange={handleChange} style={{ ...inputStyle, maxWidth: '340px' }} />, false, null, 'second_applicant_permanent_address_proof')}
                </>
              )}

              {field('Mobile Number of Second Applicant', <PhoneInput name="second_applicant_mobile_number" />, true, null, 'second_applicant_mobile_number')}

              {field('Preferred Email ID of Second Applicant', <input type="email" name="second_applicant_email" value={formData.second_applicant_email} onChange={handleChange} onBlur={handleFieldBlur} style={{ ...inputStyle, maxWidth: '340px' }} />, false, null, 'second_applicant_email')}

              {field(
                'Annual Income of Second Applicant',
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                  {[
                    { value: 'Upto 25 Lakhs', label: 'Upto 25 Lakhs' },
                    { value: '25 lakhs to 1 Crore', label: '25 Lakhs to 1 Crore' },
                    { value: '1 to 5 crore', label: '1 crore to 5 crores' },
                    { value: 'Above 5 crores', label: 'Above 5 crores' },
                  ].map((opt) => (
                    <label key={opt.value} style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      margin: 0,
                      padding: 0,
                    }}>
                      <input type="radio" name="second_applicant_annual_income" value={opt.value} checked={formData.second_applicant_annual_income === opt.value} onChange={handleChange} style={{
                        flexShrink: 0,
                        margin: '4px 8px 0 0',
                        padding: 0,
                        width: 'auto'
                      }} />
                      <span style={{
                        margin: 0,
                        padding: 0,
                        textAlign: 'left',
                        lineHeight: '1.4'
                      }}>
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>,
                false, null, 'second_applicant_annual_income'
              )}

              {field('Indicative Net-worth of Second Applicant (INR Crs)', <input type="number" name="second_applicant_net_worth" value={formData.second_applicant_net_worth} onChange={handleChange} style={{ ...inputStyle, maxWidth: '180px' }} />)}

              {field(
                'PMLA Details of Second Applicant',
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                  {PMLA_OPTIONS.map((opt) => (
                    <label key={opt.value} style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      margin: 0,
                      padding: 0,
                    }}>
                      <input type="radio" name="second_applicant_pmla" value={opt.value} checked={formData.second_applicant_pmla === opt.value} onChange={handleChange} style={{
                        flexShrink: 0,
                        margin: '4px 8px 0 0',
                        padding: 0,
                        width: 'auto'
                      }} />
                      <span style={{
                        margin: 0,
                        padding: 0,
                        textAlign: 'left',
                        lineHeight: '1.4'
                      }}>
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>,
                false,
                'PMLA - Prevention of Money Laundering Act'
              )}

              {field('Does the second applicant have any stock restrictions?', <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input type="radio" name="second_applicant_stock_restrictions" value={opt.value} checked={formData.second_applicant_stock_restrictions === opt.value} onChange={handleChange} style={{
                      margin: '0 6px 0 0',
                      padding: 0,
                      width: 'auto'
                    }} />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>)}

              {formData.second_applicant_stock_restrictions === 'Yes' && (
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>Second Applicant Stock Restrictions List <span style={{ color: 'red' }}>*</span></label>
                  <StockSearch name="second_applicant_stock_restrictions_list" />
                  {fieldErrors.second_applicant_stock_restrictions_list && (
                    <div data-field-error role="alert" style={{ color: '#c62828', fontSize: '13px', marginTop: '6px' }}>
                      {fieldErrors.second_applicant_stock_restrictions_list}
                    </div>
                  )}
                </div>
              )}

              {!showThirdApplicant && (
                <button
                  type="button"
                  onClick={() => setShowThirdApplicant(true)}
                  style={{
                    padding: '10px 20px',
                    marginTop: '16px',
                    backgroundColor: '#033132',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                  }}
                >
                  + Add Third Applicant
                </button>
              )}
            </>
          )}

          {showThirdApplicant && (
            <>
              {sectionHeader('Personal Details (Third Applicant)')}
              <p style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>
                *To be filled for joint account applicants (Third Applicant)
              </p>

              {field(
                'Name of the Third Applicant as per PAN',
                <div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>First Name</label>
                      <input name="third_applicant_first_name" value={formData.third_applicant_first_name} onChange={handleChange} style={{ ...inputStyle, borderColor: fieldErrors.third_applicant_first_name ? 'red' : '' }} placeholder="First Name" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Middle Name</label>
                      <input name="third_applicant_middle_name" value={formData.third_applicant_middle_name} onChange={handleChange} style={inputStyle} placeholder="Middle Name" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Last Name</label>
                      <input name="third_applicant_last_name" value={formData.third_applicant_last_name} onChange={handleChange} style={inputStyle} placeholder="Last Name" />
                    </div>
                  </div>
                </div>,
                false,
                'PAN is a mandatory document and would be used as Id proof',
                'third_applicant_first_name'
              )}

              {field('Gender of Third Applicant', <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'others', label: 'Others' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input type="radio" name="third_applicant_gender" value={opt.value} checked={formData.third_applicant_gender === opt.value} onChange={handleChange} style={{
                      margin: '0 6px 0 0',
                      padding: 0,
                      width: 'auto'
                    }} />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>, false, null, 'third_applicant_gender')}

              {field('Date of Birth of Third Applicant', <input type="date" name="third_applicant_date_of_birth" value={formData.third_applicant_date_of_birth} onChange={handleChange} style={{ ...inputStyle, maxWidth: '220px' }} aria-label="Date of birth of third applicant, calendar picker" />, false, null, 'third_applicant_date_of_birth')}

              {field(
                'Occupation of Third Applicant',
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                  {OCCUPATIONS.map((occ) => (
                    <label key={occ} style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      margin: 0,
                      padding: 0,
                    }}>
                      <input type="radio" name="third_applicant_occupation" value={occ} checked={formData.third_applicant_occupation === occ} onChange={handleChange} style={{
                        flexShrink: 0,
                        margin: '4px 8px 0 0',
                        padding: 0,
                        width: 'auto'
                      }} />
                      <span style={{
                        margin: 0,
                        padding: 0,
                        textAlign: 'left',
                        lineHeight: '1.4'
                      }}>
                        {occ}
                      </span>
                    </label>
                  ))}
                </div>,
                false, null, 'third_applicant_occupation'
              )}

              {field('Marital Status of Third Applicant', <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'Single', label: 'Single' }, { value: 'married', label: 'Married' }, { value: 'others', label: 'Others' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input type="radio" name="third_applicant_marital_status" value={opt.value} checked={formData.third_applicant_marital_status === opt.value} onChange={handleChange} style={{
                      margin: '0 6px 0 0',
                      padding: 0,
                      width: 'auto'
                    }} />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>, false, null, 'third_applicant_marital_status')}

              {field('Residential Status of Third Applicant', <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'Resident', label: 'Resident' }, { value: 'Non-Resident', label: 'Non-Resident' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input type="radio" name="third_applicant_residential_status" value={opt.value} checked={formData.third_applicant_residential_status === opt.value} onChange={handleChange} style={{
                      margin: '0 6px 0 0',
                      padding: 0,
                      width: 'auto'
                    }} />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>, false, null, 'third_applicant_residential_status')}

              {field(
                'Country of Birth (Third Applicant)',
                <select name="third_applicant_country_of_birth_id" value={formData.third_applicant_country_of_birth_id} onChange={handleChange} style={selectStyle}>
                  <option value="">Select Country</option>
                  {countries.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                </select>,
                false, null, 'third_applicant_country_of_birth_id'
              )}

              {field(
                'State of Birth (Third Applicant)',
                <select name="third_applicant_state_of_birth_id" value={formData.third_applicant_state_of_birth_id} onChange={handleChange} style={selectStyle}>
                  <option value="">Select State</option>
                  {thirdStates.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                </select>,
                false, null, 'third_applicant_state_of_birth_id'
              )}

              {field(
                'City of Birth (Third Applicant)',
                <>
                  <select name="third_applicant_city_of_birth_id" value={formData.third_applicant_city_of_birth_id} onChange={handleChange} style={selectStyle}>
                    <option value="">Select City</option>
                    {thirdCities.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                    <option value="other">Other (Please specify)</option>
                  </select>
                  {formData.third_applicant_city_of_birth_id === 'other' && (
                    <input name="manual_third_city_of_birth" value={formData.manual_third_city_of_birth} onChange={handleChange} required style={{ ...inputStyle, maxWidth: '220px', marginTop: '8px' }} placeholder="Specify city" />
                  )}
                </>,
                false, null, 'third_applicant_city_of_birth_id'
              )}

              {field('Citizenship of Third Applicant', <input type="text" name="third_applicant_citizenship" value={formData.third_applicant_citizenship} onChange={handleChange} style={{ ...inputStyle, maxWidth: '300px' }} placeholder="e.g., Indian, American" />)}

              {field('Mother\'s Name of Third Applicant', <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <input name="third_applicant_mother_first_name" value={formData.third_applicant_mother_first_name} onChange={handleChange} style={{ ...inputStyle, flex: 1 }} placeholder="First Name" />
                <input name="third_applicant_mother_middle_name" value={formData.third_applicant_mother_middle_name} onChange={handleChange} style={{ ...inputStyle, flex: 1 }} placeholder="Middle Name" />
                <input name="third_applicant_mother_last_name" value={formData.third_applicant_mother_last_name} onChange={handleChange} style={{ ...inputStyle, flex: 1 }} placeholder="Last Name" />
              </div>)}

              {field('Mobile Number of Third Applicant', <PhoneInput name="third_applicant_mobile_number" />, false, null, 'third_applicant_mobile_number')}

              {field('Email of Third Applicant', <input type="email" name="third_applicant_email" value={formData.third_applicant_email} onChange={handleChange} style={{ ...inputStyle, maxWidth: '300px' }} placeholder="Email Address" />, false, null, 'third_applicant_email')}

              {field(
                'Annual Income of Third Applicant',
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                  {[
                    { value: 'Upto 25 Lakhs', label: 'Upto 25 Lakhs' },
                    { value: '25 lakhs to 1 Crore', label: '25 Lakhs to 1 Crore' },
                    { value: '1 to 5 crore', label: '1 crore to 5 crores' },
                    { value: 'Above 5 crores', label: 'Above 5 crores' },
                  ].map((opt) => (
                    <label key={opt.value} style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      margin: 0,
                      padding: 0,
                    }}>
                      <input type="radio" name="third_applicant_annual_income" value={opt.value} checked={formData.third_applicant_annual_income === opt.value} onChange={handleChange} style={{
                        flexShrink: 0,
                        margin: '4px 8px 0 0',
                        padding: 0,
                        width: 'auto'
                      }} />
                      <span style={{
                        margin: 0,
                        padding: 0,
                        textAlign: 'left',
                        lineHeight: '1.4'
                      }}>
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>,
                false, null, 'third_applicant_annual_income'
              )}

              {field('Indicative Net-worth of Third Applicant (INR Crs)', <input type="number" name="third_applicant_net_worth" value={formData.third_applicant_net_worth} onChange={handleChange} style={{ ...inputStyle, maxWidth: '180px' }} />)}

              {field(
                'PMLA Details of Third Applicant',
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                  {PMLA_OPTIONS.map((opt) => (
                    <label key={opt.value} style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      margin: 0,
                      padding: 0,
                    }}>
                      <input type="radio" name="third_applicant_pmla" value={opt.value} checked={formData.third_applicant_pmla === opt.value} onChange={handleChange} style={{
                        flexShrink: 0,
                        margin: '4px 8px 0 0',
                        padding: 0,
                        width: 'auto'
                      }} />
                      <span style={{
                        margin: 0,
                        padding: 0,
                        textAlign: 'left',
                        lineHeight: '1.4'
                      }}>
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>,
                false,
                'PMLA - Prevention of Money Laundering Act'
              )}

              {field('Is the address of the Third Applicant same as the First Applicant?', <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input type="radio" name="third_applicant_address_same_as_first" value={opt.value} checked={formData.third_applicant_address_same_as_first === opt.value} onChange={handleChange} style={{
                      margin: '0 6px 0 0',
                      padding: 0,
                      width: 'auto'
                    }} />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>, false, null, 'third_applicant_address_same_as_first')}

              {formData.third_applicant_address_same_as_first !== 'Yes' && (
                <>
                  {field('Is the communication address of Third Applicant same as permanent address?', <div style={{ display: 'flex', gap: '24px' }}>
                    {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                      <label key={opt.value} style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        margin: 0,
                        padding: 0,
                      }}>
                        <input type="radio" name="third_applicant_address_same_address" value={opt.value} checked={formData.third_applicant_address_same_address === opt.value} onChange={handleChange} style={{
                          margin: '0 6px 0 0',
                          padding: 0,
                          width: 'auto'
                        }} />
                        <span style={{
                          margin: 0,
                          padding: 0,
                        }}>
                          {opt.label}
                        </span>
                      </label>
                    ))}
                  </div>, false, null, 'third_applicant_address_same_address')}

                  {field(
                    'Document provided as proof of communication address of Third Applicant',
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                      {ADDRESS_PROOFS.map((p) => (
                        <label key={p} style={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                          margin: 0,
                          padding: 0,
                        }}>
                          <input type="radio" name="third_applicant_communication_address_proof" value={p} checked={formData.third_applicant_communication_address_proof === p} onChange={handleChange} style={{
                            flexShrink: 0,
                            margin: '4px 8px 0 0',
                            padding: 0,
                            width: 'auto'
                          }} />
                          <span style={{
                            margin: 0,
                            padding: 0,
                            textAlign: 'left',
                            lineHeight: '1.4'
                          }}>
                            {p}
                          </span>
                        </label>
                      ))}
                      {formData.third_applicant_communication_address_proof === 'Other' && (
                        <input name="third_applicant_communication_proof_other" value={formData.third_applicant_communication_proof_other} onChange={handleChange} style={{ ...inputStyle, maxWidth: '220px' }} placeholder="Please specify" />
                      )}
                    </div>,
                    false,
                    null,
                    'third_applicant_communication_address_proof'
                  )}

                  {field('Document provided as proof of permanent address of Third Applicant (in case different from communication address)', <input name="third_applicant_permanent_address_proof" value={formData.third_applicant_permanent_address_proof} onChange={handleChange} style={{ ...inputStyle, maxWidth: '340px' }} />, false, null, 'third_applicant_permanent_address_proof')}
                </>
              )}

              {field('Does the third applicant have any stock restrictions?', <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input type="radio" name="third_applicant_stock_restrictions" value={opt.value} checked={formData.third_applicant_stock_restrictions === opt.value} onChange={handleChange} style={{
                      margin: '0 6px 0 0',
                      padding: 0,
                      width: 'auto'
                    }} />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>)}

              {formData.third_applicant_stock_restrictions === 'Yes' && (
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}>Third Applicant Stock Restrictions List <span style={{ color: 'red' }}>*</span></label>
                  <StockSearch name="third_applicant_stock_restrictions_list" />
                  {fieldErrors.third_applicant_stock_restrictions_list && (
                    <div data-field-error role="alert" style={{ color: '#c62828', fontSize: '13px', marginTop: '6px' }}>
                      {fieldErrors.third_applicant_stock_restrictions_list}
                    </div>
                  )}
                </div>
              )}

              <button
                type="button"
                onClick={() => setShowThirdApplicant(false)}
                style={{
                  padding: '10px 20px',
                  marginTop: '16px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                }}
              >
                Remove Third Applicant
              </button>
            </>
          )}

          {/* ===== NOMINATION DETAILS ===== */}
          {sectionHeader('Nomination Details')}

          {field(
            'Do you wish to appoint a nominee for this account?',
            <div style={{ display: 'flex', gap: '24px' }}>
              {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                <label key={opt.value} style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  margin: 0,
                  padding: 0,
                }}>
                  <input type="radio" name="has_nominee" value={opt.value} checked={formData.has_nominee === opt.value} onChange={handleChange} required style={{
                    margin: '0 6px 0 0',
                    padding: 0,
                    width: 'auto'
                  }} />
                  <span style={{
                    margin: 0,
                    padding: 0,
                  }}>
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>,
            true, null, 'has_nominee'
          )}

          {formData.has_nominee === 'No' && (
            <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#856404' }}>
              <strong>Note:</strong> No nominee requires Nomination Opt-Out Form to be signed along with physical set of documents.
            </div>
          )}

          {formData.has_nominee === 'Yes' && (
            <div style={{ background: '#d1ecf1', border: '1px solid #bee5eb', borderRadius: '4px', padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#0c5460' }}>
              <strong>Note:</strong> A nominee must be a separate individual name and cannot be same as sole applicant or joint applicant.
            </div>
          )}

          {showNominee && (
            <>
              {nominees.map((nominee, idx) => {
                const nomineeLabel = idx === 0 ? 'first/sole nominee' : (idx === 1 ? 'second nominee' : 'third nominee');
                const nomineeCapital = idx === 0 ? 'First/Sole Nominee' : (idx === 1 ? 'Second Nominee' : 'Third Nominee');
                return (
                <div key={idx} style={{ background: '#f9f9f9', border: '1px solid #ddd', borderRadius: '6px', padding: '16px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h4 style={{ margin: '0 0 0 0', fontSize: '15px', fontWeight: '600', color: '#033132' }}>{nomineeCapital}</h4>
                    {nominees.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeNominee(idx)}
                        style={{ background: '#c62828', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <div style={{ flex: 1, minWidth: '120px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>First Name <span style={{ color: 'red' }}>*</span></label>
                      <input
                        type="text"
                        name={`nominee_${idx}_first_name`}
                        value={nominee.first_name}
                        onChange={(e) => handleNomineeChange(idx, 'first_name', e.target.value)}
                        style={{ ...inputStyle, borderColor: nomineeErrors[`nominee_${idx}_first_name`] ? 'red' : '' }}
                        placeholder="First Name"
                      />
                      {nomineeErrors[`nominee_${idx}_first_name`] && <div data-error-key={`nominee_${idx}_first_name`} style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>{nomineeErrors[`nominee_${idx}_first_name`]}</div>}
                    </div>
                    <div style={{ flex: 1, minWidth: '120px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Middle Name</label>
                      <input type="text" name={`nominee_${idx}_middle_name`} value={nominee.middle_name} onChange={(e) => handleNomineeChange(idx, 'middle_name', e.target.value)} style={inputStyle} placeholder="Middle Name" />
                    </div>
                    <div style={{ flex: 1, minWidth: '120px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Last Name</label>
                      <input
                        type="text"
                        name={`nominee_${idx}_last_name`}
                        value={nominee.last_name}
                        onChange={(e) => handleNomineeChange(idx, 'last_name', e.target.value)}
                        style={{ ...inputStyle, borderColor: nomineeErrors[`nominee_${idx}_last_name`] ? 'red' : '' }}
                        placeholder="Last Name"
                      />
                      {nomineeErrors[`nominee_${idx}_last_name`] && <div data-error-key={`nominee_${idx}_last_name`} style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>{nomineeErrors[`nominee_${idx}_last_name`]}</div>}
                    </div>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontWeight: '600', display: 'block', marginBottom: '6px', fontSize: '13px' }}>Is the {nomineeLabel} a Minor? <span style={{ color: 'red' }}>*</span></label>
                    <div style={{ display: 'flex', gap: '24px' }}>
                      {['Yes', 'No'].map((opt) => (
                        <label key={opt} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', margin: 0, padding: 0 }}>
                          <input
                            type="radio"
                            name={`nominee_${idx}_is_minor`}
                            value={opt}
                            checked={nominee.is_minor === opt}
                            onChange={(e) => handleNomineeChange(idx, 'is_minor', e.target.value)}
                            style={{ margin: '0 6px 0 0', padding: 0, width: 'auto' }}
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                    {nomineeErrors[`nominee_${idx}_is_minor`] && <div style={{ color: '#c62828', fontSize: '12px', marginTop: '4px' }}>{nomineeErrors[`nominee_${idx}_is_minor`]}</div>}
                  </div>

                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Date of Birth of {nomineeLabel} <span style={{ color: 'red' }}>*</span></label>
                      <input
                        type="date"
                        name={`nominee_${idx}_dob`}
                        value={nominee.dob}
                        onChange={(e) => handleNomineeChange(idx, 'dob', e.target.value)}
                        style={{ ...inputStyle, borderColor: nomineeErrors[`nominee_${idx}_dob`] ? 'red' : '' }}
                        aria-label={`Date of birth of ${nomineeLabel}, calendar picker`}
                      />
                      {nomineeErrors[`nominee_${idx}_dob`] && <div data-error-key={`nominee_${idx}_dob`} style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>{nomineeErrors[`nominee_${idx}_dob`]}</div>}
                    </div>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Relationship with the First/Sole applicant <span style={{ color: 'red' }}>*</span></label>
                      <input
                        type="text"
                        name={`nominee_${idx}_relationship`}
                        value={nominee.relationship}
                        onChange={(e) => handleNomineeChange(idx, 'relationship', e.target.value)}
                        style={{ ...inputStyle, borderColor: nomineeErrors[`nominee_${idx}_relationship`] ? 'red' : '' }}
                        placeholder="Relationship"
                      />
                      {nomineeErrors[`nominee_${idx}_relationship`] && <div data-error-key={`nominee_${idx}_relationship`} style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>{nomineeErrors[`nominee_${idx}_relationship`]}</div>}
                    </div>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontWeight: '600', display: 'block', marginBottom: '6px', fontSize: '13px' }}>Is address of the {nomineeLabel} same as the Sole/First Holder? <span style={{ color: 'red' }}>*</span></label>
                    <div style={{ display: 'flex', gap: '24px' }}>
                      {['Yes', 'No'].map((opt) => (
                        <label key={opt} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', margin: 0, padding: 0 }}>
                          <input
                            type="radio"
                            name={`nominee_${idx}_address_same`}
                            value={opt}
                            checked={nominee.address_same === opt}
                            onChange={(e) => handleNomineeChange(idx, 'address_same', e.target.value)}
                            style={{ margin: '0 6px 0 0', padding: 0, width: 'auto' }}
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                    {nomineeErrors[`nominee_${idx}_address_same`] && <div data-error-key={`nominee_${idx}_address_same`} style={{ color: '#c62828', fontSize: '12px', marginTop: '4px' }}>{nomineeErrors[`nominee_${idx}_address_same`]}</div>}
                  </div>

                  {nominee.address_same === 'No' && (
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ fontWeight: '600', display: 'block', marginBottom: '4px', fontSize: '13px' }}>{nomineeCapital} Address <span style={{ color: 'red' }}>*</span></label>
                      <textarea
                        name={`nominee_${idx}_full_address`}
                        value={nominee.full_address}
                        onChange={(e) => handleNomineeChange(idx, 'full_address', e.target.value)}
                        style={{ ...inputStyle, borderColor: nomineeErrors[`nominee_${idx}_full_address`] ? 'red' : '', minHeight: '80px' }}
                        placeholder="Enter full address"
                      />
                      {nomineeErrors[`nominee_${idx}_full_address`] && <div data-error-key={`nominee_${idx}_full_address`} style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>{nomineeErrors[`nominee_${idx}_full_address`]}</div>}
                    </div>
                  )}

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontWeight: '600', display: 'block', marginBottom: '6px', fontSize: '13px' }}>Document provided as proof of address of the {nomineeLabel}</label>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <select
                          name={`nominee_${idx}_id_proof_type`}
                          value={(nominee && nominee.id_proof_type) || ''}
                          onChange={(e) => handleNomineeChange(idx, 'id_proof_type', e.target.value)}
                          style={{ ...inputStyle, borderColor: nomineeErrors[`nominee_${idx}_id_proof_type`] ? 'red' : '' }}
                        >
                          <option value="">Select Document Type</option>
                          <option value="aadhar">Aadhar Card</option>
                          <option value="passport">Passport</option>
                          <option value="voter_id">Voter ID</option>
                          <option value="driving_license">Driving License</option>
                          <option value="utility_bill">Utility Bill</option>
                          <option value="other">Other</option>
                        </select>
                        {nomineeErrors[`nominee_${idx}_id_proof_type`] && <div data-error-key={`nominee_${idx}_id_proof_type`} style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>{nomineeErrors[`nominee_${idx}_id_proof_type`]}</div>}
                        {nominee && nominee.id_proof_type === 'other' && (
                          <input
                            type="text"
                            name={`nominee_${idx}_id_proof_other`}
                            value={nominee.id_proof_other || ''}
                            onChange={(e) => handleNomineeChange(idx, 'id_proof_other', e.target.value)}
                            style={{ ...inputStyle, marginTop: '8px' }}
                            placeholder="Specify document type"
                          />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <label style={{ fontWeight: '600', display: 'block', marginBottom: '4px', fontSize: '13px' }}>Upload Document</label>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const file = e.target.files[0] || null;
                            if (file) {
                              const validation = validateFile(file);
                              if (!validation.isValid) {
                                setNomineeErrors((prev) => ({ ...prev, [`nominee_${idx}_id_proof`]: validation.error }));
                                const updated = [...nomineeIdProofs];
                                updated[idx] = null;
                                setNomineeIdProofs(updated);
                              } else {
                                setNomineeErrors((prev) => {
                                  const next = { ...prev };
                                  delete next[`nominee_${idx}_id_proof`];
                                  return next;
                                });
                                const updated = [...nomineeIdProofs];
                                updated[idx] = file;
                                setNomineeIdProofs(updated);
                              }
                            } else {
                              const updated = [...nomineeIdProofs];
                              updated[idx] = null;
                              setNomineeIdProofs(updated);
                            }
                          }}
                          style={{ fontSize: '13px' }}
                        />
                        {nomineeErrors[`nominee_${idx}_id_proof`] && <div style={{ color: '#c62828', fontSize: '12px', marginTop: '6px' }}>{nomineeErrors[`nominee_${idx}_id_proof`]}</div>}
                      </div>
                    </div>
                    <small style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '12px' }}>{getFileConstraintMessage()}</small>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Mobile Number of the {nomineeLabel}</label>
                      <input
                        type="text"
                        name={`nominee_${idx}_mobile_number`}
                        value={nominee.mobile_number}
                        onChange={(e) => handleNomineeChange(idx, 'mobile_number', e.target.value)}
                        style={{ ...inputStyle, borderColor: nomineeErrors[`nominee_${idx}_mobile_number`] ? 'red' : '' }}
                        placeholder="+91 XXXXX XXXXX"
                      />
                      {nomineeErrors[`nominee_${idx}_mobile_number`] && <div data-error-key={`nominee_${idx}_mobile_number`} style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>{nomineeErrors[`nominee_${idx}_mobile_number`]}</div>}
                    </div>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Email of the {nomineeLabel}</label>
                      <input
                        type="email"
                        name={`nominee_${idx}_email`}
                        value={nominee.email}
                        onChange={(e) => handleNomineeChange(idx, 'email', e.target.value)}
                        style={{ ...inputStyle, borderColor: nomineeErrors[`nominee_${idx}_email`] ? 'red' : '' }}
                        placeholder="email@example.com"
                      />
                      {nomineeErrors[`nominee_${idx}_email`] && <div data-error-key={`nominee_${idx}_email`} style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>{nomineeErrors[`nominee_${idx}_email`]}</div>}
                    </div>
                  </div>

                  {nominees.length > 1 && (
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Sharing Percentage <span style={{ color: 'red' }}>*</span></label>
                      <input
                        type="number"
                        value={nominee.sharing_percentage}
                        onChange={(e) => updateSharingPercentage(idx, e.target.value)}
                        min="0"
                        max="100"
                        step="0.01"
                        style={{ ...inputStyle, maxWidth: '140px' }}
                        placeholder="0-100"
                      />
                      <small style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '12px' }}>Total: {sharingRatioTotal.toFixed(2)}%</small>
                    </div>
                  )}

                  {nominee && nominee.is_minor === 'Yes' && guardians && (
                    <div style={{ background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '6px', padding: '16px', marginTop: '16px' }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: '600', color: '#033132' }}>Guardian Details for {nomineeLabel}</h4>

                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                        <div style={{ flex: 1, minWidth: '120px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Guardian First Name <span style={{ color: 'red' }}>*</span></label>
                          <input
                            type="text"
                            value={(guardians[idx] && guardians[idx].first_name) || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setGuardians(prev => prev ? { ...prev, [idx]: { ...(prev[idx] || {}), first_name: value } } : prev);
                            }}
                            style={{ ...inputStyle, borderColor: nomineeErrors[`guardian_${idx}_first_name`] ? 'red' : '' }}
                            placeholder="First Name"
                          />
                          {nomineeErrors[`guardian_${idx}_first_name`] && <div style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>{nomineeErrors[`guardian_${idx}_first_name`]}</div>}
                        </div>
                        <div style={{ flex: 1, minWidth: '120px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Guardian Middle Name</label>
                          <input
                            type="text"
                            value={(guardians[idx] && guardians[idx].middle_name) || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setGuardians(prev => prev ? { ...prev, [idx]: { ...(prev[idx] || {}), middle_name: value } } : prev);
                            }}
                            style={inputStyle}
                            placeholder="Middle Name"
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: '120px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Guardian Last Name</label>
                          <input
                            type="text"
                            value={(guardians[idx] && guardians[idx].last_name) || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setGuardians(prev => prev ? { ...prev, [idx]: { ...(prev[idx] || {}), last_name: value } } : prev);
                            }}
                            style={inputStyle}
                            placeholder="Last Name"
                          />
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Guardian Mobile Number</label>
                          <input
                            type="text"
                            value={(guardians[idx] && guardians[idx].mobile_number) || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setGuardians(prev => prev ? { ...prev, [idx]: { ...(prev[idx] || {}), mobile_number: value } } : prev);
                            }}
                            style={{ ...inputStyle, borderColor: nomineeErrors[`guardian_${idx}_mobile_number`] ? 'red' : '' }}
                            placeholder="+91 XXXXX XXXXX"
                          />
                          {nomineeErrors[`guardian_${idx}_mobile_number`] && <div data-error-key={`guardian_${idx}_mobile_number`} style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>{nomineeErrors[`guardian_${idx}_mobile_number`]}</div>}
                        </div>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Guardian Email</label>
                          <input
                            type="email"
                            value={(guardians[idx] && guardians[idx].email) || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setGuardians(prev => prev ? { ...prev, [idx]: { ...(prev[idx] || {}), email: value } } : prev);
                            }}
                            style={{ ...inputStyle, borderColor: nomineeErrors[`guardian_${idx}_email`] ? 'red' : '' }}
                            placeholder="email@example.com"
                          />
                          {nomineeErrors[`guardian_${idx}_email`] && <div data-error-key={`guardian_${idx}_email`} style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>{nomineeErrors[`guardian_${idx}_email`]}</div>}
                        </div>
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ fontWeight: '600', display: 'block', marginBottom: '6px', fontSize: '13px' }}>Guardian Address Proof <span style={{ color: 'red' }}>*</span></label>
                        <select
                          value={(guardians[idx] && guardians[idx].communication_address_proof) || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setGuardians(prev => prev ? { ...prev, [idx]: { ...(prev[idx] || {}), communication_address_proof: value } } : prev);
                          }}
                          style={{ ...inputStyle, borderColor: nomineeErrors[`guardian_${idx}_communication_address_proof`] ? 'red' : '' }}
                        >
                          <option value="">Select an option</option>
                          <option value="aadhar">Aadhar Card</option>
                          <option value="passport">Passport</option>
                          <option value="voter_id">Voter ID</option>
                          <option value="driving_license">Driving License</option>
                          <option value="utility_bill">Utility Bill</option>
                        </select>
                        {nomineeErrors[`guardian_${idx}_communication_address_proof`] && <div style={{ color: '#c62828', fontSize: '12px', marginTop: '2px' }}>{nomineeErrors[`guardian_${idx}_communication_address_proof`]}</div>}
                      </div>
                    </div>
                  )}
                </div>
              );
              })}

              {nominees.length < 3 && (
                <button
                  type="button"
                  onClick={addNominee}
                  style={{ background: '#033132', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', marginBottom: '16px', fontSize: '14px', fontWeight: '600' }}
                >
                  + Add Nominee
                </button>
              )}

              {nomineeErrors.sharing_percentage_total && (
                <div style={{ background: '#ffebee', border: '1px solid #ef9a9a', padding: '10px 14px', borderRadius: '4px', marginBottom: '16px', color: '#c62828', fontSize: '13px' }}>
                  {nomineeErrors.sharing_percentage_total}
                </div>
              )}

            </>
          )}

          {/* ===== RISK PROFILING ===== */}
          {sectionHeader('Risk Profiling Section')}
          <p style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>To be answered by Sole/First Applicant</p>

          {field(
            'Describe your Investment Experience',
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                { value: 'no_prior_experience', label: 'No Prior Experience' },
                { value: 'Limited Experience', label: 'Limited Experience – I have little or no knowledge of investment products outside of traditional bank savings account or time deposits' },
                { value: 'Moderate', label: 'Moderate – I have a general understanding and currently hold shares/MFs or have invested earlier in shares, mutual funds and am aware of related risks' },
                { value: 'Extensive', label: "Extensive – I'm an active and experienced investor comfortable in making my own investment decisions and I have invested before." },
              ].map((opt) => (
                <label key={opt.value} style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'flex-start',
                  margin: 0,
                  padding: 0,
                }}>
                  <input type="radio" name="investment_experience" value={opt.value} checked={formData.investment_experience === opt.value} onChange={handleChange} required style={{
                    flexShrink: 0,
                    margin: '4px 8px 0 0',
                    padding: 0,
                    width: 'auto'
                  }} />
                  <span style={{
                    margin: 0,
                    padding: 0,
                    lineHeight: '1.4'
                  }}>
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>,
            true, null, 'investment_experience'
          )}

          {field('How would you personally describe your risk tolerance?', <Slider name="risk_tolerance" min={0} max={10} />, true, '1-3 Low risk  ·  4-7 Moderate risk  ·  8-10 High Risk', 'risk_tolerance')}

          {field('What is your Investment Objective?', <Slider name="investment_objective" min={0} max={10} />, true, '1-3 Capital Protection  ·  4-7 Regular Income  ·  8-10 Wealth Creation', 'investment_objective')}

          {field('What is your Investment Horizon?', <Slider name="investment_horizon" min={0} max={10} />, true, '1-3: Short term (<1 yr)  ·  4-7: Medium term (1-3 yrs)  ·  8-10: Long term (>3 yrs)', 'investment_horizon')}

          {field('What percentage of your investment portfolio would you initially allocate to the portfolio manager?', <Slider name="portfolio_manager_allocation" min={0} max={100} step={10} suffix="%" />, true, null, 'portfolio_manager_allocation')}

          {field('Would you require a systematic investment/withdrawal plan?', <div style={{ display: 'flex', gap: '24px' }}>
              {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                <label key={opt.value} style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  margin: 0,
                  padding: 0,
                }}>
                  <input type="radio" name="require_systematic_investment_plan" value={opt.value} checked={formData.require_systematic_investment_plan === opt.value} onChange={handleChange} required style={{
                    margin: '0 6px 0 0',
                    padding: 0,
                    width: 'auto'
                  }} />
                  <span style={{
                    margin: 0,
                    padding: 0,
                  }}>
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>, true, null, 'require_systematic_investment_plan')}

          {field('How dependent are you on this investment to meet monthly expenses?', <Slider name="expense_dependency" min={0} max={10} />, true, '1-3 High dependence  ·  4-7 Moderate dependence  ·  8-10 Low dependence', 'expense_dependency')}

          {field(
            'Are you aware that investments in market related securities, including equity investments, have capital erosion risks and are you prepared for the same?',
            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input
                      type="radio"
                      name="investment_risk_awareness"
                      value={opt.value}
                      checked={formData.investment_risk_awareness === opt.value}
                      onChange={handleChange}
                      required
                      aria-label={`Investment risk awareness ${opt.label}`}
                      style={{
                        margin: '0 6px 0 0',
                        padding: 0,
                        width: 'auto'
                      }}
                    />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>,
            true, null, 'investment_risk_awareness'
          )}

          {formData.investment_risk_awareness === 'No' && (
            <div style={{ background: '#fff3cd', border: '1px solid #ffc107', padding: '10px 14px', borderRadius: '4px', marginBottom: '14px', color: '#856404' }}>
              Please note that Agreya would not be able to offer you any services as all market related securities, including equity investments, have inherent capital erosion risks.
            </div>
          )}

          {riskEnabled && (
            <>
              {field('How do you/would you react to a substantial decline (greater than 25%) in your equity portfolio? (1=Panic, 10=Calm)', <Slider name="substantial_decline_reaction" min={0} max={10} />, true, '1: Panic  ·  10: Calm', 'substantial_decline_reaction')}

              {field(
                'Are you consenting for use of derivatives in underlying investment approaches?',
                <div style={{ display: 'flex', gap: '24px' }}>
                  {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                    <label key={opt.value} style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      margin: 0,
                      padding: 0,
                    }}>
                      <input type="radio" name="cderivatives" value={opt.value} checked={formData.cderivatives === opt.value} onChange={handleChange} required style={{
                        margin: '0 6px 0 0',
                        padding: 0,
                        width: 'auto'
                      }} />
                      <span style={{
                        margin: 0,
                        padding: 0,
                      }}>
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>,
                true, null, 'cderivatives'
              )}

              {/* Derivatives Modal */}
              {showDerivativesModal && (
                <div
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
                  role="presentation"
                  aria-hidden={!showDerivativesModal}
                >
                  <div
                    style={{ background: '#fff', borderRadius: '8px', maxWidth: '550px', width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="derivatives-modal-title"
                    aria-describedby="derivatives-modal-description"
                  >
                    <div style={{ background: '#C39239', color: '#fff', padding: '18px 20px', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h2 id="derivatives-modal-title" style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Use of Derivatives - Important Information</h2>
                      <button
                        type="button"
                        onClick={() => setShowDerivativesModal(false)}
                        aria-label="Close derivatives information modal"
                        style={{ background: 'none', border: 'none', color: '#fff', fontSize: '28px', cursor: 'pointer', padding: '0', width: '28px', height: '28px', lineHeight: '28px', flexShrink: 0, marginLeft: '10px' }}
                      >
                        ×
                      </button>
                    </div>
                    <div style={{ padding: '24px' }} id="derivatives-modal-description">
                      <p style={{ marginTop: 0, marginBottom: '12px', fontSize: '14px', color: '#333' }}>You have chosen investment approaches with underlying use of derivatives. Please note the following:</p>
                      <ol style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', paddingLeft: '20px' }}>
                        <li style={{ marginBottom: '12px' }}>Derivatives is a general term used for a broad range of investment strategies and are usually considered as high risk investments. Derivatives may be used for return enhancement or protection of investment and involve different risk-reward outcomes. It is essential that you discuss with Agreya what their specific strategy is before going ahead.</li>
                        <li>One of the big disadvantages of using investment approaches with derivatives is resultant taxation implication for the investor. In India, the taxation of income from derivative transactions is treated as non-speculative business income and is taxed at a higher rate than STCG/LTCG. Therefore it becomes essential to consult a tax advisor to ensure compliance with all reporting requirements.</li>
                      </ol>
                      <p style={{ marginTop: '16px', marginBottom: '20px', fontSize: '13px', color: '#333', fontWeight: '500' }}>By confirming, you explicitly confirm that you are aware of the risks involved and taxation implication.</p>
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => setShowDerivativesModal(false)}
                          style={{ background: '#C39239', color: '#fff', border: 'none', padding: '10px 28px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', minWidth: '140px' }}
                          aria-label="Yes, I confirm awareness of derivatives risks and taxation implications"
                        >
                          Yes, I confirm
                        </button>
                        <button
                          type="button"
                          onClick={() => { setFormData((p) => ({ ...p, cderivatives: 'No' })); setShowDerivativesModal(false); }}
                          style={{ background: '#6c757d', color: '#fff', border: 'none', padding: '10px 28px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', minWidth: '140px' }}
                          aria-label="No, I do not consent to derivatives"
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Same risk profile question for joint accounts */}
          {showSameRiskQ && (
            <div style={{ background: '#f8f9fa', padding: '14px', borderRadius: '4px', marginBottom: '16px' }}>
              {field('Is the risk profile for the second applicant the same as for the first applicant?', <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input type="radio" name="same_risk_profile" value={opt.value} checked={formData.same_risk_profile === opt.value} onChange={handleChange} style={{
                      margin: '0 6px 0 0',
                      padding: 0,
                      width: 'auto'
                    }} />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>, false, null, 'same_risk_profile')}
            </div>
          )}

          {/* ===== SECOND APPLICANT RISK PROFILING ===== */}
          {showSecondRisk && (
            <>
              {sectionHeader('Risk Profiling Section (Second Applicant)')}
              <p style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>To be answered by the Second Applicant</p>

              {field(
                'Describe your Investment Experience (Second Applicant)',
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {[
                    { value: 'no_prior_experience', label: 'No Prior Experience' },
                    { value: 'Limited Experience', label: 'Limited Experience – I have little or no knowledge of investment products outside of traditional bank savings account or time deposits' },
                    { value: 'Moderate', label: 'Moderate – I have a general understanding and currently hold shares/MFs or have invested earlier in shares, mutual funds and am aware of related risks' },
                    { value: 'Extensive', label: "Extensive – I'm an active and experienced investor comfortable in making my own investment decisions and I have invested before." },
                  ].map((opt) => (
                    <label key={opt.value} style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-start',
                      margin: 0,
                      padding: 0,
                    }}>
                      <input type="radio" name="second_investment_experience" value={opt.value} checked={formData.second_investment_experience === opt.value} onChange={handleChange} style={{
                        flexShrink: 0,
                        margin: '4px 8px 0 0',
                        padding: 0,
                        width: 'auto'
                      }} />
                      <span style={{
                        margin: 0,
                        padding: 0,
                        lineHeight: '1.4'
                      }}>
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>,
                false, null, 'second_investment_experience'
              )}

              {field('How would you personally describe your risk tolerance?', <Slider name="second_risk_tolerance" min={0} max={10} />, false, '1-3 Low risk --------------- 4-7 Moderate risk --------------- 8-10 High Risk', 'second_risk_tolerance')}
              {field('What is your Investment Objective?', <Slider name="second_investment_objective" min={0} max={10} />, false, '1-3 Capital Protection --------------- 4-7 Regular Income --------------- 8-10 Wealth Creation', 'second_investment_objective')}
              {field('What is your Investment Horizon?', <Slider name="second_investment_horizon" min={0} max={10} />, false, '1-3: Short term (<1 yr) ----------- 4-7:Medium term (1-3yrs) ---------- 8-10: Long term (>3 yrs)', 'second_investment_horizon')}
              {field('What percentage of your investment portfolio would you initially allocate to the portfolio manager?', <Slider name="second_portfolio_manager_allocation" min={0} max={100} step={10} suffix="%" />, false, null, 'second_portfolio_manager_allocation')}
              {field('Would you require a systematic investment/withdrawal plan?', <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input type="radio" name="second_require_systematic_investment_plan" value={opt.value} checked={formData.second_require_systematic_investment_plan === opt.value} onChange={handleChange} style={{
                      margin: '0 6px 0 0',
                      padding: 0,
                      width: 'auto'
                    }} />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>)}
              {field('How dependent are you on this investment to meet monthly expenses?', <Slider name="second_expense_dependency" min={0} max={10} />, false, '1-3 High dependence --------- 4-7 Moderate dependence ---------- 8-10 Low dependence')}

              {field(
                'Are you aware that investments in market related securities, including equity investments, have capital erosion risks and are you prepared for the same?',
                <div style={{ display: 'flex', gap: '24px' }}>
                  {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                    <label key={opt.value} style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      margin: 0,
                      padding: 0,
                    }}>
                      <input type="radio" name="second_investment_risk_awareness" value={opt.value} checked={formData.second_investment_risk_awareness === opt.value} onChange={handleChange} style={{
                        margin: '0 6px 0 0',
                        padding: 0,
                        width: 'auto'
                      }} />
                      <span style={{
                        margin: 0,
                        padding: 0,
                      }}>
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>
              )}

              {formData.second_investment_risk_awareness === 'No' && (
                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', padding: '10px 14px', borderRadius: '4px', marginBottom: '14px', color: '#856404' }}>
                  Please note that Agreya would not be able to offer services to this applicant as all market related securities, including equity investments, have inherent capital erosion risks.
                </div>
              )}

              {secondRiskEnabled && (
                <>
                  {field('How do you/would you react to a substantial decline (greater than 25%) in your equity portfolio?', <Slider name="second_substantial_decline_reaction" min={0} max={10} />, false, '1: Panic----------------------------------------------------------- 10: Calm')}

                  {field(
                    'Are you consenting for use of derivatives in underlying investment approaches?',
                    <div style={{ display: 'flex', gap: '24px' }}>
                      {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                        <label key={opt.value} style={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          margin: 0,
                          padding: 0,
                        }}>
                          <input type="radio" name="second_cderivatives" value={opt.value} checked={formData.second_cderivatives === opt.value} onChange={handleChange} style={{
                            margin: '0 6px 0 0',
                            padding: 0,
                            width: 'auto'
                          }} />
                          <span style={{
                            margin: 0,
                            padding: 0,
                          }}>
                            {opt.label}
                          </span>
                        </label>
                      ))}
                    </div>,
                    true, null, 'second_cderivatives'
                  )}

                  {showSecondDerivativesModal && (
                    <div
                      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
                      role="presentation"
                      aria-hidden={!showSecondDerivativesModal}
                    >
                      <div
                        style={{ background: '#fff', borderRadius: '8px', maxWidth: '550px', width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="second-derivatives-modal-title"
                        aria-describedby="second-derivatives-modal-description"
                      >
                        <div style={{ background: '#C39239', color: '#fff', padding: '18px 20px', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h2 id="second-derivatives-modal-title" style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Use of Derivatives - Important Information</h2>
                          <button
                            type="button"
                            onClick={() => setShowSecondDerivativesModal(false)}
                            aria-label="Close derivatives information modal for second applicant"
                            style={{ background: 'none', border: 'none', color: '#fff', fontSize: '28px', cursor: 'pointer', padding: '0', width: '28px', height: '28px', lineHeight: '28px', flexShrink: 0, marginLeft: '10px' }}
                          >
                            ×
                          </button>
                        </div>
                        <div style={{ padding: '24px' }} id="second-derivatives-modal-description">
                          <p style={{ marginTop: 0, marginBottom: '12px', fontSize: '14px', color: '#333' }}>You have chosen investment approaches with underlying use of derivatives. Please note the following:</p>
                          <ol style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', paddingLeft: '20px' }}>
                            <li style={{ marginBottom: '12px' }}>Derivatives is a general term used for a broad range of investment strategies and are usually considered as high risk investments. Derivatives may be used for return enhancement or protection of investment and involve different risk-reward outcomes. It is essential that you discuss with Agreya what their specific strategy is before going ahead.</li>
                            <li>One of the big disadvantages of using investment approaches with derivatives is resultant taxation implication for the investor. In India, the taxation of income from derivative transactions is treated as non-speculative business income and is taxed at a higher rate than STCG/LTCG. Therefore it becomes essential to consult a tax advisor to ensure compliance with all reporting requirements.</li>
                          </ol>
                          <p style={{ marginTop: '16px', marginBottom: '20px', fontSize: '13px', color: '#333', fontWeight: '500' }}>By confirming, you explicitly confirm that you are aware of the risks involved and taxation implication.</p>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              onClick={() => setShowSecondDerivativesModal(false)}
                              style={{ background: '#C39239', color: '#fff', border: 'none', padding: '10px 28px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', minWidth: '140px' }}
                              aria-label="Yes, second applicant confirms awareness of derivatives risks"
                            >
                              Yes, I confirm
                            </button>
                            <button
                              type="button"
                              onClick={() => { setFormData((p) => ({ ...p, second_cderivatives: 'No' })); setShowSecondDerivativesModal(false); }}
                              style={{ background: '#6c757d', color: '#fff', border: 'none', padding: '10px 28px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', minWidth: '140px' }}
                              aria-label="No, second applicant does not consent to derivatives"
                            >
                              No
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {showSameRiskQThird && (
            <div style={{ background: '#f8f9fa', padding: '14px', borderRadius: '4px', marginBottom: '16px' }}>
              {field('Is the risk profile for the third applicant the same as for the first applicant?', <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input type="radio" name="same_risk_profile_third" value={opt.value} checked={formData.same_risk_profile_third === opt.value} onChange={handleChange} style={{
                      margin: '0 6px 0 0',
                      padding: 0,
                      width: 'auto'
                    }} />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>, false, null, 'same_risk_profile_third')}
            </div>
          )}

          {showThirdRisk && (
            <>
              {sectionHeader('Risk Profiling Section (Third Applicant)')}
              <p style={{ color: '#666', marginBottom: '16px', fontSize: '13px' }}>To be answered by the Third Applicant</p>

              {field(
                'Describe your Investment Experience (Third Applicant)',
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {[
                    { value: 'no_prior_experience', label: 'No Prior Experience' },
                    { value: 'Limited Experience', label: 'Limited Experience – I have little or no knowledge of investment products outside of traditional bank savings account or time deposits' },
                    { value: 'Moderate', label: 'Moderate – I have a general understanding and currently hold shares/MFs or have invested earlier in shares, mutual funds and am aware of related risks' },
                    { value: 'Extensive', label: "Extensive – I'm an active and experienced investor comfortable in making my own investment decisions and I have invested before." },
                  ].map((opt) => (
                    <label key={opt.value} style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-start',
                      margin: 0,
                      padding: 0,
                    }}>
                      <input type="radio" name="third_investment_experience" value={opt.value} checked={formData.third_investment_experience === opt.value} onChange={handleChange} style={{
                        flexShrink: 0,
                        margin: '4px 8px 0 0',
                        padding: 0,
                        width: 'auto'
                      }} />
                      <span style={{
                        margin: 0,
                        padding: 0,
                        lineHeight: '1.4'
                      }}>
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>,
                false, null, 'third_investment_experience'
              )}

              {field('How would you personally describe your risk tolerance?', <Slider name="third_risk_tolerance" min={0} max={10} />, false, '1-3 Low risk --------------- 4-7 Moderate risk --------------- 8-10 High Risk', 'third_risk_tolerance')}
              {field('What is your Investment Objective?', <Slider name="third_investment_objective" min={0} max={10} />, false, '1-3 Capital Protection --------------- 4-7 Regular Income --------------- 8-10 Wealth Creation', 'third_investment_objective')}
              {field('What is your Investment Horizon?', <Slider name="third_investment_horizon" min={0} max={10} />, false, '1-3: Short term (<1 yr) ----------- 4-7:Medium term (1-3yrs) ---------- 8-10: Long term (>3 yrs)', 'third_investment_horizon')}
              {field('What percentage of your investment portfolio would you initially allocate to the portfolio manager?', <Slider name="third_portfolio_manager_allocation" min={0} max={100} step={10} suffix="%" />, false, null, 'third_portfolio_manager_allocation')}
              {field('Would you require a systematic investment/withdrawal plan?', <div style={{ display: 'flex', gap: '24px' }}>
                {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                  <label key={opt.value} style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                    padding: 0,
                  }}>
                    <input type="radio" name="third_require_systematic_investment_plan" value={opt.value} checked={formData.third_require_systematic_investment_plan === opt.value} onChange={handleChange} style={{
                      margin: '0 6px 0 0',
                      padding: 0,
                      width: 'auto'
                    }} />
                    <span style={{
                      margin: 0,
                      padding: 0,
                    }}>
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>, false, null, 'third_require_systematic_investment_plan')}
              {field('How dependent are you on this investment to meet monthly expenses?', <Slider name="third_expense_dependency" min={0} max={10} />, false, '1-3 High dependence --------- 4-7 Moderate dependence ---------- 8-10 Low dependence', 'third_expense_dependency')}

              {field(
                'Are you aware that investments in market related securities, including equity investments, have capital erosion risks and are you prepared for the same?',
                <div style={{ display: 'flex', gap: '24px' }}>
                  {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                    <label key={opt.value} style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      margin: 0,
                      padding: 0,
                    }}>
                      <input type="radio" name="third_investment_risk_awareness" value={opt.value} checked={formData.third_investment_risk_awareness === opt.value} onChange={handleChange} style={{
                        margin: '0 6px 0 0',
                        padding: 0,
                        width: 'auto'
                      }} />
                      <span style={{
                        margin: 0,
                        padding: 0,
                      }}>
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>,
                true, null, 'third_investment_risk_awareness'
              )}

              {formData.third_investment_risk_awareness === 'No' && (
                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', padding: '10px 14px', borderRadius: '4px', marginBottom: '14px', color: '#856404' }}>
                  Please note that Agreya would not be able to offer services to this applicant as all market related securities, including equity investments, have inherent capital erosion risks.
                </div>
              )}

              {field('How do you/would you react to a substantial decline (greater than 25%) in your equity portfolio?', <Slider name="third_substantial_decline_reaction" min={0} max={10} />, false, '1: Panic----------------------------------------------------------- 10: Calm', 'third_substantial_decline_reaction')}

              {field(
                'Are you consenting for use of derivatives in underlying investment approaches?',
                <div style={{ display: 'flex', gap: '24px' }}>
                  {[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }].map((opt) => (
                    <label key={opt.value} style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      margin: 0,
                      padding: 0,
                    }}>
                      <input type="radio" name="third_cderivatives" value={opt.value} checked={formData.third_cderivatives === opt.value} onChange={handleChange} style={{
                        margin: '0 6px 0 0',
                        padding: 0,
                        width: 'auto'
                      }} />
                      <span style={{
                        margin: 0,
                        padding: 0,
                      }}>
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>,
                true, null, 'third_cderivatives'
              )}

              {showThirdDerivativesModal && (
                <div
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
                  role="presentation"
                  aria-hidden={!showThirdDerivativesModal}
                >
                  <div
                    style={{ background: '#fff', borderRadius: '8px', maxWidth: '550px', width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="third-derivatives-modal-title"
                    aria-describedby="third-derivatives-modal-description"
                  >
                    <div style={{ background: '#C39239', color: '#fff', padding: '18px 20px', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h2 id="third-derivatives-modal-title" style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Use of Derivatives - Important Information</h2>
                      <button
                        type="button"
                        onClick={() => setShowThirdDerivativesModal(false)}
                        aria-label="Close derivatives information modal for third applicant"
                        style={{ background: 'none', border: 'none', color: '#fff', fontSize: '28px', cursor: 'pointer', padding: '0', width: '28px', height: '28px', lineHeight: '28px', flexShrink: 0, marginLeft: '10px' }}
                      >
                        ×
                      </button>
                    </div>
                    <div style={{ padding: '24px' }} id="third-derivatives-modal-description">
                      <p style={{ marginTop: 0, marginBottom: '12px', fontSize: '14px', color: '#333' }}>You have chosen investment approaches with underlying use of derivatives. Please note the following:</p>
                      <ol style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', paddingLeft: '20px' }}>
                        <li style={{ marginBottom: '12px' }}>Derivatives is a general term used for a broad range of investment strategies and are usually considered as high risk investments. Derivatives may be used for return enhancement or protection of investment and involve different risk-reward outcomes. It is essential that you discuss with Agreya what their specific strategy is before going ahead.</li>
                        <li>One of the big disadvantages of using investment approaches with derivatives is resultant taxation implication for the investor. In India, the taxation of income from derivative transactions is treated as non-speculative business income and is taxed at a higher rate than STCG/LTCG. Therefore it becomes essential to consult a tax advisor to ensure compliance with all reporting requirements.</li>
                      </ol>
                      <p style={{ marginTop: '16px', marginBottom: '20px', fontSize: '13px', color: '#333', fontWeight: '500' }}>By confirming, you explicitly confirm that you are aware of the risks involved and taxation implication.</p>
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => setShowThirdDerivativesModal(false)}
                          style={{ background: '#C39239', color: '#fff', border: 'none', padding: '10px 28px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', minWidth: '140px' }}
                          aria-label="Yes, third applicant confirms awareness of derivatives risks and taxation implications"
                        >
                          Yes, I confirm
                        </button>
                        <button
                          type="button"
                          onClick={() => { setFormData((p) => ({ ...p, third_cderivatives: 'No' })); setShowThirdDerivativesModal(false); }}
                          style={{ background: '#6c757d', color: '#fff', border: 'none', padding: '10px 28px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', minWidth: '140px' }}
                          aria-label="No, third applicant does not consent to derivatives"
                        >
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ===== RISK CATEGORY DISPLAY ===== */}
          {sectionHeader('Risk Category and Risk Outlook')}

          {riskData ? (
            <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', padding: '14px 18px', borderRadius: '6px', marginBottom: '16px' }}>
              <p style={{ margin: 0, fontSize: '15px' }}>
                Based on your answers, Agreya determines your Risk Category as{' '}
                <strong style={{ color: '#033132' }}>{riskData.riskCategory}</strong> and Risk Outlook as{' '}
                <strong style={{ color: '#033132' }}>{riskData.riskOutlook}</strong>.
              </p>
              <p style={{ margin: '4px 0 0', color: '#555', fontSize: '13px' }}>
                Total Risk Score: <strong>{riskData.totalScore}</strong>
              </p>
            </div>
          ) : (
            <div style={{ background: '#f5f5f5', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px', color: '#666', fontSize: '15px' }}>
              Complete all risk profiling questions (with slider values &gt; 0) to see your risk category.
            </div>
          )}

          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <button
              type="button"
              onClick={generatePdf}
              style={{
                background: (riskData && formData.investment_risk_awareness === 'Yes' && (!secondApplicantDifferentRisk || formData.second_investment_risk_awareness === 'Yes') && (!thirdApplicantDifferentRisk || formData.third_investment_risk_awareness === 'Yes')) ? '#033132' : '#666',
                color: '#fff',
                border: 'none',
                padding: '10px 28px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '15px',
              }}
              aria-label="Yes, I accept. Download Risk Profiling PDF"
            >
              Yes, I accept – Download Risk Profiling PDF
            </button>
            {pdfDownloaded && (
              <p style={{ color: '#2e7d32', marginTop: '8px', fontSize: '13px' }} role="status" aria-live="polite">
                ✓ PDF generated and downloaded successfully.
              </p>
            )}
            {pdfError && (
              <div
                role="alert"
                aria-live="polite"
                aria-atomic="true"
                style={{
                  marginTop: '12px',
                  padding: '12px 14px',
                  background: '#ffebee',
                  border: '1px solid #ef9a9a',
                  borderRadius: '4px',
                  color: '#c62828',
                  fontSize: '13px',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {pdfError}
              </div>
            )}
          </div>

          {/* ===== INVESTMENT APPROACH ===== */}
          {sectionHeader('Investment Approach suitable to your risk profile')}

          {(formData.investment_risk_awareness !== 'Yes' || (secondApplicantDifferentRisk && formData.second_investment_risk_awareness !== 'Yes') || (thirdApplicantDifferentRisk && formData.third_investment_risk_awareness !== 'Yes')) && (
            <div style={{ background: '#fff3cd', border: '1px solid #ffc107', padding: '10px 14px', borderRadius: '4px', marginBottom: '14px', color: '#856404', fontSize: '13px' }}>
              Investment approaches will be available after all applicants confirm capital erosion risk awareness.
            </div>
          )}
          {formData.investment_risk_awareness === 'Yes' && (!isJoint || formData.second_investment_risk_awareness === 'Yes') && (!showThirdApplicant || formData.third_investment_risk_awareness === 'Yes') && !pdfDownloaded && (
            <div style={{ background: '#fff3cd', border: '1px solid #ffc107', padding: '10px 14px', borderRadius: '4px', marginBottom: '14px', color: '#856404', fontSize: '13px' }}>
              Please download the Risk Profiling PDF first to unlock investment approach selection.
            </div>
          )}
          {formData.investment_risk_awareness === 'Yes' && (!isJoint || formData.second_investment_risk_awareness === 'Yes') && (!showThirdApplicant || formData.third_investment_risk_awareness === 'Yes') && pdfDownloaded && enabledApproaches.size === 0 && (
            <div style={{ background: '#fff3cd', border: '1px solid #ffc107', padding: '10px 14px', borderRadius: '4px', marginBottom: '14px', color: '#856404', fontSize: '13px' }}>
              Complete all risk profiling sliders and answer the derivatives question to unlock investment approaches.
            </div>
          )}

          <div style={{ marginBottom: '14px' }}>
            <label style={{ fontWeight: '600', display: 'block', marginBottom: '8px' }}>
              Please select your preferred choice of Investment Approach
              <span style={{ color: 'red' }}> *</span>
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
              {ALL_APPROACHES.map((value) => {
                const enabled = enabledApproaches.has(value);
                const checked = formData.investment_approach.includes(value);
                return (
                  <label
                    key={value}
                    style={{
                      cursor: enabled ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      margin: 0,
                      padding: '8px 12px',
                      borderRadius: '4px',
                      background: enabled ? (checked ? '#e8f5e9' : '#f9f9f9') : '#f0f0f0',
                      border: enabled ? (checked ? '1px solid #a5d6a7' : '1px solid #ddd') : '1px solid #e0e0e0',
                    }}
                  >
                    <input
                      type="checkbox"
                      name="investment_approach"
                      value={value}
                      checked={checked}
                      disabled={!enabled}
                      onChange={handleChange}
                      style={{
                        flexShrink: 0,
                        margin: '4px 8px 0 0',
                        padding: 0,
                        width: 'auto',
                        opacity: enabled ? 1 : 0.5,
                      }}
                    />
                    <span style={{
                      margin: 0,
                      padding: 0,
                      textAlign: 'left',
                      lineHeight: '1.4',
                      color: enabled ? '#222' : '#666',
                      fontWeight: checked ? '600' : 'normal',
                    }}>
                      {value}
                      {!enabled && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#666' }}>(not suitable for your risk profile)</span>}
                    </span>
                  </label>
                );
              })}
            </div>
            {fieldErrors.investment_approach && (
              <div data-field-error style={{ color: '#c62828', fontSize: '13px', marginTop: '6px' }}>{fieldErrors.investment_approach}</div>
            )}
          </div>

          {/* ===== FEE SCHEDULE ===== */}
          {sectionHeader('Fee Schedule')}

          {formData.investment_approach.length === 0 ? (
            <div style={{ color: '#666', marginBottom: '14px', fontSize: '13px', padding: '10px', background: '#f5f5f5', borderRadius: '4px' }}>
              Fee schedules will appear here once you select an Investment Approach above.
            </div>
          ) : (
            <>
              {/* Collect fixed and hybrid fee items visible for selected approaches */}
              {(() => {
                const fixedItems = [];
                const hybridItems = [];
                formData.investment_approach.forEach((approach) => {
                  const m = APPROACH_FEE_MAP[approach];
                  if (!m) return;
                  if (m.fixed) fixedItems.push({ label: m.fixed, approach });
                  if (m.hybrid) hybridItems.push({ label: m.hybrid, approach });
                });
                return (
                  <>
                    {fixedItems.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ fontWeight: '700', display: 'block', marginBottom: '8px', color: '#033132' }}>FIXED FEE MODEL</label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
                          {fixedItems.map(({ label }) => {
                            const isDisabled = disabledFees.has(label);
                            const isChecked = formData.fixed_fee_model.includes(label);
                            return (
                              <label
                                key={label}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  justifyContent: 'flex-start',
                                  margin: 0,
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                                  background: isChecked ? '#e8f5e9' : isDisabled ? '#f5f5f5' : '#fafafa',
                                  border: isChecked ? '1px solid #a5d6a7' : '1px solid #ddd',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  name="fixed_fee_model"
                                  value={label}
                                  checked={isChecked}
                                  disabled={isDisabled}
                                  onChange={handleChange}
                                  style={{
                                    flexShrink: 0,
                                    margin: '4px 8px 0 0',
                                    padding: 0,
                                    width: 'auto',
                                    opacity: isDisabled ? 0.5 : 1,
                                  }}
                                />
                                <span style={{
                                  margin: 0,
                                  padding: 0,
                                  textAlign: 'left',
                                  lineHeight: '1.4',
                                  color: isDisabled ? '#bbb' : '#222',
                                }}>
                                  {label}
                                  {isDisabled && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#bbb' }}>(hybrid model already selected)</span>}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {hybridItems.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ fontWeight: '700', display: 'block', marginBottom: '8px', color: '#033132' }}>HYBRID FEE MODEL</label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
                          {hybridItems.map(({ label }) => {
                            const isDisabled = disabledFees.has(label);
                            const isChecked = formData.hybrid_fee_model.includes(label);
                            return (
                              <label
                                key={label}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  justifyContent: 'flex-start',
                                  margin: 0,
                                  padding: '8px 12px',
                                  borderRadius: '4px',
                                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                                  background: isChecked ? '#e8f5e9' : isDisabled ? '#f5f5f5' : '#fafafa',
                                  border: isChecked ? '1px solid #a5d6a7' : '1px solid #ddd',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  name="hybrid_fee_model"
                                  value={label}
                                  checked={isChecked}
                                  disabled={isDisabled}
                                  onChange={handleChange}
                                  style={{
                                    flexShrink: 0,
                                    margin: '4px 8px 0 0',
                                    padding: 0,
                                    width: 'auto',
                                    opacity: isDisabled ? 0.5 : 1,
                                  }}
                                />
                                <span style={{
                                  margin: 0,
                                  padding: 0,
                                  textAlign: 'left',
                                  lineHeight: '1.4',
                                  color: isDisabled ? '#bbb' : '#222',
                                }}>
                                  {label}
                                  {isDisabled && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#bbb' }}>(fixed model already selected)</span>}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}

          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <a
              href="https://agreyacapital.com/fee-illustration"
              target="_blank"
              rel="noopener noreferrer"
              style={{ background: '#6c757d', color: '#fff', padding: '8px 20px', borderRadius: '4px', textDecoration: 'none', fontSize: '14px' }}
              aria-label="Click here for Fee Calculator Tool opens in new tab"
            >
              Click here for Fee Calculator Tool
            </a>
          </div>

          {/* ===== KYC DOCUMENTS ===== */}
          {sectionHeader('Mandatory KYC Documents to be uploaded')}

          <div style={{ marginBottom: '8px', fontWeight: '600' }}>
            <p>– PAN is compulsory and would be used as ID proof</p>
            <p>– Acceptable documents for address: Aadhar / Passport / Voter's ID / Driving License / Others</p>
            <p>– Alternatively you can email documents to ops@agreyacapital.com</p>
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label
              htmlFor="pan-card-upload"
              style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}
            >
              PAN Card Copy (For all applicants, nominees and guardian if applicable)
            </label>
            <input
              id="pan-card-upload"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                  console.log('[FILE_UPLOAD] PAN card files selected:', Array.from(files).map(f => ({ name: f.name, size: f.size, type: f.type })));
                  const validation = validateFiles(files);
                  if (!validation.isValid) {
                    console.error('[FILE_UPLOAD] ❌ PAN card validation failed:', validation.errors);
                    setPanCardError(validation.errors.join('\n'));
                    setPanCardCopies([]);
                  } else {
                    console.log('[FILE_UPLOAD] ✓ PAN card files validated successfully:', files.length, 'file(s)');
                    setPanCardError('');
                    setPanCardCopies(Array.from(files));
                  }
                } else {
                  setPanCardError('');
                  setPanCardCopies([]);
                }
              }}
              style={{ fontSize: '14px' }}
              aria-label="Upload PAN card copies for all applicants, nominees and guardians if applicable"
              aria-describedby="pan-card-constraints pan-card-note"
              aria-invalid={!!panCardError}
            />
            <small id="pan-card-constraints" style={{ display: 'block', marginTop: '4px', color: '#666' }}>{getFileConstraintMessage()}</small>
            <small id="pan-card-note" style={{ display: 'block', marginTop: '3px', color: '#666' }}>In case applicant/nominee is a minor, please attach for Guardian.</small>
            {panCardError && (
              <div id="pan-card-error" role="alert" style={{ color: '#c62828', fontSize: '13px', marginTop: '6px', whiteSpace: 'pre-wrap' }}>{panCardError}</div>
            )}
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label
              htmlFor="address-proof-upload"
              style={{ fontWeight: '600', display: 'block', marginBottom: '4px' }}
            >
              Address Proof Document (For all applicants, nominees and guardian if applicable)
            </label>
            <input
              id="address-proof-upload"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                  console.log('[FILE_UPLOAD] Address proof files selected:', Array.from(files).map(f => ({ name: f.name, size: f.size, type: f.type })));
                  const validation = validateFiles(files);
                  if (!validation.isValid) {
                    console.error('[FILE_UPLOAD] ❌ Address proof validation failed:', validation.errors);
                    setAddressProofError(validation.errors.join('\n'));
                    setAddressProofCopies([]);
                  } else {
                    console.log('[FILE_UPLOAD] ✓ Address proof files validated successfully:', files.length, 'file(s)');
                    setAddressProofError('');
                    setAddressProofCopies(Array.from(files));
                  }
                } else {
                  setAddressProofError('');
                  setAddressProofCopies([]);
                }
              }}
              style={{ fontSize: '14px' }}
              aria-label="Upload address proof documents for all applicants, nominees and guardians if applicable"
              aria-describedby="address-proof-constraints address-proof-note"
              aria-invalid={!!addressProofError}
            />
            <small id="address-proof-constraints" style={{ display: 'block', marginTop: '4px', color: '#666' }}>{getFileConstraintMessage()}</small>
            <small id="address-proof-note" style={{ display: 'block', marginTop: '3px', color: '#666' }}>
              Please attach communication address proof additionally if different from permanent address.
            </small>
            {addressProofError && (
              <div id="address-proof-error" role="alert" style={{ color: '#c62828', fontSize: '13px', marginTop: '6px', whiteSpace: 'pre-wrap' }}>{addressProofError}</div>
            )}
          </div>

          {/* Error - Major Error Banner */}
          {submitError && (
            <div style={{
              background: '#ffebee',
              border: '2px solid #d32f2f',
              padding: '16px 20px',
              borderRadius: '8px',
              marginBottom: '24px',
              color: '#c62828',
              boxShadow: '0 2px 8px rgba(211, 47, 47, 0.15)'
            }} role="alert">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ fontSize: '20px', lineHeight: '1.4', marginTop: '2px' }}>⚠️</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', marginBottom: '6px', fontSize: '15px' }}>Error</div>
                  <div style={{ fontSize: '14px', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {submitError}
                  </div>
                  <div style={{ fontSize: '12px', marginTop: '10px', opacity: 0.8 }}>
                    💡 Please try again. If this issue persists, please contact support at ops@agreyacapital.com
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Submit */}
          <div style={{ textAlign: 'center', marginTop: '24px', marginBottom: '16px' }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                background: loading ? '#666' : '#033132',
                color: '#fff',
                border: 'none',
                padding: '12px 40px',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                letterSpacing: '1px',
              }}
              aria-label="Submit onboarding form"
              aria-disabled={loading}
            >
              {loading ? 'Submitting...' : 'SUBMIT'}
            </button>
            {loading && (
              <p style={{ color: '#666', marginTop: '8px', fontSize: '13px' }} role="status" aria-live="polite" aria-atomic="true">
                Your form is loading, please hold on a moment...
              </p>
            )}
          </div>
        </form>
        </div>{/* end padding div */}
      </div>
    </>
  );
}
